/*
  r3d-designer.js — オーダーメイドデザイナーの制御。

  サーバー処理は一切使わない。GPXの解析は r3d-gpx.js、地図は Leaflet + 地理院タイル、
  価格はバリアント、注文内容は line item properties。

  ■ 触るときの注意
  - カート追加は「通常のフォームPOST」でなければならない。fetch(/cart/add.js) に
    変えると type="file" の property が落ちてGPXが注文に届かなくなる。
  - **そのフォームPOSTは、送信時にファイルをもう一度読み直す。** そのため
    選択時に読んだバイト列から File を作り直して input に入れ替えている
    （`detachFile`）。**これを外すと Google Drive 上のGPXでカート追加が
    `ERR_UPLOAD_FILE_CHANGED` で落ちる。** 詳しくは `detachFile` のコメント。
  - 送信直前に pruneForSubmit() で空の property を disabled にしている。
    これをやめると、選ばなかった項目が空行としてカートと注文に並ぶ。
*/

(function () {
  'use strict';

  /* 地図の初期表示。選び直したときはここへ戻す */
  const DEFAULT_VIEW = { center: [36.2, 138.2], zoom: 7 };

  const GSI_TILE = 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png';
  /*
    陰影起伏図。淡色地図の上に重ねて、地形の起伏を読めるようにする（2026-08-20）。
    **整備は z16 まで。** それより寄ったときは `maxNativeZoom` で引き伸ばす。
    無いズームを取りに行くと404が並ぶ。
  */
  const GSI_SHADE = 'https://cyberjapandata.gsi.go.jp/xyz/hillshademap/{z}/{x}/{y}.png';
  const GSI_SHADE_MAX_Z = 16;
  const GSI_ATTR =
    '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noopener">地理院タイル</a>';

  /*
    山名・地名の検索（国土地理院）。**キーもサーバも要らない。**
    `Access-Control-Allow-Origin: *` を返すのでブラウザから直接呼べる（実測で確認）。
    返るのは GeoJSON の Feature 配列で、**座標は [経度, 緯度] の順**。
  */
  const PLACE_SEARCH = 'https://msearch.gsi.go.jp/address-search/AddressSearch?q=';

  /*
    <trk><name> の中身が「名前」として使えるか（2026-08-29に追加）。

    ⚠️ **記録アプリが機械的な名前を入れてくることがある。**
    ヤマレコ Android 26.34.3 の実出力は `<trk><name>track</name>` で、
    **そのまま入れると刻印のルート名欄に "track" が載る。**
    利用者が気づかずに進めば "track" が彫られる。
    （2026-08-22 に採った古いヤマレコの実出力は「仙人ヶ岳」だったので、
    アプリ側の書き出しが途中で変わったことになる。**片方だけ見て決めないこと。**）

    **一般名そのもの（＋連番）だけを弾く。** 「殺生ヒュッテ - 槍ヶ岳」のような
    実際の名前は通す。**名前の中に track を含むだけのものも通す** ―― 弾きすぎると、
    使える名前まで消えて、利用者が理由の分からない空欄を見ることになる。
  */
  const GENERIC_TRACK_NAME =
    /^(track|segment|route|activity|untitled|no ?name|トラック|セグメント|ルート|無題|新規トラック|活動記録)\s*\d*$/i;

  /*
    ⚠️⚠️ **長すぎる名前も欄に入れる**（9-54 で変更）。

    以前は 12字を超えたら入れなかったが、**実GPX 18件のうち9件がこれで空欄になっていた。**
    ⚠️ **空欄だと、客はGPXに名前があったことすら分からず、一から考えることになる。**

    ⭐ **入れたうえでカウンタを赤くし、`validate()` で注文を止める。**
    **客は元の名前を見ながら削れる** ―― `3000m峰の頂へ穂高連峰縦走` から
    どの12字を残すかは、こちらではなく客が決めるべき。
    ⚠️ **切り詰めてはいけない** ―― 12字で切ると「縦走」が消えた別の名前を刻むことになる。

    ⚠️ **地名検索からの山名（`fillNameFromPlace`）はこれと別。**
    **あちらは住所のような title を返す**ので、長いものはそもそも客が刻みたい名前ではない。
  */
  /*
    刻印に使える字だけを残す（9-54）。**クラスの外に置いてあるのは、
    `usableTrackName` からも使うため** ―― **数える前に落とす必要がある。**

    ⚠️⚠️ **「消すものを並べる」ではなく「残すものを並べる」。**
    **新しい絵文字が増えても、こちらは何もしなくてよい。**
    ⚠️ **`・`（U+30FB）はカタカナの範囲。**「槍ヶ岳・穂高岳」で必ず要る。
    ⚠️ **互換漢字（U+F900–FAFF）を外すと客の名前が消える**（﨑・髙 など）。
    ⭐ **`①②` は残す**（U+2460–24FF）。⚠️ **`㈱` は別の範囲**（U+3200–32FF）なので落ちる。
  */
  const NOT_ENGRAVABLE = /[^\u0020-\u007E\u00A0-\u024F\u2460-\u24FF\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF01-\uFF60\uFFE0-\uFFE6]/;

  /** 落とした結果と、落とした字を返す。**空白の詰めもここでやる** */
  function dropUnengravable(text) {
    const src = String(text == null ? '' : text);
    const bad = [];
    for (const ch of src) {
      if (NOT_ENGRAVABLE.test(ch) && bad.indexOf(ch) === -1) bad.push(ch);
    }
    if (!bad.length) return { v: src, bad: bad };
    let v = src;
    bad.forEach(function (ch) { v = v.split(ch).join(''); });
    // ⚠️ 抜いた跡の空白を詰める。⭐ 全角空白は半角に置き換えない
    v = v.replace(/[ \u3000]{2,}/g, function (m) { return m[0]; }).trim();
    return { v: v, bad: bad };
  }

  function usableTrackName(raw) {
    const name = String(raw || '').trim();
    /* ⚠️⚠️ **判定は「落としたあと」で行うが、返すのは「落とす前」。**
       実際に落とすのは `stripEngraveField` で、**そこが「取り除きました」と画面に出す。**
       ここで落としてしまうと、**客は絵文字が消えたことに気づかない** ―― 黙って消さない。 */
    const v = dropUnengravable(name).v;
    if (!v || GENERIC_TRACK_NAME.test(v)) return '';
    return name;
  }

  /*
    刻印の欄を「字数」ではなく「幅」で止める（2026-09-01）。

    ⚠️⚠️ **刻印が潰れるかは字数ではなく、行に与えられる em で決まる。**
    **em は「有効幅 ÷ 行の幅」で頭打ちになる**ので、
    **同じ20字でも全角と半角ではまったく違う大きさになる。**
    実測（S・px0.20・押し出し0.36）:

      全角10字 -> em 6.00mm  OK
      全角11字 -> em 5.73mm  ⚠ 24%が埋まる
      全角20字 -> em 3.40mm  ⚠ 45%が埋まる
      半角17字（`Hanako Sanza 2026`）-> em 6.00mm  OK

    ⭐ **だから上限は「全角1・半角0.5 で数えた幅10」。**
    **半角なら20字入るが、全角は10字で止まる。**

    ⚠️ **これは S の値。** XL は20字でも em 9.40mm で余裕がある。
    **サイズ別に緩めることもできるが、選び直すたびに上限が動くので採らなかった。**
    ⚠️ **製造側（`model.py`）には同じ歯止めが無い。**
    **デザイナーを通さない経路で入ってきた長い文字列は、黙って潰れる。**
  */
  function textWidth(raw) {
    let w = 0;
    for (const ch of String(raw || '')) {
      const c = ch.codePointAt(0);
      /* 半角＝ASCII と半角カナ。それ以外は全角として1つぶん数える */
      w += (c < 0x0100 || (c >= 0xFF61 && c <= 0xFF9F)) ? 0.5 : 1;
    }
    return w;
  }

  /** 幅が max を超えないところまで後ろから削る */
  function trimToWidth(raw, max) {
    let w = 0;
    let out = '';
    for (const ch of String(raw || '')) {
      const c = ch.codePointAt(0);
      const d = (c < 0x0100 || (c >= 0xFF61 && c <= 0xFF9F)) ? 0.5 : 1;
      if (w + d > max) break;
      w += d;
      out += ch;
    }
    return out;
  }

  /** その欄の上限。幅で数える欄は data-r3d-width-max を持つ */
  function widthLimit(el) {
    const v = parseFloat(el.getAttribute('data-r3d-width-max'));
    return isFinite(v) && v > 0 ? v : null;
  }

  function money(cents) {
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'JPY';
    try {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return String(Math.round(cents / 100));
    }
  }

  function km(m) {
    return (m / 1000).toFixed(1) + ' km';
  }

  function meters(m) {
    return Math.round(m).toLocaleString('ja-JP') + ' m';
  }

  /*
    背面刻印の組版。製造側 product.py の engrave() と同じ規則で描く。
    **数値は run_product.py / product.py と対で持っている。片方だけ変えると
    プレビューと実物がずれる。**

      - cap は em サイズであって字の実寸ではない（PIL の font size と同じ扱い）
      - 収まらないときだけ 0.96 倍ずつ縮める。拡大はしない
      - group が同じ行は最小サイズに揃える（Date の範囲表記が一番長いため）
      - 行間 2.2mm、左右の余白 11mm、行の塊は台紙の縦中央
      - 空文字の行はスペーサー。文字は描かず高さだけ進める
  */
  /* 模型の高さの上限[mm]。**造形範囲256mmと、Lサイズ用の箱（内寸135mm）から決めた**
     （`data/shipping.md`）。基部の厚みは製造側 product.py の base_mm と同値。 */
  /*
    刻印プレビューの QR。⚠️⚠️ **中身は固定**（`https://sanza.sikaworks.net`）なので、
    実行時に符号化せず、あらかじめ計算した並びを持つ。**外部ライブラリを増やさない**
    （E3 の判断と同じ ―― 外が落ちるとデザイナーが動かず注文が取れない）。
    ⭐ **`make_plate_art.py` と同条件**（誤り訂正 L / 静穏帯 2 → 29モジュール）。
    ⚠️⚠️ **QR_URL を変えたら、ここも作り直すこと。**
      manufacture/.venv/Scripts/python -c "import segno,io;q=segno.make('<URL>',error='l');\n        io.open('qr_matrix.tmp','w').write('
'.join(''.join('1' if c else '0' for c in r)\n        for r in q.matrix_iter(border=2)))"
  */
  const PROOF_QR = [
    '00000000000000000000000000000',
    '00000000000000000000000000000',
    '00111111101111011000111111100',
    '00100000100001111010100000100',
    '00101110100101011010101110100',
    '00101110100011110110101110100',
    '00101110100011100010101110100',
    '00100000100101111000100000100',
    '00111111101010101010111111100',
    '00000000001100110000000000000',
    '00110110100100101010100000100',
    '00111101011010111100011111000',
    '00001101100101110111110100100',
    '00001010000001001110100111100',
    '00100010111111000100100000100',
    '00111010000111010110001001000',
    '00111100101100000111001111100',
    '00101001001100001100010110100',
    '00100000111001110011111011000',
    '00000000001010010010001011000',
    '00111111100011101010101000100',
    '00100000100101010010001001000',
    '00101110101111101111111000000',
    '00101110101001000111100001100',
    '00101110100101111100001111100',
    '00100000101000001001011011100',
    '00111111101100101011000100100',
    '00000000000000000000000000000',
    '00000000000000000000000000000',
  ];

  /* 刻印プレビューの字の色。⚠️ 台紙は CSS の `.r3d-proof__plate`。**対で動かす** */
  const PROOF_INK = '#d8dbe0';

  const MAX_MODEL_H_MM = 120;
  const MODEL_BASE_MM = 11;

  /*
    起伏の下限（**F5 / 9-44**、2026-09-06に確定）。

      起伏[mm] = 標高差[m] × 長辺[mm] ÷ 切り出し範囲の長辺[m]

    **上限（MAX_MODEL_H_MM）の裏返し。** あちらは狭い範囲で高くなりすぎる側、
    こちらは広い範囲で平らになりすぎる側を見る。

    ⚠️⚠️ **下限は2本立てで、大きいほうを採る。**
      ・**絶対値 5mm** ―― これを下回ると、見ても触っても地形と分からない
      ・**長辺の3%** ―― 250mmの板に5mmの膨らみは、見た目にはただの平板

    小さいサイズは絶対値で、大きいサイズは比率で止まる。実売サイズでの
    必要な起伏比は S 5.00% / M 3.33% / L 3.00% / XL 3.00%。

    ⚠️ **HANDOFF の「長辺180mmなら3%」は売っていないサイズの数字。**
    実売は S100 / M150 / L200 / XL250。**同じ記録でもSは外れてLは通る**ので、
    入稿時に一律で弾いてはいけない（L・XLなら作れる記録を断ることになる）。

    ⚠️ **手元の実記録23本で検証済み。** 弾かれたのは丹沢の長い縦走（4.8%）の
    Sだけで、**登山の記録は実質すべて通る。**
  */
  const MIN_RELIEF_MM = 5.0;
  const MIN_RELIEF_RATIO = 0.03;
  /** そのサイズで必要な起伏[mm]。**2本の下限の大きいほう** */
  const reliefFloorMm = (longMm) => Math.max(MIN_RELIEF_MM, MIN_RELIEF_RATIO * longMm);

  const PROOF = {
    gapMm: 2.2,
    /* 数値3行のあいだだけ行間を広げる。2.2mmだと3行が塊に見えて読みにくい
       （run_product.py の DATA_GAP_MM と同値） */
    dataGapMm: 4.0,
    /* 背面の余白。**製造側 model.py の back_margin_mm と同値。片方だけ変えない。**
       11 → 6mm（2026-08-29）。小さいサイズほど不利だったため ―― 11mm は
       S（幅67mm）では幅の33%を余白に使う一方、XL（167mm）では13%。
       刻印は幅で頭打ちになるので、**Sだけが極端に小さい字になっていた。**
       ⭐ 数値3行は S で 2.40 → 4.20mm、M で 4.50 → 6.90mm。L・XL はほぼ不変。 */
    /* ⚠️⚠️ **5.0 → 4.0**（2026-09-06）。**`make_plate_art.py` の `MARGIN_MM` と対。**
       ⚠️ **6.0 → 5.0 は模型の背面だったころの値**（2026-09-01。9-49）。 */
    marginMm: 4.0,
    /* 銘板ブランクの実寸。⚠️ `make_plate_art.py` の PLATE_W_MM / PLATE_H_MM と対 */
    plateWMm: 54.0,
    plateHMm: 86.0,
    /* 外枠。⚠️ `make_plate_art.py` の BORDER_INSET_MM / BORDER_W_MM と対 */
    borderInsetMm: 2.5,
    borderWMm: 0.25,
    /* QR。⚠️ `make_plate_art.py` の QR_MM / QR_GAP_MM と対 */
    qrMm: 12.0,
    qrGapMm: 1.2,
    /* 塊を切るスペーサー行。5.0mmだと版面が縦に間延びして見えたので下げた
       （run_product.py の SPACER_MM と同値） */
    spacerMm: 2.0,
    /* ⚠️⚠️ 2026-09-01 に一度 10/8/8 へ組み替えて、同日戻した（9-49）。
       **戻した理由: 版面の向きを取り違えて測っていた。**
       S の外形は 66.6（短辺）× 100.0（長辺）で、文字は短辺方向に並ぶ。
       **律速は縦ではなく幅**なので、cap を動かしてもほとんど効かない。 */
    nameCapMm: 16.0,
    routeCapMm: 9.0,
    routeCapJpMm: 12.0,   // 日本語は画数が多く同じ高さでは線が寄る（run_product.py と同値）
    dataCapMm: 8.0,
    freeCapMm: 7.5,
    /* 最終行の銘。欧文だけなのでこの大きさでも字画は埋まらない
       （run_product.py の MARK / MARK_CAP_MM と同値） */
    markCapMm: 7.0,   /* ⭐ 2行に分けたので上げた（2026-09-01。9-49） */
    minCapMm: 0.8,
    /* 出典表記。国土地理院コンテンツ利用規約が出典と「加工した旨」の記載を求める。
       **model.py の ATTR / ATTR_CAP_MM / ATTR_GAP_MM と対。片方だけ変えない。**
       **「基盤地図情報」「数値標高モデル」と書かないこと。** あちらは申請が要る側の
       製品名で、こちらが申請不要なのは「地理院の標高タイルとして使っている」ことが根拠。

       ⚠️ **2026-08-29 に短縮した。** 回答（問合せ番号91080）で示された文言は
       `地理院タイル_標高タイルを加工して作成` だったが、出典明示の記入例
       （https://www.gsi.go.jp/LAW/2930-meizi.html）に自由度があると判断して短縮した。
       理由は読みやすさ ―― `_` は彫り込み4.5mmで潰れ、画面ではリンクの下線と重なる。
       **「標高タイル」は残してあるので根拠との対応は保っている。**
       ⚠️ **再照会はしていない。指摘が来たら回答の文言へ戻すこと。**
       ⚠⚠ 2026-09-01 に2度目の短縮（16字→11字）。理由は字数そのもので、
       16字は有効幅に入らず字が伸びない。model.py の ATTR と対。 */
    attr: '地理院標高タイル加工',
    /* ⚠⚠ 4.5 では S で em 3.67mm まで落ち、標・高が押し出し幅で埋まる。
       model.py の ATTR_CAP_MM と対。片方だけ変えないこと */
    attrCapMm: 7.5,   /* 6.5 では最悪の字が 8.2％、7.5 で 5.2％（2026-09-01） */
    attrGapMm: 1.5,
    /* 空きをどこまで詰めてよいか。これ以上詰めると行が団子になる
       （run_product.py 側は product.py の SPACE_MIN_RATIO） */
    spaceMinRatio: 0.35,
    /* 台紙が余っているときに引き伸ばす上限（product.py の MAX_GROW と同値） */
    maxGrow: 1.6,
    /* 刻印が頭打ちになる長辺[mm]。model.py の back_long_cap_mm と同値 */
    longCapMm: 150.0,
    mark: 'sanza.sikaworks.net',
    /* 銘の右に注文番号を並べる（run_product.py の MARK_SEP / order_no と対）。
       **番号はチェックアウトで発番されるので、この画面では存在しない。**
       伏せ字で描いておき、実物には Shopify の注文番号（order.name、例 #1001）が入る。
       **書体が等幅なので、桁が同じなら数字の中身によらず幅は完全に一致する**
       （実測 #1001 / #8888 / #0000 いずれも702px）。**伏せ字と実物で版面がずれない。**
       ここが刻印の唯一の確認機会なので、幅が合うことが要る。
       Shopifyの注文番号は #1001 始まりなので4桁を仮に置いている。
       **5桁になっても銘は縮まない**（実測。6桁で初めてSサイズの銘だけ54→52pxになる）。 */
    markGapMm: 1.2,   /* 銘と注文番号のあいだ。2行を組として近づける */
    markPlaceholder: '#0000',
    /* 製造側が刻印に使う書体そのもの（BIZ-UDGothicB.ttc）。
       別の書体で測ると、画面では収まっているのに実物は縮む。 */
    font: '"BIZ UDGothic", "Noto Sans JP", sans-serif',
  };

  /** 日本語（かな・漢字）を含むか。product.py のフォント切替と同じ判定。 */
  function hasJp(text) {
    for (let i = 0; i < text.length; i++) {
      if (text.charCodeAt(i) > 0x2e80) return true;
    }
    return false;
  }


  class Designer {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-r3d-form]');
      if (!this.form) return;

      this.hasProduct = root.dataset.hasProduct === 'true';
      this.engraveYes = root.dataset.engraveYes || 'あり';

      /*
        同時に使える色の数 = マルチマテリアル装置のスロット数。**ベース色を含む。**
        地形のベース色が必ず1本消費するので、ルートに使えるのは (これ - 1) 色。
        4スロットなら「ベース + ルート3色」。セクション設定で変えられる。
      */
      this.maxColors = parseInt(root.dataset.maxColors, 10) || 4;

      /* 今月の受付を止めているか。止めているあいだはカート追加だけを塞ぐ */
      this.closed = root.dataset.closed === 'true';
      this.closedNotice =
        root.dataset.closedNotice || '今月の受付は終了しました。翌月初に再開します。';

      /* ルート色の一覧。トラック別の割り当て行と、地図の描き分けに使う */
      this.palette = [];
      const pjson = root.querySelector('[data-r3d-route-palette]');
      if (pjson) {
        try {
          this.palette = JSON.parse(pjson.textContent);
        } catch (e) {
          this.palette = [];
        }
      }

      this.variants = [];
      const vjson = root.querySelector('[data-r3d-variants]');
      if (vjson) {
        try {
          this.variants = JSON.parse(vjson.textContent);
        } catch (e) {
          this.variants = [];
        }
      }

      this.state = {
        step: 1,
        buildType: '',
        sizeEl: null,
        baseColor: '',
        routeColor: '',
        routeMode: 'single', // 'single' = 1色 / 'date' = 日付ごと / 'split' = トラック区切りごと
        trackColors: [], // トラック番号順の色名。split のときだけ使う
        engrave: true, // 刻印は標準。選択制ではない（2026-08-19）
        /* ギャラリーの作例に使ってよいか。**null = 未選択（必須）**（2026-09-08）。
           ⚠️ **バリアントの軸1（サイズ）の値で表す。** 画面では別の欄に見せている */
        sample: null,
        gpx: null,
        latlon: null,
        latlonOutside: false,
        /* 切り出し範囲に標高データが無い（国土地理院の圏外）。
           **矩形の国内判定を通り抜ける場所がある**ので、実データの有無で最後に止める */
        noDem: false,
        margin: parseInt(root.querySelector('[data-r3d-margin]').value, 10),
      };

      this.map = null;
      this.trackLayer = null;
      this.cropLayer = null;
      this.markerLayer = null;

      /*
        GPXから自動入力した値を覚えておく。利用者が書き換えたかどうかは、
        いまの欄の値が「前回こちらが入れた値」と一致するかで判定する。
        一致していれば触られていないので、上書きも消去もしてよい。
      */
      this.autoFilled = { dateStart: '', dateEnd: '', route: '', name: '' };

      this.bind();
      this.render();
      /* URLで山を指定して来た人を、その山まで進めておく（山名ページからの導線） */
      this.applyDeepLink();
    }

    /* ---------------------------------------------------------------- *
     * イベント
     * ---------------------------------------------------------------- */

    bind() {
      const root = this.root;

      root.querySelectorAll('[data-r3d-buildtype]').forEach((btn) => {
        btn.addEventListener('click', () => {
          /* 種類が変わったときだけ初期化する。同じものを押し直しただけで
             選択済みのGPXが消えると事故になる */
          if (this.state.buildType !== btn.dataset.r3dBuildtype) {
            this.resetSelection();
          }
          this.state.buildType = btn.dataset.r3dBuildtype;
          /* 山の一覧は地形の欄でしか使わない。**ここまで来て初めて読む** */
          if (this.state.buildType !== 'route') this.loadMountains();
          /*
            選んだ時点でステップ2へ送る。ここは選択肢が2つあるだけの画面で、
            選んだあとに「次へ」を押させる意味がないため、ボタンは置いていない。
            goto() が render() と地図の invalidateSize() までやるので、
            ここで render() を呼ぶ必要はない（呼ぶと二重になる）。
          */
          this.goto(2);
        });
      });

      /* 作例利用の2択（2026-09-08）。**既定は未選択。必須。** */
      root.querySelectorAll('[data-r3d-sample]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.sample = btn.dataset.r3dSample === 'yes';
          this.render();
        });
      });

      root.querySelectorAll('[data-r3d-next]').forEach((btn) => {
        btn.addEventListener('click', () => this.goto(parseInt(btn.dataset.r3dNext, 10)));
      });
      root.querySelectorAll('[data-r3d-prev]').forEach((btn) => {
        btn.addEventListener('click', () => this.goto(parseInt(btn.dataset.r3dPrev, 10)));
      });
      root.querySelectorAll('[data-r3d-stepnav] .r3d-stepnav__item').forEach((li) => {
        li.addEventListener('click', () => {
          const target = parseInt(li.dataset.step, 10);
          // 進行済みのステップにだけ戻れる
          if (target <= this.state.step) this.goto(target);
        });
      });

      const file = root.querySelector('[data-r3d-file]');
      file.addEventListener('change', () => this.readFile(file.files && file.files[0]));
      this.bindInAppHint();

      const drop = root.querySelector('[data-r3d-drop]');
      ['dragenter', 'dragover'].forEach((ev) =>
        drop.addEventListener(ev, (e) => {
          e.preventDefault();
          drop.classList.add('is-over');
        })
      );
      ['dragleave', 'drop'].forEach((ev) =>
        drop.addEventListener(ev, (e) => {
          e.preventDefault();
          drop.classList.remove('is-over');
        })
      );
      drop.addEventListener('drop', (e) => {
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (!f) return;
        // ドロップされたファイルを file input に移し替える。
        // これをしないとフォームPOSTでGPXが送られない。
        const dt = new DataTransfer();
        dt.items.add(f);
        file.files = dt.files;
        this.readFile(f);
      });

      this.bindPlaceSearch();
      this.bindMountainPicker();

      const margin = root.querySelector('[data-r3d-margin]');
      margin.addEventListener('input', () => {
        this.state.margin = parseInt(margin.value, 10);
        root.querySelector('[data-r3d-margin-out]').textContent = this.state.margin + ' m';
        this.drawCrop();
        this.render();
      });

      root.querySelectorAll('[data-r3d-size]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.sizeEl = btn;
          this.render();
        });
      });

      root.querySelectorAll('[data-r3d-routemode]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.routeMode = btn.dataset.r3dRoutemode;
          /* まとまりの取り方が変わると、色だけでなく分割そのものが変わる。
             色の差し替え（refreshTrackColors）では足りないので描き直す */
          this.state.trackColors = [];
          if (this.state.gpx) this.drawTrack(this.state.gpx);
          this.render();
        });
      });

      /* トラック別の色見本は後からJSで作るので、親で拾う */
      const tc = root.querySelector('[data-r3d-trackcolors]');
      if (tc) {
        tc.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-r3d-trackswatch]');
          if (!btn) return;
          if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
          this.state.trackColors[parseInt(btn.dataset.track, 10)] = btn.dataset.value;
          this.render();
        });
      }

      root.querySelectorAll('[data-r3d-swatch]').forEach((btn) => {
        btn.addEventListener('click', () => {
          // 斜線つき（在庫なし／沈む）は押しても効かない。disabled でも念のため見る
          if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
          const kind = btn.dataset.r3dSwatch;
          if (kind === 'base') {
            /*
              ⚠️⚠️ **ベース色を変えたら、ルート色の選択を捨てる**（9-48）。
              **ルート色はベース色に対して選んだもの**で、地の色が変われば
              その判断は引き継げない ―― 候補の顔ぶれも、沈むかどうかも変わる。
              **残っている色がたまたま使えても、選び直してもらう。**
              ⭐ トラック別の割り当ても空にする。**新しいベース色で既定から取り直す。**
            */
            if (this.state.baseColor !== btn.dataset.value) {
              this.state.routeColor = '';
              this.state.trackColors = [];
            }
            this.state.baseColor = btn.dataset.value;
          } else {
            this.state.routeColor = btn.dataset.value;
          }
          this.render();
        });
      });

      const note = root.querySelector('[data-r3d-note]');
      const noteCount = root.querySelector('[data-r3d-note-count]');
      note.addEventListener('input', () => {
        noteCount.textContent = String(note.value.length);
      });

      root.querySelectorAll('[data-r3d-required-when-engrave]').forEach((el) => {
        el.addEventListener('input', () => {
          // ⚠️ 刻めない字を落としてから描き直す（9-54）
          this.stripEngraveField(el);
          this.render();
        });
      });

      /* 等高線は注文内容に出すので、変えたら描き直す（2026-08-29）。
         ⚠️ **この select には今までリスナが1つも無かった。** JSは値を読まず、
         フォームPOSTでそのまま送るだけの作りだったため。 */
      const contourSel = root.querySelector('select[name="properties[等高線]"]');
      if (contourSel) contourSel.addEventListener('change', () => this.render());

      /* ⭐ 有償オプション（額装・フラッグ・背面磁石）。
         ⚠️⚠️ **選ぶとバリアントが変わる** ―― 価格も、カートへ送る ID も変わるので、
         **描き直さないと画面と実際が食い違う。** */
      root.querySelectorAll('select[data-r3d-opt]').forEach((sel) => {
        sel.addEventListener('change', () => this.render());
      });

      /* 刻印欄はどれもプレビューに出る。入力のたびに描き直す */
      const engrave = root.querySelector('[data-r3d-engrave-fields]');
      if (engrave) {
        const onEngrave = () => {
          /* ⚠️⚠️ **刻めない字を先に落とす**（9-54）。**幅を測る前にやる** ――
             絵文字は幅を持つので、残したまま測ると上限の計算が狂う。 */
          this.stripAllEngraveFields();
          /* ⚠️ **`maxlength` は字数にしか効かない。** 幅で止める欄はここで削る。
             **`maxlength` は外していない** ―― 幅10は半角なら20字ちょうどなので、
             貼り付けの上限として意味がある（幅の計算に回す前に頭を止める）。 */
          engrave.querySelectorAll('[data-r3d-width-max]').forEach(function (el) {
            const max = widthLimit(el);
            if (max && textWidth(el.value) > max) {
              const at = el.selectionStart;
              el.value = trimToWidth(el.value, max);
              if (at !== null && at !== undefined) {
                const p = Math.min(at, el.value.length);
                try { el.setSelectionRange(p, p); } catch (e) { /* type によっては不可 */ }
              }
            }
          });
          this.syncDateProp();
          this.renderCounts();
          this.renderProof();
        };
        engrave.addEventListener('input', onEngrave);
        engrave.addEventListener('change', onEngrave);
      }

      /* 台紙の幅はコンテナ幅から決まるので、幅が変わったら描き直す。
         書体が届く前に測ると字幅が別物になるので、読み込み後にもう一度描く。 */
      let proofTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(proofTimer);
        proofTimer = setTimeout(() => this.renderProof(), 120);
      });
      if (document.fonts && document.fonts.load) {
        document.fonts
          .load('400 20px "BIZ UDGothic"')
          .then(() => this.renderProof())
          .catch(() => {});
      }

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    /* ---------------------------------------------------------------- *
     * ステップ遷移
     * ---------------------------------------------------------------- */

    goto(step) {
      if (step > this.state.step && !this.stepComplete(this.state.step)) return;
      this.state.step = step;
      this.render();
      // 3Dはここに来たときだけ組む（Three.jsの読み込みと標高の取得が走る）
      if (step === 3) this.renderView3d();

      const panel = this.root.querySelector('[data-r3d-panel="' + step + '"]');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const focusable = panel.querySelector('button, input, select, textarea');
        if (focusable) focusable.focus({ preventScroll: true });
      }

      // 非表示のうちに初期化した地図はサイズが 0 のままなので測り直させる
      if (step === 2) {
        this.ensureMap();
        if (this.map) window.setTimeout(() => this.map.invalidateSize(), 60);
      }
    }

    stepComplete(step) {
      const s = this.state;
      if (step === 1) return !!s.buildType;
      if (step === 2) {
        /* 標高データが無い範囲では先へ進ませない。サイズも色も選べても作れない */
        if (s.noDem) return false;
        if (s.buildType === 'route') return !!s.gpx;
        return !!s.latlon;
      }
      if (step === 3) {
        if (!s.sizeEl || !s.baseColor) return false;
        if (s.buildType === 'route' && !this.routeColorReady()) return false;
        return true;
      }
      return true;
    }

    /* ---------------------------------------------------------------- *
     * GPX / 座標
     * ---------------------------------------------------------------- */

    readFile(f) {
      const status = this.root.querySelector('[data-r3d-file-status]');
      /*
        Shopify の line item property のファイル上限が20MB。超えるとカート追加が
        分かりにくいエラーで落ちるので、読み込む前にこちらで断る。
        12万点・7MBで解析に1.2秒かかるので、これ以上大きいと画面も固まる。
      */
      const MAX_BYTES = 20 * 1024 * 1024;
      if (f && f.size > MAX_BYTES) {
        this.state.gpx = null;
        status.textContent =
          'ファイルが大きすぎます（' +
          (f.size / 1048576).toFixed(1) +
          'MB）。20MBまでのGPSログをお送りください。';
        status.className = 'r3d-drop__status is-error';
        this.clearGpxView();
        this.render();
        return;
      }
      if (!f) {
        this.state.gpx = null;
        status.textContent = '';
        this.clearGpxView();
        this.render();
        return;
      }

      // 前のファイルの表示・余白・地図の縮尺を残さない
      this.resetSelection({ keepFile: true });

      status.textContent = f.name + ' を読み込んでいます…';
      status.className = 'r3d-drop__status';

      /*
        ⚠️⚠️⚠️ **`readAsText` ではなく `readAsArrayBuffer` で読むこと。**
        **読んだバイト列をそのまま `detachFile()` に渡して、`input` の中身を
        自前のコピーに入れ替えるため**（2026-09-10。下の `detachFile` を見よ）。
        ⭐ 解析用の文字列は `TextDecoder` で作る。**`readAsText`（既定UTF-8・BOMは
        読み飛ばし）と同じ結果**になるので、解析側の挙動は変わらない。
      */
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const buf = reader.result;
          const gpx = window.R3DGpx.parse(new TextDecoder().decode(buf));
          this.state.gpx = gpx;
          this.state.gpx.fileName = f.name;
          /* ⭐ 元のファイル名を控える。**Shopify が付け替えた名前では中身が分からない** */
          this.setProp('gpxName', f.name);
          status.textContent = f.name;
          status.className = 'r3d-drop__status is-ok';
          this.fillStats(gpx);
          this.drawTrack(gpx);
          /* 解析が通ったものだけ差し替える。失敗したファイルを送っても意味がない */
          this.detachFile(f, buf);
        } catch (err) {
          this.state.gpx = null;
          this.setProp('gpxName', '');
          status.textContent = err.message || 'GPSログを読み込めませんでした。';
          status.className = 'r3d-drop__status is-error';
          /* 前のファイルの統計と軌跡を消す。エラーを出しているのに数値と枠が
             残っていると、受け付けられたように見える */
          this.clearGpxView();
        }
        this.render();
      };
      reader.onerror = () => {
        this.state.gpx = null;
        status.textContent = 'ファイルを読み込めませんでした。';
        status.className = 'r3d-drop__status is-error';
        this.render();
      };
      reader.readAsArrayBuffer(f);
    }

    /*
      読み終えたバイト列から File を作り直して、`input[type=file]` の中身を
      それに入れ替える。

      ⚠️⚠️⚠️ **これが無いと Google Drive 上のGPXでカート追加が落ちる**
      （2026-09-10。実機の Android Chrome で `net::ERR_UPLOAD_FILE_CHANGED` を確認）。

      ⚠️⚠️ **原因は「ファイルを2回読んでいる」こと。**
      **1回目はここ（選択時、解析のため）。2回目はカート追加のフォームPOSTで、
      ブラウザが元のファイルを開き直して多重パートに流し込む。**
      **端末ローカルのファイルなら実体が変わらないので通るが、Drive を選ぶと
      渡されるのは `content://`（Storage Access Framework）で、実体はその場で
      ストリーミング／キャッシュ生成される。** Chrome は選択時にサイズと更新時刻を
      控え、**送信直前にもう一度 stat して食い違えばアップロードを中止する**
      ―― それが `ERR_UPLOAD_FILE_CHANGED`。
      ⚠️ **「ステップは最後まで進むのに、カート追加だけ落ちる」** という症状に
      なるのはこのため。1回目の読み込みは成功しているので、解析も地図も通る。

      ⭐ **`DataTransfer` で作った File はページのメモリ上にあるので、`content://`
      にも端末のファイルシステムにも依存しない。** 送信時に読み直しても変わらない。
      ⭐ **解析に使ったのと同じバイト列を送る**ことになるので、
      「画面で確認したものと、届いたものが違う」事故も同時に防げる。

      ⚠️ **`input.files` への代入では `change` は発火しない。** ここは `change`
      ハンドラの中なので、発火すると無限ループになる。**発火する実装に変えないこと。**
      ⚠️ **ドロップ経路でも同じ差し替えが起きる**（あちらは先に元のFileを入れ、
      ここで自前のコピーに置き換わる）。二重でも害はない。
      ⚠️ **`DataTransfer` が無い古い環境では何もしない。** 元のファイルのまま
      送られるだけで、いまと同じ挙動に戻る。**握りつぶしてよい。**
    */
    detachFile(f, buf) {
      const input = this.root.querySelector('[data-r3d-file]');
      if (!input || !buf) return;
      try {
        const copy = new File([buf], f.name, {
          type: f.type || 'application/gpx+xml',
          lastModified: Date.now()
        });
        const dt = new DataTransfer();
        dt.items.add(copy);
        input.files = dt.files;
      } catch (e) {
        /* 差し替えられなくても注文自体は通る。Drive のときだけ従来どおり落ちる */
      }
    }

    /*
      ステップ2の選択と表示を初期状態に戻す。

      ビルドタイプを選び直したとき、GPXを選び直したときに呼ぶ。
      前の選択の名残（軌跡・マーカー・切り出し枠・統計・刻印の自動入力・
      切り出し余白・地図の縮尺）が混ざったままだと、いま何を作ろうとして
      いるのかが画面から読めなくなる。

      keepFile を立てると、input[type=file] の中身だけ残す。GPXを選び直した
      直後に呼ぶときは、選んだばかりのファイルを消してしまわないため。
    */
    resetSelection(opts) {
      const root = this.root;
      const keepFile = !!(opts && opts.keepFile);

      this.state.gpx = null;
      const status = root.querySelector('[data-r3d-file-status]');
      if (status && !keepFile) {
        status.textContent = '';
        status.className = 'r3d-drop__status';
      }
      if (!keepFile) {
        const file = root.querySelector('input[type="file"]');
        if (file) file.value = '';
      }

      // 切り出し余白は section 設定の既定へ（value属性がその値）
      const margin = root.querySelector('[data-r3d-margin]');
      if (margin) {
        margin.value = margin.defaultValue;
        this.state.margin = parseInt(margin.defaultValue, 10);
        const out = root.querySelector('[data-r3d-margin-out]');
        if (out) out.textContent = this.state.margin + ' m';
        this.setProp('margin', '');
      }

      if (this.map) {
        if (this.trackLayer) {
          this.map.removeLayer(this.trackLayer);
          this.trackLayer = null;
        }
        this.map.setView(DEFAULT_VIEW.center, DEFAULT_VIEW.zoom);
      }

      this.clearGpxView();
      // マーカー・切り出し枠・エラー表示・hidden の座標をまとめて落とす
      this.clearLatLon('');
    }

    /*
      GPXが無効になった／外されたときの後始末。

      枠を隠すだけでは足りない。中の数値・hidden property・自動入力した刻印欄が
      前のファイルのまま残り、エラーを出しているのに距離が見えている状態になる。
      刻印欄は注文に載るので特に危ない。
    */
    clearGpxView() {
      const root = this.root;
      const stats = root.querySelector('[data-r3d-stats]');
      if (stats) stats.hidden = true;
      root.querySelectorAll('[data-r3d-stat]').forEach(function (el) {
        el.textContent = '—';
      });
      this.setProp('distance', '');
      this.setProp('gain', '');
      this.setProp('peak', '');

      // 自動入力したままの欄だけ消す。利用者が書き換えていたら残す
      const dateStart = root.querySelector('[data-r3d-date-start]');
      if (dateStart && dateStart.value === this.autoFilled.dateStart) dateStart.value = '';
      this.autoFilled.dateStart = '';
      const dateEnd = root.querySelector('[data-r3d-date-end]');
      if (dateEnd && dateEnd.value === this.autoFilled.dateEnd) dateEnd.value = '';
      this.autoFilled.dateEnd = '';
      this.syncDateProp();

      const routeEl = root.querySelector('input[name="properties[ルート名]"]');
      if (routeEl && routeEl.value === this.autoFilled.route) routeEl.value = '';
      this.autoFilled.route = '';

      /* 山名は地形モードで `fillNameFromPlace` が入れる。ここはGPX向けの
         後始末だが、**`resetSelection()` から呼ばれる＝種類を切り替えたとき**に
         通るので、自動入力の落とし場所をこの1か所に揃えてある。
         地形で「富士山」を入れたあとルートへ切り替えたら、山名も落ちる。 */
      const nameEl = root.querySelector('input[name="properties[山名]"]');
      if (nameEl && nameEl.value === this.autoFilled.name) nameEl.value = '';
      this.autoFilled.name = '';
      this.renderCounts();

      const empty = this.root.querySelector('[data-r3d-map-empty]');
      if (empty) empty.hidden = false;
      if (this.map) {
        if (this.trackLayer) {
          this.map.removeLayer(this.trackLayer);
          this.trackLayer = null;
        }
        if (this.cropLayer) {
          this.map.removeLayer(this.cropLayer);
          this.cropLayer = null;
        }
        this.cropSig = null;
      }
      const dims = this.root.querySelector('[data-r3d-dims]');
      if (dims) dims.hidden = true;
      const mesh = this.root.querySelector('[data-r3d-mesh]');
      if (mesh) mesh.hidden = true;
      this.setProp('mesh', '');
      this.modelDims = null;
      /* 等高線の推奨。斜度の分布は範囲ごと、文面は範囲×サイズごとに作り直す */
      this.contourStats = null;
      this.contourStatsSig = null;
      /* 標高差を取りに行っている最中の範囲。二重に投げないための札 */
      this.statsPending = null;
      this.contourRecSig = null;
    }

    fillStats(gpx) {
      const root = this.root;
      root.querySelector('[data-r3d-stats]').hidden = false;
      const set = (k, v) => {
        root.querySelector('[data-r3d-stat="' + k + '"]').textContent = v;
      };

      set('distance', km(gpx.distanceM));
      set('gain', gpx.elevationGainM === null ? '記録なし' : meters(gpx.elevationGainM));
      set('start', gpx.startLabel || '記録なし');
      set('end', gpx.endLabel || '記録なし');
      /*
        「トラック数」= <trk> の数。<trkseg> の数ではない。
        色分けが分割に使う単位と同じにしてある。**表示された数がそのまま
        色の数の上限になる。** trkseg を数えていた時期があるが、trkseg の
        切れ目は記録の一時停止で入るもので山行の意味と一致せず、名前も持たない。
      */
      const trackCount = (gpx.trackNames && gpx.trackNames.length) || 1;
      set('tracks', trackCount.toLocaleString('ja-JP') + ' 本');
      set('points', gpx.pointCount.toLocaleString('ja-JP') + ' 点');

      // 刻印用の hidden property
      this.setProp('distance', km(gpx.distanceM));
      this.setProp('gain', gpx.elevationGainM === null ? '' : meters(gpx.elevationGainM));
      /*
        ⭐ **最高標高もGPXの値**（2026-09-09）。**距離・累積標高と出どころを揃える。**
        ⚠️⚠️ **一度DEM由来にしたが、同日に戻した。** 理由は2つ。
        **① 問い合わせに答えられない** ―― **DEMでも地形図の標高点は出ない。**
        **実測（妙義）: 地形図の相馬岳1,104mに対し、5mメッシュのDEMは1,101.4m、
        ルート最高点の周囲120mを探しても1,102.7m止まり。標高点は測量による点の値で、
        5mメッシュはそこを滑らかにしてしまう。**
        **② プレビューと銘板が食い違う** ―― **ブラウザは10mメッシュ(z14)、
        製造は5mメッシュ(z15)。同じルートで1,099.9 と 1,101.4 に割れた。**
        ⭐ **GPX由来なら「お送りいただいたログの値です」で説明が通る。**
      */
      this.setProp('peak', gpx.elevationMaxM === null || gpx.elevationMaxM === undefined
        ? '' : meters(gpx.elevationMaxM));

      /*
        日付欄はGPXの値（JST変換済み）で自動入力する。
        「空のときだけ入れる」だと、別のGPXに差し替えても前のファイルの日付が
        残り、違う日付が刻まれる。空か、前回こちらが入れた値のままなら上書きする
        （＝利用者が手で直した値は守る）。
      */
      this.fillDatesFromGpx(gpx);

      this.prefetch3d();

      // ルート名は GPX の <trk><name> を初期値として提示するだけ。
      // 自動確定はしない（誤読を刻まないため）。
      // 2026-08-19 まではASCIIだけを採っていた（欄が英語表記専用だったため）。
      // 日本語も刻めるようにしたので、ヤマレコ等の日本語トラック名もそのまま使う。
      const routeEl = root.querySelector('input[name="properties[ルート名]"]');
      if (routeEl && (!routeEl.value || routeEl.value === this.autoFilled.route)) {
        const usable = usableTrackName(gpx.trackName);
        routeEl.value = usable;
        // ⚠️⚠️ **GPX の名前にこそ絵文字が入っている**（実データ12本中7本。9-54）。
        // **入れた直後に落とす。** 落としたことは欄の下に出る
        this.stripEngraveField(routeEl);
        this.autoFilled.route = routeEl.value;
      }
      this.renderCounts();
    }

    /* ---------------------------------------------------------------- *
     * 地域 → 山 で選ぶ（国土地理院「日本の主な山岳標高（1003山）」）
     *
     * 一覧はテーマに同梱してある（`assets/r3d-mountains.js`、54KB）。
     * **地形を選んだときだけ読む。** ルート（GPX）で来た人には要らない。
     * ---------------------------------------------------------------- */

    ensureMountains() {
      if (this.mountainsPromise) return this.mountainsPromise;
      if (window.R3DMountains) {
        this.mountainsPromise = Promise.resolve(window.R3DMountains);
        return this.mountainsPromise;
      }
      const url = this.root.dataset.mountainsUrl;
      if (!url) return Promise.resolve(null);

      this.mountainsPromise = new Promise((resolve) => {
        const sc = document.createElement('script');
        sc.src = url;
        sc.onload = () => resolve(window.R3DMountains || null);
        /* 読めなくても止めない。下の「山名で探す」と地図クリックが残っている */
        sc.onerror = () => resolve(null);
        document.head.appendChild(sc);
      });
      return this.mountainsPromise;
    }

    bindMountainPicker() {
      const pref = this.root.querySelector('[data-r3d-pref]');
      const peak = this.root.querySelector('[data-r3d-peak]');
      if (!pref || !peak) return;

      pref.addEventListener('change', () => this.fillPeaks(pref.value));
      peak.addEventListener('change', () => {
        const i = parseInt(peak.value, 10);
        const list = (this.mountains && this.mountains.m[pref.value]) || [];
        const m = list[i];
        if (!m) return;
        /* [山名, 標高, 緯度, 経度]。**緯度が先**（検索APIのGeoJSONとは逆なので注意） */
        this.pickPlace(m[2], m[3], m[0]);
      });
    }

    /* ---------------------------------------------------------------- *
     * URL で山を指定して開く（2026-09-08 に追加）
     *
     * 山名ページ（`/pages/yarigatake` など）から
     * `/pages/designer?mountain=槍ヶ岳` で来たときに、
     * **地形モードで、その山を選んだ状態**まで進めておく。
     *
     * ⚠️⚠️ **ルート（GPX）モードには効かない。** 切り出す範囲を決めるのはGPXなので、
     * 山名を先に渡しても使い道がない。**山名ページ側でボタンを2つに分けること**
     * ―― 「GPSログから作る」は素の `/pages/designer` へ、
     * 「この山の地形から作る」だけが `?mountain=` を付ける。
     *
     * ⚠️ **同名の山が複数の県にある**（白馬岳は長野県と富山県の両方に入っている）。
     * `pref` を添えれば指定できるが、無ければ最初に見つかった県を使う。
     * **座標は同じ**なので、どちらを選んでも同じ地点になる。
     *
     * ⚠️⚠️ **見つからなくても止めない。** 何もせず通常の画面のままにする。
     * URLを手で書き換えられたり、山名の表記が変わったりしても壊れないため。
     * ---------------------------------------------------------------- */

    applyDeepLink() {
      if (typeof window.URLSearchParams !== 'function') return;
      const q = new window.URLSearchParams(window.location.search);
      const wanted = (q.get('mountain') || '').trim();
      const wantedPlace = (q.get('place') || '').trim();
      if (!wanted && !wantedPlace) return;
      const wantedPref = (q.get('pref') || '').trim();

      const btn = this.root.querySelector('[data-r3d-buildtype="terrain"]');
      if (!btn) return;
      /*
        ボタンを押したことにする。**loadMountains() と goto(2) を自前で書き直さない**
        ―― 同じ手順を2か所に置くと、片方だけ直す事故になる。
      */
      btn.click();

      /* `?mountain=` が無ければ `?place=` を見る。**両方あるときは一覧のほうを採る**
         ―― 一覧は出典（国土地理院「日本の主な山岳標高」）が決まっていて、標高も持っている */
      if (!wanted) {
        this.applyDeepLinkPlace(wantedPlace);
        return;
      }

      /*
        `fillPrefs()` は `loadMountains()` の中で同じ Promise に繋いである。
        こちらは**そのあとに繋ぐ**ので、都道府県の選択肢が埋まってから動く。
        （`this.mountains` が入るのも先に走るほうの中）
      */
      this.ensureMountains().then((data) => {
        if (!data) return;
        const prefs = wantedPref ? [wantedPref] : Object.keys(data.m);

        /*
          まず完全一致で探し、無ければ「別名つき」だけを拾う。

          ⚠️⚠️ **一覧の名前には別名が括弧で付いているものがある**
          ―― `羊蹄山（蝦夷富士）` `アトサヌプリ（硫黄山）`。
          **完全一致だけだと `羊蹄山` で無反応になる**（2026-09-08に実測して判明）。

          ⚠️ **拾うのは「指定した名前 ＋ 全角の開き括弧」で始まるものだけ。**
          前方一致まで広げると `白馬` が `白馬岳` にも `白馬鑓ヶ岳` にも当たり、
          **どちらを出すかを機械が勝手に決める**ことになる。
        */
        const hit = (name, pass) => (pass === 0 ? name === wanted : name.indexOf(wanted + '（') === 0);

        for (let pass = 0; pass < 2; pass++) {
          for (let p = 0; p < prefs.length; p++) {
            const list = data.m[prefs[p]] || [];
            for (let i = 0; i < list.length; i++) {
              if (!hit(list[i][0], pass)) continue;

              const pref = this.root.querySelector('[data-r3d-pref]');
              const peak = this.root.querySelector('[data-r3d-peak]');
              if (pref) pref.value = prefs[p];
              this.fillPeaks(prefs[p]);
              if (peak) peak.value = String(i);

              /* [山名, 標高, 緯度, 経度]。**緯度が先**（検索APIのGeoJSONとは逆） */
              this.pickPlace(list[i][2], list[i][3], list[i][0]);
              return;
            }
          }
        }
      });
    }

    /* ---------------------------------------------------------------- *
     * `?place=<山名>` で開く（2026-09-15に追加。残件 X22）
     *
     * ⚠️⚠️ **`?mountain=` は一覧（`r3d-mountains.js` の987山）しか探さない。**
     * **一覧に無い山は山名ページを作っても designer へ流せなかった**
     * ―― 仙人ヶ岳がそれで、素の `/pages/designer` へ飛ばして逃げていた。
     * ⭐ **こちらは画面の「山名で探す」と同じ地理院の地名検索を叩く**ので、
     * **一覧に無い山でも通る。**
     *
     * ⚠️ **一覧に山を手で足す案は採らない** ―― `r3d-mountains.js` の出典は
     * 国土地理院「日本の主な山岳標高」で、手で足すと出典と中身がずれる。
     *
     * ⚠️⚠️⚠️ **「1件目を採る」は駄目**（2026-09-15に実測）。
     * **目的の山が1件目に来るとは限らない** ―― `谷川岳` は6番目で、
     * 上位は「栃木県那珂川町谷川」「群馬県みなかみ町谷川」だった。
     * **`槍ヶ岳` も1件目は「鹿島槍ヶ岳」。**
     *
     * ⚠️⚠️ **同じ名前が2件返る山がある** ―― `唐松岳`（2件）`大源太山`（2件）。
     * ⭐ **完全一致がちょうど1件のときだけ選ぶ。**
     * **2件以上なら選ばず、その2件を先頭に並べて本人に選んでもらう**
     * ―― どちらを採るかを機械が決めない、という `?mountain=` と同じ考え方。
     *
     * ⚠️ **見つからなくても止めない。** 検索欄に言葉が入った通常の画面のままにする。
     * ---------------------------------------------------------------- */

    applyDeepLinkPlace(wanted) {
      const input = this.root.querySelector('[data-r3d-place]');
      if (!input || !wanted) return;
      input.value = wanted;

      const title = (row) => (row && row.properties && row.properties.title) || '';
      const coords = (row) => {
        const g = row && row.geometry;
        return g && Array.isArray(g.coordinates) ? g.coordinates : null;
      };

      this.searchPlace(wanted, (rows) => {
        const exact = rows.filter((r) => title(r) === wanted && coords(r));

        if (exact.length === 1) {
          /* ⚠️ **「探しています…」を自分で消す** ―― `pickPlace()` は候補一覧は畳むが、
             メッセージ欄は触らない。消さないと出たまま残る（2026-09-15に実物で確認） */
          this.placeMessage('');
          const c = coords(exact[0]);
          /* **GeoJSON は [経度, 緯度]。** pickPlace は緯度が先 */
          this.pickPlace(c[1], c[0], title(exact[0]));
          return;
        }

        /*
          選べないときは、完全一致だけを先頭へ寄せてから描く。
          ⚠️⚠️ **`renderPlaces()` は上から6件しか出さない** ―― 寄せないと、
          **同名2件のうち片方が6件目より後ろに沈んで見えなくなる**
          （`大源太山` は5番目と6番目に入っており、実際に片方が切れる）。
        */
        const rest = rows.filter((r) => exact.indexOf(r) === -1);
        this.renderPlaces(exact.concat(rest));

        /*
          ⚠️⚠️ **ここで `placeMessage()` を使わない** ―― **あれは文字を出すときに
          候補一覧を消す**ので、いま描いたばかりの一覧が消える。
          ⭐ 一覧と言葉を**同時に出したいのはここだけ**なので、欄へ直接書く。
        */
        if (exact.length > 1) {
          const err = this.root.querySelector('[data-r3d-place-error]');
          if (err) {
            err.textContent =
              '同じ名前の地点が' + exact.length + '件あります。どちらかをお選びください。';
            err.hidden = false;
          }
        }
      });
    }

    /* 一覧を読んで都道府県の選択肢を作る。**地形を選んだときに初めて呼ぶ。**
       bind() から呼ぶと、GPXで来た人にも53KBを読ませることになる */
    loadMountains() {
      if (this.mountainsFilled) return;
      this.mountainsFilled = true;

      const pref = this.root.querySelector('[data-r3d-pref]');
      if (!pref) return;

      this.ensureMountains().then((data) => {
        if (!data) return;
        this.mountains = data;
        this.fillPrefs();
      });
    }

    /*
      都道府県の選択肢（47件）を作る。

      **地方は `optgroup` の見出しとして出す。選ばせない。**
      以前は地方の select を1段目に置いていたが、`optgroup` で地方が見えている以上、
      同じことを2度させているだけだった（2026-08-20に廃止）。
      `regions` は並び（北から南）と束ね方を決めるためだけに使う。
    */
    fillPrefs() {
      const pref = this.root.querySelector('[data-r3d-pref]');
      if (!pref || !this.mountains) return;

      pref.textContent = '';
      const head = document.createElement('option');
      head.value = '';
      head.textContent = '都道府県を選ぶ';
      pref.appendChild(head);

      this.mountains.regions.forEach((r) => {
        const g = document.createElement('optgroup');
        g.label = r[0];
        r[1].forEach((name) => {
          const o = document.createElement('option');
          o.value = name;
          /* 山の数を添える。5件しかない県もあるので、開く前に見当がつく */
          o.textContent = name + '（' + (this.mountains.m[name] || []).length + '）';
          g.appendChild(o);
        });
        pref.appendChild(g);
      });
    }

    fillPeaks(prefName) {
      const peak = this.root.querySelector('[data-r3d-peak]');
      if (!peak) return;
      const list = (this.mountains && this.mountains.m[prefName]) || [];

      peak.textContent = '';
      const head = document.createElement('option');
      head.value = '';
      head.textContent = '山を選ぶ';
      peak.appendChild(head);

      list.forEach((m, i) => {
        const o = document.createElement('option');
        o.value = String(i);
        /* 標高を添える。同名の山が同じ県に並ぶことがあり、名前だけでは選べない */
        o.textContent = m[0] + '（' + m[1].toLocaleString('ja-JP') + 'm）';
        peak.appendChild(o);
      });
      peak.disabled = !list.length;
    }

    /* ---------------------------------------------------------------- *
     * アプリ内ブラウザで GPX が選べない（2026-09-16に実機で判明。残件つき）
     *
     * ⚠️⚠️⚠️ **Instagram のプロフィールのリンクから来ると、外のブラウザではなく
     * アプリの中のブラウザで開く。** **そこでファイル選択が写真と動画しか出さず、
     * `.gpx` にたどり着けない。** ⭐ **注文の出口がそこで詰まる。**
     *
     * ⭐ **こちらの `accept` に画像の型は入っていない**（`.gpx,application/gpx+xml,text/xml`）。
     * **アプリ側がそういう選択画面を出している。**
     *
     * ⚠️⚠️ **OSの権限を変えてもらう案内はしない。**
     * **手順が機種とAndroidの版で違い、たどり着けない人のほうが多い。**
     * ⭐⭐ **「ブラウザで開き直す」だけを案内する。**
     *
     * ⭐ 出す入口は**名乗りだけ**。**アプリ内ブラウザと分かったら、すぐ出す。**
     *
     * ⚠️⚠️⚠️ **「何も選ばずに戻ってきたら出す」は作ったが、効かないので外した**（2026-09-16）。
     * **アプリ内ブラウザのファイル選択は別の窓ではなく画面内の重ね表示で、
     * 戻っても `window` の `focus` が飛ばない**（実機で確認）。
     * ⚠️ **残る手は「押してからN秒たってもファイルが来なければ出す」タイマーだが、採らない**
     * ―― **パソコンでファイル選択に時間をかけた人にも「右上の ⋮ から」が出てしまう。**
     * ⭐⭐ **名乗りで拾えないアプリの人は FAQ が受け皿**（「スマホでGPXファイルが選べません」）。
     * ⭐ **実機で踏んだアプリが出たら `IN_APP` に足す。**
     *
     * ⚠️ **見当違いでも害は無い**（案内が1行出るだけ。閉じられる）。
     * ⚠️⚠️ **iOS で同じことが起きるかは未確認。** **文面は機種の操作を名指ししない。**
     * ---------------------------------------------------------------- */

    /* ⭐ 名乗りにアプリの名前が入るものだけ。⚠️ 増やすのは実機で踏んでから */
    static get IN_APP() {
      return /Instagram|FBAN|FBAV|FB_IAB|Line\/|Twitter|TikTok/i;
    }

    /* ⚠️ **閉じるは付けていない。**「一度閉じると戻す手が無い」ため（2026-09-16の判断）。
       ⭐ **アプリ内ブラウザの間は出したままにする。** */
    hintInApp() {
      const el = this.root.querySelector('[data-r3d-inapp-hint]');
      if (el) el.hidden = false;
    }

    bindInAppHint() {
      const el = this.root.querySelector('[data-r3d-inapp-hint]');
      if (!el) return;

      if (Designer.IN_APP.test(window.navigator.userAgent || '')) this.hintInApp();
    }

    /* ---------------------------------------------------------------- *
     * 山名で地点を探す（国土地理院の地名検索API）
     * ---------------------------------------------------------------- */

    bindPlaceSearch() {
      const input = this.root.querySelector('[data-r3d-place]');
      const go = this.root.querySelector('[data-r3d-place-go]');
      if (!input || !go) return;

      /*
        **Enter でフォームを飛ばさない。** カート追加は通常のフォームPOSTなので、
        この欄で Enter を押すと注文が送信されてしまう（`type="submit"` のボタンが
        フォーム内にあるため、既定では Enter が押したことになる）。
      */
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this.searchPlace(input.value);
      });
      go.addEventListener('click', () => this.searchPlace(input.value));
    }

    /** 検索欄の下に出すメッセージ。候補一覧は消す */
    placeMessage(text) {
      const err = this.root.querySelector('[data-r3d-place-error]');
      const list = this.root.querySelector('[data-r3d-places]');
      if (err) {
        err.textContent = text || '';
        err.hidden = !text;
      }
      if (list && text) {
        list.textContent = '';
        list.hidden = true;
      }
    }

    /*
      `after` は検索が終わったあとに、返ってきた行をそのまま渡して呼ぶ（2026-09-15に追加）。
      **`applyDeepLink()` が `?place=` で来たときに使う。**
      ⚠️ **画面から押したときは渡さない**ので、これまでの動きは変わらない。
    */
    searchPlace(q, after) {
      const text = String(q || '').trim();
      const list = this.root.querySelector('[data-r3d-places]');
      if (!text) {
        this.placeMessage('');
        return;
      }

      /* 続けて検索されたときは、最後のものだけを採る。
         先に投げたほうが後から返ると、選んだつもりのない候補が並ぶ */
      const seq = (this.placeSeq = (this.placeSeq || 0) + 1);
      this.placeMessage('探しています…');

      fetch(PLACE_SEARCH + encodeURIComponent(text))
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
        .then((rows) => {
          if (seq !== this.placeSeq) return;
          const arr = Array.isArray(rows) ? rows : [];
          if (typeof after === 'function') {
            after(arr);
            return; /* 並べ方まで after が決める。二重に描かない */
          }
          this.renderPlaces(arr);
        })
        .catch(() => {
          if (seq !== this.placeSeq) return;
          this.placeMessage(
            '検索に接続できませんでした。「山を選ぶ」からもお選びいただけます。'
          );
        });
    }

    renderPlaces(rows) {
      const list = this.root.querySelector('[data-r3d-places]');
      if (!list) return;

      if (!rows.length) {
        this.placeMessage('見つかりませんでした。「山を選ぶ」からもお探しいただけます。');
        return;
      }

      this.placeMessage('');
      list.textContent = '';
      /* 多すぎると欄が地図を押し下げるので上から数件だけ。
         山名は上位に出る（実測: 宮之浦岳・仙人ヶ岳とも該当が1件目） */
      rows.slice(0, 6).forEach((row) => {
        const g = row && row.geometry;
        const p = row && row.properties;
        if (!g || !Array.isArray(g.coordinates) || !p) return;
        /* **GeoJSON なので [経度, 緯度] の順。** 逆に読むと海の上に飛ぶ */
        const lon = g.coordinates[0];
        const lat = g.coordinates[1];

        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'r3d-place';
        /* API が返す文字列なので textContent で入れる（innerHTML にしない） */
        btn.textContent = p.title || '（名称なし）';
        btn.addEventListener('click', () => this.pickPlace(lat, lon, p.title));
        li.appendChild(btn);
        list.appendChild(li);
      });
      list.hidden = !list.children.length;
    }

    /* 山を決めたときの共通の受け口。「地域→山」と「山名で探す」の両方がここへ来る。
       **keepView は立てない。** 選んだ山へ地図が動いてほしい場面なので
       drawCrop() の fitBounds を効かせる */
    pickPlace(lat, lon, title) {
      this.setPoint(lat, lon);

      const list = this.root.querySelector('[data-r3d-places]');
      if (list) {
        list.textContent = '';
        list.hidden = true;
      }
      /* 範囲外なら setPoint がエラーを出しているので、こちらは黙る。
         二重に出すと同じことを2か所で言うことになる */
      const place = this.root.querySelector('[data-r3d-place]');
      if (place && this.state.latlon) place.value = title || place.value;

      if (this.state.latlon) this.fillNameFromPlace(title);
    }

    /*
      選んだ地点の名前を、刻印の「山名」欄へ初期値として入れる（2026-08-29に追加）。

      **地形モードでは、利用者は山名か地名を指定して地点を決めている。**
      その名前をもう一度手で書かせる理由が無い。GPXモードの「ルート名」を
      `<trk><name>` で埋めているのと同じ考え方。

      **提示するだけで、自動確定はしない。** 空か、前回こちらが入れた値のままの
      ときだけ上書きする（＝手で直した値は守る）。刻印欄は注文に載るので、
      利用者が書いた文字を勝手に置き換えないこと。

      ⚠️ **12文字を超える名前は入れない。** 欄は maxlength="12" だが、
      それは手入力にしか効かず、`value` に代入すれば超過した値が入ってしまう。
      **地名検索（地理院 AddressSearch）は住所のような長い title も返す**ので、
      入れると文字数カウンタが 15/12 のようになり、切り詰めれば違う名前を刻む。
      **入れずに placeholder のままにして、利用者に書いてもらう。**
    */
    fillNameFromPlace(title) {
      const el = this.root.querySelector('input[name="properties[山名]"]');
      if (!el) return;
      const max = parseInt(el.getAttribute('maxlength'), 10) || 12;
      const usable = String(title || '').trim();
      if (!usable || usable.length > max) return;
      if (el.value && el.value !== this.autoFilled.name) return;   // 手で直した値は守る
      el.value = usable;
      this.autoFilled.name = usable;
      this.renderCounts();
    }

    /** 緯度経度が無効になったときの後始末。地図のマーカーと枠も消す */
    clearLatLon(message) {
      this.state.latlon = null;
      this.state.latlonOutside = !!message;
      /* 読み取れなくなったら hidden 側も空に戻す。残しておくと、画面上は
         「未入力」なのに送信対象には前の座標が入ったままになる。
         カート追加は validate() が止めるので注文には届かないが、
         状態としてずれるので揃えておく（空なら pruneForSubmit が外す）。 */
      this.setProp('latlon', '');
      const err = this.root.querySelector('[data-r3d-point-error]');
      if (err) {
        err.textContent = message || '';
        err.hidden = !message;
      }
      /* 前の地点の名残を地図に残さない。範囲外を選んだのに枠が出たままだと
         「受け付けられた」ように見える */
      if (this.map) {
        if (this.markerLayer) {
          this.map.removeLayer(this.markerLayer);
          this.markerLayer = null;
        }
        if (this.cropLayer) {
          this.map.removeLayer(this.cropLayer);
          this.cropLayer = null;
        }
        this.cropSig = null;
      }
      this.render();
    }

    /*
      地点を決める。**呼ぶのは「地域→山」と「山名で探す」の2か所だけ。**
      緯度経度の直接入力と地図クリックは 2026-08-20 に廃止した。

      国外の判定は残してある。**いまの2経路はどちらも国内のデータしか返さないので
      通らないはずだが、経路を足したときの歯止めとして置いておく。**
    */
    setPoint(lat, lon) {
      if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
        this.clearLatLon('');
        return;
      }
      if (!window.R3DGpx.isInJapan(lat, lon)) {
        this.clearLatLon(window.R3DGpx.OUT_OF_RANGE_MESSAGE_POINT);
        return;
      }
      this.state.latlon = { lat: lat, lon: lon };
      this.state.latlonOutside = false;
      const errEl = this.root.querySelector('[data-r3d-point-error]');
      if (errEl) errEl.hidden = true;
      this.setProp('latlon', lat.toFixed(6) + ', ' + lon.toFixed(6));
      this.drawPoint(lat, lon);
      this.prefetch3d();
      this.render();
    }

    /* ---------------------------------------------------------------- *
     * 地図
     * ---------------------------------------------------------------- */

    ensureMap() {
      if (this.map || typeof window.L === 'undefined') return;
      const el = this.root.querySelector('[data-r3d-map]');
      this.map = window.L.map(el, { scrollWheelZoom: false, attributionControl: true });
      window.L
        .tileLayer(GSI_TILE, { attribution: GSI_ATTR, maxZoom: 18 })
        .addTo(this.map);

      /*
        陰影起伏図を淡色地図の上に重ねる。**切り出し範囲の良し悪しは起伏で決まる**ので、
        ステップ2の時点で地形が読めるほうがよい（立体はステップ3まで出ない）。

        **重ね方は `multiply` の 0.45。** 地理院の陰影起伏図は中間グレー基準（平均126）で、
        真っ白基準ではない。そのため `overlay` / `soft-light` では**淡色地図の上でほぼ何も
        起きない**（実測: 明るさ比100%、起伏はまったく出なかった）。乗算だけが起伏を出す。

        濃さは実測で決めた（4タイルの明るさの標準偏差＝起伏の出方 / 平均 / 暗い側5%）:
          重ねない 9〜15 / 251〜253 / 222
          0.25     23〜27 / 219〜231 / 185
          **0.45   39〜44 / 194〜214 / 140  ← これ**
          0.60     51〜58 / 175〜201 / 103（地図が暗く沈む）

        軌跡と切り出し枠はベクタ（overlayPane）でこの上に描かれるので、混色の影響は受けない。
        出典は同じ地理院タイルなので、表記はベース側のものがそのまま効く。
      */
      window.L
        .tileLayer(GSI_SHADE, {
          maxZoom: 18,
          maxNativeZoom: GSI_SHADE_MAX_Z,
          opacity: 0.45,
          className: 'r3d-map__shade',
        })
        .addTo(this.map);

      this.map.setView(DEFAULT_VIEW.center, DEFAULT_VIEW.zoom);

      /*
        **地図はクリックを受けない**（2026-08-20）。地点は「地域→山」か
        「山名で探す」で決める。地図は決まった範囲を見せる場所にした。
      */
    }

    /*
      色を選ぶ画面に出す軌跡の図。

      ステップ3からはステップ2の地図が見えないので、ここにも軌跡を描く。
      地図タイルは読まず、点列を viewBox に収めるだけの単純な投影にしている
      （x に cos(緯度) を掛けて、緯度が高いところで横に伸びるのを防ぐ）。
      各区切りの中点に番号を置き、下の行と対応させる。
    */
    renderTrackMap() {
      const svg = this.root.querySelector('[data-r3d-trackmap]');
      const gpx = this.state.gpx;
      if (!svg) return;
      /* GPXが無ければ枠ごと出さない（地形のときは軌跡が無い） */
      const wrap = this.root.querySelector('[data-r3d-trackmap-wrap]');
      if (wrap) wrap.hidden = !(gpx && gpx.points && gpx.points.length);
      /*
        図の地はベース色にする。地形の上にルートが乗るので、実物の見え方に近い。
        ベース色と同じ色をルートに選ぶと、ここで見えなくなる。それが正しい。
        未選択のあいだは既定の生成りのまま。
      */
      const bg = this.baseHex();
      svg.style.background = bg || '';
      if (!gpx || !gpx.points.length) {
        svg.innerHTML = '';
        return;
      }
      const W = 400;
      const H = 200;
      const pad = 14;
      const pts = gpx.points;
      const midLat = (gpx.bbox.south + gpx.bbox.north) / 2;
      const kx = Math.cos((midLat * Math.PI) / 180);
      const xs = pts.map(function (p) {
        return p.lon * kx;
      });
      const ys = pts.map(function (p) {
        return p.lat;
      });
      const minX = Math.min.apply(null, xs);
      const maxX = Math.max.apply(null, xs);
      const minY = Math.min.apply(null, ys);
      const maxY = Math.max.apply(null, ys);
      const spanX = maxX - minX || 1e-9;
      const spanY = maxY - minY || 1e-9;
      const scale = Math.min((W - pad * 2) / spanX, (H - pad * 2) / spanY);
      const offX = (W - spanX * scale) / 2;
      const offY = (H - spanY * scale) / 2;
      const proj = function (i) {
        return [
          offX + (xs[i] - minX) * scale,
          // SVG は下が正。南北を反転させる
          H - (offY + (ys[i] - minY) * scale),
        ];
      };

      const seg2 = this.segmentation();
      const segs = seg2.lines;
      const out = [];
      const self = this;
      const numbered = {};
      segs.forEach(function (seg, i) {
        const gi = seg2.groupOf[i] || 0;
        const d = [];
        for (let k = seg[0]; k <= seg[1]; k++) {
          const xy = proj(k);
          d.push((k === seg[0] ? 'M' : 'L') + xy[0].toFixed(1) + ' ' + xy[1].toFixed(1));
        }
        out.push(
          '<path d="' +
            d.join(' ') +
            '" fill="none" stroke="' +
            self.lineColorAt(i) +
            '" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>'
        );
        /* 単色のときは番号を出さない。まとまりが1つで意味がない */
        if (self.state.routeMode === 'single') return;
        /* 番号は色グループごとに1つだけ置く。trkseg が複数ある <trk> で
           同じ番号がいくつも出ると、まとまりの数を読み違える */
        if (numbered[gi]) return;
        numbered[gi] = 1;
        const mid = proj(Math.floor((seg[0] + seg[1]) / 2));
        out.push(
          '<circle cx="' +
            mid[0].toFixed(1) +
            '" cy="' +
            mid[1].toFixed(1) +
            '" r="9" fill="#fff" stroke="' +
            self.lineColorAt(i) +
            '" stroke-width="2"/>'
        );
        out.push(
          '<text x="' +
            mid[0].toFixed(1) +
            '" y="' +
            (mid[1] + 3.5).toFixed(1) +
            '" text-anchor="middle" font-size="10" font-weight="700" fill="#1B2A38">' +
            (gi + 1) +
            '</text>'
        );
      });
      svg.innerHTML = out.join('');
    }

    /*
      すでに描いてある軌跡の色だけを差し替える。
      モードや色を変えるたびに drawTrack を呼ぶと、ポリラインを作り直すぶん
      ちらつくうえ、fitBounds の判定も走ってしまう。
    */
    refreshTrackColors() {
      if (!this.trackLayer) return;
      const self = this;
      this.trackLayer.getLayers().forEach(function (layer, i) {
        if (layer.setStyle) layer.setStyle({ color: self.lineColorAt(i) });
      });
    }

    /** split のときに実際に使っている色の数（重複は1つと数える） */
    usedColorCount() {
      const s = this.state;
      if (s.routeMode === 'single') return s.routeColor ? 1 : 0;
      const set = {};
      for (let i = 0; i < this.trackCount(); i++) {
        if (s.trackColors[i]) set[s.trackColors[i]] = 1;
      }
      return Object.keys(set).length;
    }

    /*
      上限を超えているか。同じ色を使い回せば区切りが多くても超えない。
      **ベース色の1本を足して数える。** 装置のスロットは地形にも1本要る。
    */
    overColorLimit() {
      if (this.state.routeMode === 'single') return false;
      return 1 + this.usedColorCount() > this.maxColors;
    }

    /** ルート色の指定が済んでいるか */
    routeColorReady() {
      const s = this.state;
      if (this.overColorLimit()) return false;
      if (s.routeMode === 'date' && !this.canSplitByDate()) return false;
      if (s.routeMode === 'single') return !!s.routeColor;
      const n = this.trackCount();
      for (let i = 0; i < n; i++) {
        if (!s.trackColors[i]) return false;
      }
      return true;
    }

    /*
      線を引く単位と、色を決める単位をまとめて返す。

        lines    … [開始index, 終了index] の配列。地図と図はこの単位で線を引く
        groupOf  … lines と同じ長さ。各線が属する色グループの番号
        labels   … 色グループの見出し
        count    … 色グループの数

      **線は <trkseg> 単位、色は <trk> 単位**（split のとき）。trkseg で切らずに
      引くと、記録が途切れた区間を直線がまたぐ。かといって trkseg ごとに色を
      変えると、休憩でアプリを止めただけの場所で色が変わる。
      date のときは日付のまとまりが線の単位でもある。
    */
    segmentation() {
      const g = this.state.gpx;
      const s = this.state;
      if (!g) return { lines: [], groupOf: [], labels: [], count: 1 };

      const segs = g.segments.length ? g.segments : [[0, g.points.length - 1]];
      if (s.routeMode === 'date') {
        const d = g.dateSegments || [];
        return {
          lines: d,
          groupOf: d.map(function (_, i) {
            return i;
          }),
          labels: (g.dateLabels || []).slice(),
          count: d.length || 1,
        };
      }

      const groupOf = g.segTrack && g.segTrack.length ? g.segTrack : segs.map(function () {
        return 0;
      });
      const names = g.trackNames && g.trackNames.length ? g.trackNames : [''];
      return {
        lines: segs,
        groupOf: groupOf,
        /* ⚠️ **空だけでなく、機械的な名前も番号に落とす**（2026-08-29）。
           ヤマレコ Android 26.34.3 の実出力は5本とも `<trk><name>track</name>` で、
           **色を選ぶ行の見出しが「track」5行になっていた。**
           空のときの `'トラック' + (i+1)` は元からあったが、`track` は空ではないので
           素通りしていた。**判定は刻印欄と同じ `GENERIC_TRACK_NAME` を使う。**
           ⚠️ **こちらは長さで弾かない。** 見出しは彫らないので、長い名前でも
           そのまま出したほうが利用者にはどの区間か分かる。 */
        labels: names.map(function (nm, i) {
          const name = String(nm || '').trim();
          return name && !GENERIC_TRACK_NAME.test(name) ? name : 'トラック' + (i + 1);
        }),
        count: names.length || 1,
      };
    }

    /** 地図と図に引く線 */
    colorSegments() {
      return this.segmentation().lines;
    }

    /*
      HTMLへ差し込む前のエスケープ。

      トラック名は GPX の <trk><name> から来る。利用者が持ち込むファイルの中身
      なので信用できない。細工したGPXを読ませればスクリプトが動く状態だった。
      innerHTML を組み立てる箇所では必ず通すこと。
    */
    esc(v) {
      return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /*
      注文に載せる呼び名。トラック名や日付ではなく「ルート1・ルート2」。

      画面で色を選ぶ行にはトラック名（「仙人ヶ岳」など）を出すが、注文と
      サマリーではこちらを使う。理由は3つ。
        - サマリーは幅226pxしかなく、山名と色名を並べると詰まる
        - 製造側は3MFの材質を Route1…RouteN と名付けている。番号で揃うと対応が取れる
        - 軌跡の図に振っている番号とも一致する
    */
    outputLabels() {
      const out = [];
      for (let i = 0; i < this.trackCount(); i++) out.push('ルート' + (i + 1));
      return out;
    }

    /** 色グループの見出し（色を選ぶ行に出す。トラック名や日付） */
    segmentLabels() {
      /* ⚠️⚠️ **見出しにも刻めない字が出る**（9-54）。**GPX の名前をそのまま出しているため。**
         ⭐ **画面は「彫られるもの」を見せる場所**なので、ここでも落とす ――
         **絵文字が見えていると、それが彫られると思わせてしまう。**
         ⚠️ **落として空になるなら元のまま出す**（何も出ないほうが分かりにくい）。 */
      return this.segmentation().labels.map(function (label) {
        return dropUnengravable(label).v || label;
      });
    }

    /** 色グループの数。未読込なら1 */
    trackCount() {
      if (!this.state.gpx) return 1;
      return this.segmentation().count || 1;
    }

    /** <trk> の数。分け方の選択肢を出すかどうかの判定に使う */
    trackGroupCount() {
      const g = this.state.gpx;
      if (!g) return 0;
      return (g.trackNames && g.trackNames.length) || 0;
    }

    /** このGPXで日付ごとの色分けが使えるか（全点が時刻を持つか） */
    canSplitByDate() {
      const g = this.state.gpx;
      return !!(g && g.dateSegments && g.dateSegments.length);
    }

    /*
      色名から16進を引く。

      **引けなかったときはパレットの先頭を返す。決め打ちの色を書かないこと。**
      2026-08-21 まで `#C41E28` を直書きしていたが、これは**選択肢に無い色**だった。
      2026-08-19 に Bambu の実在色へパレットを差し替えたときの取り残しで、
      **サイズを選んだ直後（ルート色を選ぶ前）のプレビューが、買えない赤で描かれていた。**
      先頭から引けば、パレットを入れ替えても選べない色は出ない。

      パレットはテーマ設定のブロック順そのままで、製造側（技術検証プロジェクトの
      run_product.py）の DEFAULT_PALETTE と**中身も順番も揃えてある**。
      先頭はどちらもスカーレットレッド #DE4343。
      **片方だけ変えると、画面で選んだ色と造形される色がずれる。**
      最後の `#DE4343` は、パレットのJSONが壊れて読めなかったときだけ効く。
    */
    hexOf(name) {
      const hit = this.palette.find(function (c) {
        return c.name === name;
      });
      if (hit) return hit.hex;
      return this.defaultRouteHex();
    }

    /** ルート色が決まっていないときに描く色。**いま選べるもの**の先頭 */
    defaultRouteHex() {
      const pal = this.defaultRouteColors();
      return (pal[0] && pal[0].hex) || '#DE4343';
    }

    /** 色グループ i の色 */
    trackColorAt(i) {
      const s = this.state;
      if (s.routeMode === 'single') return s.routeColor ? this.hexOf(s.routeColor) : this.defaultRouteHex();
      return this.hexOf(s.trackColors[i]);
    }

    /*
      選ばれているベース色の16進。未選択なら null。
      ルート色はパレットのJSONから引けるが、ベース色はスウォッチの
      data-hex から取る（色数が多く、JSONをもう1つ持つほどではない）。
    */
    baseHex() {
      const btn = this.root.querySelector('[data-r3d-swatch="base"][aria-pressed="true"]');
      return btn ? btn.dataset.hex || null : null;
    }

    /* 16進の相対輝度。WCAGの式 */
    relLum(hex) {
      const h = String(hex || '').replace('#', '');
      if (h.length !== 6) return 0;
      const c = [0, 2, 4].map(function (i) {
        const v = parseInt(h.slice(i, i + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    }

    contrast(a, b) {
      const la = this.relLum(a);
      const lb = this.relLum(b);
      return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
    }

    /*
      ⭐⭐ ここから下が「群でルート色を出し分ける」仕掛け（9-48）。

      **出し分けの単位は群であって、ベース色1つずつではない。**
      ベース色は必ず暗色か中明色のどちらかに属し、群が2つのことを決める:
        ・どのルート色を候補に出すか（route ブロックの for_dark / for_mid）
        ・沈む／沈まないを分ける規定値（暗色 2.7 ／ 中明色 1.9）

      ⚠️⚠️ **群の候補を「両ベースで共通に使える色」と読むと壊れる。**
      中明色群はダークグリーン(明度16%)とマーブル白(明度90%)で通る色が正反対なので、
      積集合を取ると**各ベースで最も良い色が必ず落ちる**（白5.12 も マリン4.29 も消える）。
      正しくは「候補は群が持ち、そのうちどれが生きるかはベース色が決める」。
    */

    /* ---------------------------------------------------------------- *
     * 刻めない文字（9-54）
     *
     * ⚠️⚠️ **実データのルート名は半分以上が刻めない。** GPX 12本のうち7本に
     * 絵文字が入っていた（💯 🤣 🧗 🏔️ と、`‼️` の後ろの異体字セレクタ）。
     * **書体（BIZ UDGothic）に無い字は □ として彫られる。**
     *
     * ⚠️ **画面では見えてしまう** ―― ブラウザは無い字を別の書体で補うので、
     * **入力欄では絵文字が出て、現物では □ になる。**
     *
     * ⭐⭐ 判定は「BIZ UDGothic を先頭にした書体で描いた絵」と
     * 「存在しない書体だけで描いた絵」の比較。
     * **一致したら、その字は BIZ UDGothic では描かれていない**（＝ブラウザが補った）。
     * **製造側 `model.py` の `_unrenderable` と同じ考え方。**
     *
     * ⚠️⚠️ **書体が届く前に判定すると、全部「刻めない」になって入力を消してしまう。**
     * **既知の文字（あ）が通ることを毎回確かめてから使う。**
     * ---------------------------------------------------------------- */
    /*
      ⚠️⚠️ **2026-09-02: 「消すものを並べる」方式では取りこぼす。**
      **最初は書体で判定したが、Google Fonts の分割配信のせいで普通の漢字まで消した。**
      **次に絵文字の範囲を並べたが、`㈱ ♪ → ♡ ‼` のような記号が残った。**

      ⭐⭐ **「残すものを並べる」方式にした。** **刻印に使う字だけを通す。**
      **新しい絵文字が増えても、こちらは何もしなくてよい。**

      ⚠️⚠️ **`・`（U+30FB）はカタカナの範囲にある。** **「槍ヶ岳・穂高岳」で必ず要る。**
      ⚠️ **`（）／＃` は全角の範囲、`〜「」々` は CJK 約物の範囲。**
      ⚠️ **互換漢字（﨑 など）は人名で使う。** **外すと客の名前が消える。**
      ⚠️ **ラテン拡張は外国語の山名・人名のため。**
      ⭐ **`①②` は残す**（U+2460–24FF）。⚠️ **`㈱` は別の範囲**（U+3200–32FF）なので落ちる。

      ⚠️ **製造側 `model.py` はこれより緩い**（書体にあるかだけを見る）。
      **画面のほうが厳しいのは意図どおり** ―― **彫れるかと、載せてよいかは別。**
    */
    notEngravable(text) {
      return dropUnengravable(text).bad;
    }

    /*
      刻めない文字を欄から取り除く。

      ⚠️ **以前は「〇〇 は刻印できないため取り除きました」と出していた**が、
      9-54 で出さないことにした（`data-r3d-dropped` の行も削除）。
      ⭐ **消えるのは絵文字と記号だけで、打ったそばから見えているので、
      断り書きが常時出るほうがうるさいという判断。**
    */
    stripEngraveField(el) {
      if (!el) return;
      const res = dropUnengravable(el.value);
      if (!res.bad.length) return;
      const at = el.selectionStart;
      const cut = el.value.length - res.v.length;
      el.value = res.v;
      try { el.setSelectionRange(Math.max(0, at - cut), Math.max(0, at - cut)); } catch (e) {}
    }

    /** 刻む3欄をまとめて見る。**GPXや検索から入れた値も通す** */
    stripAllEngraveFields() {
      const root = this.root;
      ['properties[山名]', 'properties[ルート名]', 'properties[フリーテキスト]']
        .forEach((n) => this.stripEngraveField(
          root.querySelector('[name="' + n + '"]')));
    }

    /** 群ごとのコントラスト規定値 */
    groupThreshold(group) {
      const ds = this.root.dataset;
      const v = parseFloat(group === 'mid' ? ds.thresholdMid : ds.thresholdDark);
      if (isFinite(v) && v > 0) return v;
      return group === 'mid' ? 1.9 : 2.7;
    }

    /** 選ばれているベース色のボタン。未選択なら null */
    baseSwatch() {
      return this.root.querySelector('[data-r3d-swatch="base"][aria-pressed="true"]');
    }

    /*
      ルート色1色の、いまのベース色に対する扱い。
        show   … 画面に出すか。**群が違う色と、ベース色と同じ色は出さない**
        ok     … 選べるか
        reason … 選べない理由。'oos'（まだ買っていない）/ 'weak'（沈む）

      ⚠️ 在庫を先に見る。沈むかどうかはベース色しだいで変わるが、
      買っていないことはベース色と関係なく決まっているので、そちらが強い理由になる。
    */
    routeState(c) {
      if (!c) return { show: false, ok: false, reason: '' };
      const oos = c.inStock === false;
      const btn = this.baseSwatch();
      if (!btn) {
        // ベース色を選ぶ前は絞り込めない。在庫だけ見る
        return { show: true, ok: !oos, reason: oos ? 'oos' : '' };
      }
      const group = btn.dataset.group || 'dark';
      /* 群の指定が無いのと、両方とも外してあるのは違う。
         **キーごと無いとき**（テーマエディタで足した直後）は Liquid 側が両方 true にするので、
         ここに空で来るのは「どちらの群にも出さない」と明示されたときだけ。 */
      const groups = c.groups || [];
      if (groups.indexOf(group) === -1) return { show: false, ok: false, reason: '' };
      const baseHex = String(btn.dataset.hex || '').toUpperCase();
      // 同じ色は地形と見分けがつかない（中明色群のダークグリーンで実際に起きる）
      if (String(c.hex || '').toUpperCase() === baseHex) {
        return { show: false, ok: false, reason: '' };
      }
      if (oos) return { show: true, ok: false, reason: 'oos' };
      /* ⚠️ **沈む色は止めない**（2026-09-01 に戻した）。
         **在庫は硬い制約**（そもそも刷れない）だが、**コントラストは好みの判断**で、
         沈んで見えても作れる。**選べるようにして、選んだときに警告を出す。** */
      if (this.contrast(c.hex, baseHex) < this.groupThreshold(group)) {
        return { show: true, ok: true, reason: 'weak' };
      }
      return { show: true, ok: true, reason: '' };
    }

    /** 色名から扱いを引く */
    routeStateOf(name) {
      const hit = this.palette.find(function (c) {
        return c.name === name;
      });
      return this.routeState(hit);
    }

    /*
      ⭐⭐ **加算はバリアントの価格差から出す**（2026-09-07。案B）。

      ⚠️⚠️ **画面に金額を直書きしない。** 以前は色ごとに `data-surcharge="1000"` を
      持たせていたが、**それだと「画面に出る額」と「請求される額」が別々に育つ。**
      **実際に 2026-09-07 まで、+1,000円 と出しながら加算されずに売れる状態だった。**
      ⭐ **商品の価格を正にして、そこから引き算する**ので、**ズレようがない。**

      ⚠️ 単位に注意 ―― **`v.price` は最小単位（円の100倍）。**
    */
    priceDiff(o1, a, b) {
      const va = this.variantAt(o1, a[0], a[1]);
      const vb = this.variantAt(o1, b[0], b[1]);
      if (!va || !vb) return 0;
      return Math.round((va.price - vb.price) / 100);
    }

    /** 差を測るサイズ。**未選択なら最初のサイズ** ―― 選ぶ前から額を出すため */
    feeSizeValue() {
      if (this.state.sizeEl) return this.state.sizeEl.dataset.optionValue;
      const first = this.root.querySelector('[data-r3d-size]');
      return first ? first.dataset.optionValue : '';
    }

    /*
      ベース色1つぶんの加算[円]。**素材が標準ならゼロ。**
      ⭐ **選ぶ前に分かることが要件**（9-48）。選んでから値段が変わると不意打ちになる。
      **印（＋）はスウォッチに常に出し**、**金額は色名のとなりで出す。**
    */
    surchargeOf(btn) {
      if (!btn) return 0;
      /* 有償オプションの欄（額装・フラッグ・磁石）もここを通る */
      if (btn.dataset.r3dOpt) return this.optionFeeOf(btn);
      const mat = btn.dataset.material || '標準';
      if (mat === '標準') return 0;
      const sz = this.feeSizeValue();
      const ad = this.addonOptionValue();
      return sz ? this.priceDiff(sz, [mat, ad], ['標準', ad]) : 0;
    }

    /*
      有償オプションの欄1つぶんの加算[円]。**3つとも軸なので、価格差で出る。**

      ⭐ **その欄だけを外した値との差**を測る ―― 「額装＋フラッグ」から額装を外すと
      「フラッグ」。**組み合わせの中でも1つぶんの額が出せる。**
    */
    optionFeeOf(el) {
      const val = this.optValue(el);
      if (!val || val === 'なし') return 0;
      const sz = this.feeSizeValue();
      if (!sz) return 0;
      const mat = this.materialValue();
      /* ⚠️ **軸に載る名前で外す。** `addonOptionValue()` と同じ読み替え
         （「あり」→ 欄の名前 / それ以外は選んだ値そのもの） */
      const label = val === 'あり' ? el.dataset.optLabel : val;
      const withIt = this.addonOptionValue();
      const without = withIt.split('＋').filter(function (x) { return x !== label; });
      return this.priceDiff(sz, [mat, withIt], [mat, without.length ? without.join('＋') : 'なし']);
    }

    /** 「+1,000円」の形。**金額は色名のとなりでしか出さない** */
    feeText(n) {
      return '+' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '円';
    }

    /** いま選べるルート色だけ */
    selectableRouteColors() {
      const self = this;
      const out = this.palette.filter(function (c) {
        return self.routeState(c).ok;
      });
      return out.length ? out : this.palette.slice();
    }

    /*
      既定の割り当てに使う色。**沈む色は既定にしない。**
      選べはするが、こちらから勧める色ではないので、初期値では避ける。
      避けきれないときは選べる色から取る。
    */
    defaultRouteColors() {
      const self = this;
      const good = this.palette.filter(function (c) {
        const st = self.routeState(c);
        return st.ok && st.reason !== 'weak';
      });
      return good.length ? good : this.selectableRouteColors();
    }

    /*
      ベース色を変えると、選んでいたルート色が候補から外れることがある。
      **黙って残すと、選べない色のまま注文が通ってしまう。**
      外れたものはここで落とし、必須欄として選び直させる。
    */
    pruneColorChoices() {
      const s = this.state;
      if (s.routeColor && !this.routeStateOf(s.routeColor).ok) s.routeColor = '';
      for (let i = 0; i < s.trackColors.length; i++) {
        if (s.trackColors[i] && !this.routeStateOf(s.trackColors[i]).ok) s.trackColors[i] = '';
      }
    }

    /*
      スウォッチ1つに状態を反映する。**消さずに斜線で残す。**
      在庫が無いことも、そのベース色では沈むことも、**選べる色を選ぶための情報**なので、
      黙って消すと「前はあったのに」になる。
    */
    paintSwatch(btn, st, label) {
      btn.hidden = !st.show;
      btn.classList.toggle('r3d-swatch--oos', st.reason === 'oos');
      btn.disabled = st.show && !st.ok;
      btn.setAttribute('aria-disabled', st.show && !st.ok ? 'true' : 'false');
      const why =
        st.reason === 'oos'
          ? '（在庫がないため選べません）'
          : st.reason === 'weak'
            ? '（ベース色との差が小さく、模型では沈んで見えます）'
            : '';
      btn.title = label + why;
      const sr = btn.querySelector('.visually-hidden');
      if (sr) sr.textContent = label + why;
    }

    /*
      斜線つきで出ているルート色の理由を書く。

      ⚠️ **2026-09-01 に性格が変わった**（9-48）。
      もとは「沈むけれど作れるので止めない」という注意だったが、
      群ごとに規定値を持たせたことで**沈む色はそもそも選べなくなった**。
      いまは「なぜ押せない色が並んでいるのか」を説明する欄。

      **消さずに斜線で残しているのは、選べる色を選ぶための情報だから。**
      黙って消すと、ベース色を変えたときに色の数が理由もなく増減して見える。
    */
    renderContrastNotice() {
      const el = this.root.querySelector('[data-r3d-contrast]');
      if (!el) return;
      const s = this.state;
      const baseHex = this.baseHex();
      if (s.buildType !== 'route' || !baseHex) {
        el.hidden = true;
        return;
      }
      const self = this;

      /* ① 沈む色の警告。**いま選んでいる色についてだけ言う。**
         選んでもいない色を並べても、判断の役に立たないうえ画面が埋まる。 */
      const chosen =
        s.routeMode === 'single'
          ? s.routeColor
            ? [s.routeColor]
            : []
          : s.trackColors.slice(0, this.trackCount());
      const weak = [];
      chosen.forEach(function (nm) {
        if (!nm || weak.indexOf(nm) !== -1) return;
        if (self.routeStateOf(nm).reason === 'weak') weak.push(nm);
      });
      const baseBtn = this.baseSwatch();
      const baseName = baseBtn ? baseBtn.dataset.value : 'ベース色';
      el.hidden = !weak.length;
      if (weak.length) {
        el.textContent =
          weak.join('・') + 'は' + baseName + 'に沈んで見えます。そのままお作りできます。';
      }

      /* ② 斜線つきで並んでいる色の説明。
         ⚠️⚠️ **2026-09-07 に文ごとやめた。** ⭐ **斜線を見れば選べないことは分かる。**
         **色名を並べても増える情報は無く、「品切れが3つある」という印象だけが残る。**
         ⭐ **器（`data-r3d-oos`）は残してある** ―― 文を戻すならここへ書けばよい。
         ⚠️ **斜線そのものは消していない** ―― **選べる色を選ぶための情報**で、
         消すとベース色を変えたときに色数が理由もなく増減して見える。 */
      const note = this.root.querySelector('[data-r3d-oos]');
      if (note) note.hidden = true;
    }

    /** 線 i の色。線と色グループは1対1とは限らない（trkseg と trk） */
    lineColorAt(i) {
      const seg = this.segmentation();
      return this.trackColorAt(seg.groupOf[i] || 0);
    }

    /*
      ルート色の2つの見せ方を切り替え、トラック別のときは割り当て行を組み立てる。

      行数はGPXの区切りの数で決まるので、GPXを読み直すと変わる。
      すでに選んである色は残し、足りない分だけパレットを順に当てる
      （初期状態で全部同じ色だと、地図を見ても区切りが判別できない）。
    */
    renderRouteMode() {
      const root = this.root;
      const s = this.state;
      /*
        3つとも常に出す。まとまりが1つでも選べる（単色と同じ結果になるだけ）。
        分け方が選べるかどうかをGPXの中身で変えると、同じ画面が人によって
        違う形になり「さっきはあった選択肢が無い」と混乱する。
        日付で分けられないGPX（時刻の無い点がある）だけは、選んだあとに
        理由を出して先へ進ませない。
      */
      root.querySelectorAll('[data-r3d-routemode]').forEach(function (btn) {
        const on = btn.dataset.r3dRoutemode === s.routeMode;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-on', on);
      });

      const multi = s.routeMode !== 'single';
      const paneSingle = root.querySelector('[data-r3d-routepane="single"]');
      const paneMulti = root.querySelector('[data-r3d-routepane="multi"]');
      if (paneSingle) paneSingle.hidden = multi;
      if (paneMulti) paneMulti.hidden = !multi;

      /* 時刻の無いGPXでは日付で分けられない。理由を出して、行は組み立てない */
      const noDate = s.routeMode === 'date' && !this.canSplitByDate();
      const noDateEl = root.querySelector('[data-r3d-nodate]');
      if (noDateEl) noDateEl.hidden = !noDate;
      if (!multi || noDate || !this.palette.length) {
        if (noDate) {
          const box0 = root.querySelector('[data-r3d-trackcolors]');
          if (box0) box0.innerHTML = '';
        }
        return;
      }

      const n = this.trackCount();
      /* 既定は上限の範囲で巡回させる。パレット全体で回すと、区切りが多いGPXで
         初期状態からいきなり上限超過になり、何もしていないのに先へ進めない */
      /* ⚠️ 既定の割り当ては**いま選べる色から**取る（9-48）。
         パレット全体で回すと、ベース色によっては選べない色が初期値で入り、
         何もしていないのに「選び直してください」になる */
      const pal = this.defaultRouteColors();
      const cycle = Math.max(1, Math.min(pal.length, this.maxColors - 1));
      /*
        **端どうしを隣り合わせとして扱う**（2026-08-22）。
        周回ルートでは最後のまとまりと最初のまとまりが**登山口で出会う**ので、
        番号が離れていても地図の上では隣になる。実際 4本のとき `0,1,2,0` になり、
        1本目と4本目が同じ色で接していた。

        `i % cycle` は**連続する2つが必ず違う**ので、問題は「最後と最初」だけ。
        衝突するのは本数が cycle の倍数 +1 のときで、そのときだけ最後を、
        **直前とも先頭とも違う色**に差し替える。
        色数が2しかない場合は差し替え先が無いので、そのときは諦めてそのまま。
      */
      const want = [];
      for (let i = 0; i < n; i++) want.push(i % cycle);
      if (n > 2 && want[n - 1] === want[0]) {
        for (let k = 0; k < cycle; k++) {
          if (k !== want[n - 2] && k !== want[0]) {
            want[n - 1] = k;
            break;
          }
        }
      }
      for (let i = 0; i < n; i++) {
        if (!s.trackColors[i]) s.trackColors[i] = pal[want[i]].name;
      }
      s.trackColors.length = n;

      /* 色が足りずに繰り返すときの説明（2026-08-22）。
         **上限超過（赤・止める）とは別物。**こちらは止めない注意。
         トラック数が `cycle` を超えると、既定の割り当てで同じ色が必ず2回以上出る。
         **装置のスロット数から来る制約で避けられない**ので、理由を書いて
         個別に変えられることを伝える。 */
      const repeatEl = root.querySelector('[data-r3d-colorrepeat]');
      if (repeatEl) {
        const repeats = n > cycle && !this.overColorLimit();
        repeatEl.hidden = !repeats;
        if (repeats) {
          /* ⚠️ **「同じ色が繰り返し出ます」ではなく、選ぶことを頼む**（2026-09-01）。
             既定でこちらが割り当ててはいるが、**決めるのは相手**で、
             起きたことの報告より、何をすればよいかを言うほうが短くて済む。 */
          repeatEl.textContent =
            'まとまり' + n + 'つに対しルートは' + cycle +
            '色までです。どこかで同じ色をお選びください。';
        }
      }

      const limitEl = root.querySelector('[data-r3d-colorlimit]');
      if (limitEl) {
        const over = this.overColorLimit();
        limitEl.hidden = !over;
        if (over) {
          /* ⚠️ **短くした**（2026-09-01）。「地形が1色使うため」の理屈は
             繰り返しの注意（上）でも言っていて、二重だった。
             **止める表示なので、まず何色までかと、いま何色かだけ言う。** */
          limitEl.textContent =
            'ルートは' +
            (this.maxColors - 1) +
            '色までです（いま' +
            this.usedColorCount() +
            '色）。同じ色を使い回せば収まります。';
        }
      }

      const box = root.querySelector('[data-r3d-trackcolors]');
      if (!box) return;
      const labels = this.segmentLabels();
      const html = [];
      for (let i = 0; i < n; i++) {
        html.push('<div class="r3d-trackcolor">');
        html.push(
          '<span class="r3d-trackcolor__label"><span class="r3d-trackcolor__dot" style="background:' +
            this.trackColorAt(i) +
            '"></span>' +
            this.esc(labels[i] || 'トラック' + (i + 1)) +
            '</span>'
        );
        html.push('<div class="r3d-swatches">');
        /* トラック別の欄も単色の欄と同じ扱いにする（9-48）。
           **片方だけ絞り込むと、単色では選べない色がここでは選べてしまう。** */
        const self = this;
        this.palette.forEach(function (c) {
          const st = self.routeState(c);
          if (!st.show) return;
          const on = s.trackColors[i] === c.name;
          const why =
            st.reason === 'oos'
              ? '（在庫がないため選べません）'
              : st.reason === 'weak'
                ? '（ベース色との差が小さく、模型では沈んで見えます）'
                : '';
          const cls = 'r3d-swatch' + (st.reason === 'oos' ? ' r3d-swatch--oos' : '');
          html.push(
            '<button type="button" class="' +
              cls +
              '" data-r3d-trackswatch data-track="' +
              i +
              '" data-value="' +
              self.esc(c.name) +
              '" style="--sw: ' +
              c.hex +
              '" aria-pressed="' +
              (on ? 'true' : 'false') +
              '"' +
              (st.ok ? '' : ' disabled aria-disabled="true"') +
              ' title="' +
              self.esc(c.name + why) +
              '"><span class="visually-hidden">' +
              self.esc(c.name + why) +
              '</span></button>'
          );
        });
        html.push('</div></div>');
      }
      box.innerHTML = html.join('');
    }

    drawTrack(gpx) {
      this.ensureMap();
      if (!this.map) return;

      const L = window.L;
      if (this.trackLayer) this.map.removeLayer(this.trackLayer);
      if (this.markerLayer) this.map.removeLayer(this.markerLayer);
      this.markerLayer = null;

      // 色を割り当てる単位ごとに別ポリラインにする（モードで単位が変わる）
      const lines = this.colorSegments().map(
        function (seg) {
          return gpx.points.slice(seg[0], seg[1] + 1).map(function (p) {
            return [p.lat, p.lon];
          });
        }
      );

      /* 区切りごとに指定の色で描く。トラック別に色を割り当てるとき、
         地図で見分けられないと「トラック2」がどこか分からない */
      const self = this;
      this.trackLayer = L.layerGroup(
        lines.map(function (pts, i) {
          return L.polyline(pts, { color: self.lineColorAt(i), weight: 4, opacity: 0.95 });
        })
      ).addTo(this.map);

      this.root.querySelector('[data-r3d-map-empty]').hidden = true;
      this.drawCrop();
    }

    drawPoint(lat, lon) {
      this.ensureMap();
      if (!this.map) return;
      const L = window.L;
      if (this.trackLayer) {
        this.map.removeLayer(this.trackLayer);
        this.trackLayer = null;
      }
      if (this.markerLayer) this.map.removeLayer(this.markerLayer);
      this.markerLayer = L.circleMarker([lat, lon], {
        radius: 7,
        color: '#C4A35A',
        fillColor: '#C4A35A',
        fillOpacity: 1,
      }).addTo(this.map);
      this.root.querySelector('[data-r3d-map-empty]').hidden = true;
      this.drawCrop();
    }

    /*
      切り出し範囲に寄り直すかどうか。

      クリックのたびに fitBounds が走ると地図が足元で動いて次が狙えないので、
      クリック由来の更新では原則そのままにする（`keepView`）。ただし
      **原則のままだと初回が破綻する**。地図はズーム7の日本全体から始まるので、
      1.6km四方の枠は画面上 14px = 地図の 0.06% にしかならず、
      「クリックしたのに何も起きない」ように見える。実際そうなっていた。

      そこで、視点を保つのは「枠がちゃんと見えている」ときだけにする。
        - 小さすぎる（短辺の12%未満）→ 寄る
        - 画面からはみ出すほど大きい     → 寄る
        - 枠の中心が画面の外             → 寄る
      全体を含むかどうかではなく中心で見るのは、少しずらしただけの
      クリックで寄り直さないため。
    */
    needsFit(bounds) {
      if (!this.keepView) return true;
      const map = this.map;
      const nw = map.latLngToContainerPoint(bounds.getNorthWest());
      const se = map.latLngToContainerPoint(bounds.getSouthEast());
      const size = map.getSize();
      const w = Math.abs(se.x - nw.x);
      const h = Math.abs(se.y - nw.y);
      const shortSide = Math.min(size.x, size.y);
      if (Math.min(w, h) < shortSide * 0.12) return true;
      if (w > size.x || h > size.y) return true;
      return !map.getBounds().contains(bounds.getCenter());
    }

    /** 切り出し範囲の矩形と、仕上がり寸法の表示を更新する */
    drawCrop() {
      const s = this.state;
      const bbox = this.currentBbox();
      const dims = this.root.querySelector('[data-r3d-dims]');

      if (!bbox) {
        dims.hidden = true;
        this.modelDims = null;
        return;
      }

      const crop = window.R3DGpx.cropArea(bbox, s.margin);
      this.setProp('margin', s.margin + ' m');

      if (this.map) {
        const L = window.L;
        if (this.cropLayer) this.map.removeLayer(this.cropLayer);
        this.cropLayer = L.rectangle(
          [
            [crop.south, crop.west],
            [crop.north, crop.east],
          ],
          { color: '#1B2A38', weight: 2, dashArray: '6 4', fill: false }
        ).addTo(this.map);

        // 色を選び直しただけで地図が動くと落ち着かないので、
        // 範囲が実際に変わったときだけ fitBounds する。
        const sig = [crop.south, crop.west, crop.north, crop.east].join(',');
        if (sig !== this.cropSig) {
          this.cropSig = sig;
          this.updateMesh(crop);
          if (this.needsFit(this.cropLayer.getBounds())) {
            this.map.fitBounds(this.cropLayer.getBounds(), { padding: [24, 24] });
          }
        }
      }

      dims.hidden = false;
      this.root.querySelector('[data-r3d-dims-area]').textContent =
        km(crop.widthM) + ' × ' + km(crop.heightM);

      // 短辺の下限で範囲を広げたときだけ注記を出す
      const widened = this.root.querySelector('[data-r3d-widened]');
      if (widened) widened.hidden = !crop.widened;

      /* **仕上がり寸法はサイズを選んだあとだけ出す。** この欄はステップ2にあり、
         サイズはステップ3で選ぶので初回は必ず未選択になる。
         ここで選べるわけではないので「サイズ未選択」と書いても手がかりにならない */
      const longEdge = s.sizeEl ? parseInt(s.sizeEl.dataset.longEdge, 10) : null;
      const modelEl = this.root.querySelector('[data-r3d-dims-model]');
      const modelWrap = this.root.querySelector('[data-r3d-dims-modelwrap]');
      if (longEdge) {
        const md = window.R3DGpx.modelDimensions(crop, longEdge);
        this.modelDims = md;
        /* **高さも出す。** 棚に置く物なので厚みは知りたい情報。
           標高が読めていないあいだは幅と奥行だけ出す（**出ないだけで止めない**）。 */
        modelEl.textContent = this.dimsText(md);
        if (modelWrap) modelWrap.hidden = false;
      } else {
        modelEl.textContent = '—';
        this.modelDims = null;
        if (modelWrap) modelWrap.hidden = true;
      }
    }

    /*
      使う標高データ（5m / 10mメッシュ）を調べて表示と property に入れる。
      **表示は `.r3d-hint--live`**（ゴールドの縦線）。切り出し範囲から決まる、
      その人だけの内容なので、固定の説明文とは体裁を分けている。
      範囲が変わったときだけ走らせる。**通信が絡むので await では待たない** —
      待つと地図の描画が引きずられる。結果が返った時点で書き込む。
    */
    updateMesh(crop) {
      const el = this.root.querySelector('[data-r3d-mesh]');
      const sig = this.cropSig;
      this.setProp('mesh', '');
      /* 範囲を変えたら判定はやり直し。**前の範囲の結果を残さない** */
      this.state.noDem = false;
      if (el) {
        el.hidden = true;
        el.classList.remove('r3d-hint--stop');
      }
      if (!window.R3DGpx.probeMesh) return;

      window.R3DGpx.probeMesh(crop).then((kind) => {
        if (sig !== this.cropSig) return;    // 途中で範囲が変わっていたら捨てる
        if (!kind) return;

        /*
          標高データが1枚も無い＝作れない。**ここで止める。**
          矩形の国内判定（`JAPAN_REGIONS`）は対馬と釜山を切り分けられないので、
          国外のGPXが通り抜けてくる。実データの有無で確実に落ちる。
          **止めるのは404が確定したときだけ**（通信失敗は `probeMesh` が null を返す）。
        */
        if (kind === 'none') {
          this.state.noDem = true;
          if (el) {
            el.textContent = window.R3DGpx.NO_DEM_MESSAGE;
            /* **赤（`--stop`）は「作れない・進めない」ときだけ。**
               コントラストの注意（`--warn`）は作れるのでゴールドのまま */
            el.classList.add('r3d-hint--stop');
            el.hidden = false;
          }
          this.render();
          return;
        }
        this.state.noDem = false;
        if (el) el.classList.remove('r3d-hint--stop');

        const label = {
          '5m': '5mメッシュ',
          '10m': '10mメッシュ',
          mixed: '5mメッシュ（一部10mメッシュ）',
        }[kind];
        const note = {
          '5m': 'この山域は5mメッシュ（標高誤差0.3m級）で整備されています。',
          /* **10m と mixed は「打ち消し」で終わらせないこと**（2026-08-25）。
             どちらも「なだらかに出ます」で切れていて、**受け取る側には
             不利な知らせしか残らなかった。** 3Dプレビューの枠下で
             肯定1行を足したのと同じ理由（`r3d-designer.liquid` の
             `.r3d-view3d__affirm` のコメント）。
             **閉じの1文は2つで同じにしてある。** 同じ内容を別の言い方で
             書くと、どちらかを直したときにもう片方が取り残される。
             **FAQ「地形の精度はどのくらいですか？」の閉じとも同じ文言。**
             片方だけ変えないこと。 */
          '10m':
            'この山域は5mメッシュが未整備のため、全国を覆う10mメッシュを使います。' +
            '尾根や谷の細かな起伏が、5mメッシュの山域よりすこしなだらかに出ますが、' +
            '模型の仕上がりに支障はありません。',
          mixed:
            '範囲の一部は5mメッシュが未整備のため、10mメッシュを使います。' +
            'その部分の起伏はすこしなだらかに出ますが、' +
            '模型の仕上がりに支障はありません。',
        }[kind];
        this.setProp('mesh', label);
        if (el) {
          el.textContent = note;
          el.hidden = false;
        }
      });
    }

    currentBbox() {
      const s = this.state;
      if (s.buildType === 'route' && s.gpx) return s.gpx.bbox;
      if (s.latlon) {
        // 地形は指定座標を中心に、余白だけで範囲が決まる
        return {
          south: s.latlon.lat,
          north: s.latlon.lat,
          west: s.latlon.lon,
          east: s.latlon.lon,
        };
      }
      return null;
    }

    /* ---------------------------------------------------------------- *
     * バリアントと価格
     * ---------------------------------------------------------------- */

    /*
      3つの軸からバリアントを引く（2026-09-07。案B）。

        軸1 サイズ   S / M / L / XL
        軸2 素材     標準 / マーブル   ⭐ **ベース色から導く**（色は増えても軸は増えない）
        軸3 額装     なし / あり       ⚠️ XL × あり は商品に無い

      ⚠️ **軸が1本だけの商品にも当たるようにしてある。** 軸を足す前後で
      **どちらかの側でカート追加が壊れるのを避けるため。**
    */
    variantAt(o1, o2, o3) {
      const hit = this.variants.find(function (v) {
        return v.o1 === o1 && (v.o2 || '') === o2 && (v.o3 || '') === o3;
      });
      if (hit) return hit;
      /* 軸がまだ1本（サイズだけ）の商品。**素材=標準・額装=なし のときだけ当てる** */
      if (o2 === '標準' && o3 === 'なし') {
        return this.variants.find(function (v) {
          return v.o1 === o1 && !v.o2 && !v.o3;
        }) || null;
      }
      return null;
    }

    /** いま選んでいる素材。**ベース色が決める。** 未選択なら標準 */
    materialValue() {
      const btn = this.baseSwatch();
      return (btn && btn.dataset.material) || '標準';
    }

    /*
      ⭐⭐ **軸3「オプション」の値を、3つの欄から組み立てる**（2026-09-07）。

        なし / 額装 / フラッグ / 背面磁石 / 額装＋フラッグ / … の8通り

      ⚠️⚠️ **並びは画面の欄の順**（額装 → フラッグ → 背面磁石）。
      **`manufacture/push_variants.py` の PARTS の並びと一字一句そろえること。**
      **ズレるとバリアントが引けず、カート追加が止まる。**

      ⚠️⚠️ **なぜ別商品ではなく軸なのか。** **送料無料を「模型2点以上」で判定するため。**
      **カートの明細を必ず模型にしておかないと、自動割引の「最小数量2」が
      「模型1点＋フラッグ」で当たってしまう。**
    */
    addonOptionValue() {
      const self = this;
      const on = [];
      this.root.querySelectorAll('[data-r3d-opt][data-opt-label]').forEach(function (el) {
        const v = self.optValue(el);
        if (!v || v === 'なし') return;
        /* ⚠️⚠️ **選んだ値そのものを使う。** **`data-opt-label` を使ってはいけない** ――
           **1つの欄が2つの値を持つ場合（額装 / アクリルカバー）に、
           どちらを選んでも `額装` になり、カバーを選んだ人に額装が売れる**
           （2026-09-08に本番で踏んだ）。
           ⭐ **「あり」だけは欄の名前に読み替える**（フラッグ・磁石の2択）。 */
        on.push(v === 'あり' ? el.dataset.optLabel : v);
      });
      return on.length ? on.join('＋') : 'なし';
    }

    /*
      有償オプションの欄の値。**`<select>` でも固定の `<p>` でも読めるようにしてある。**
      ⭐ 準備中に戻すときは Liquid を `<p>` に書き換えるだけでよい。
    */
    optValue(el) {
      if (!el) return '';
      if (el.tagName === 'SELECT') return (el.value || '').trim();
      return (el.dataset.value || el.textContent || '').trim();
    }

    /* ---------------------------------------------------------------- *
     * 作例利用（2026-09-08）
     *
     * ⚠️⚠️ **軸1（サイズ）の値に「・作例可」を差し込んで表す。**
     * **Shopify のオプション軸は3本が上限で、3本とも埋まっているため。**
     * ⭐ **素材がベース色のスウォッチから決まる（`materialValue`）のと同じ形** ――
     * **軸とUIが1対1でない前例が既にある。**
     * ---------------------------------------------------------------- */

    /** `"S（長辺100mm）"` → `"S（長辺100mm・作例可）"`。**製造側 `sample_value()` と同じ規則** */
    sampleValue(o1) {
      if (!o1) return o1;
      return o1.endsWith('）') ? o1.slice(0, -1) + '・作例可）' : o1 + '・作例可';
    }

    /** いま引くべき軸1の値。**作例可なら印つきの値** */
    sizeOptionValue(o1) {
      return this.state.sample ? this.sampleValue(o1) : o1;
    }

    /*
      作例割引の額[円]。**正の数で返す**（1000 なら「1,000円引き」）。

      ⚠️⚠️ **直書きしない。** **バリアントの価格差から出す** ―― マーブル白で踏んだ穴と同じ形を
      作らないため（上の `priceDiff` のコメント）。
      ⭐ **サイズ未選択でも出す** ―― **選ぶ前に割引額が分かることが要件**。
    */
    sampleDiscount() {
      const base = this.feeSizeValue();
      if (!base) return 0;
      const mat = this.materialValue();
      const ad = this.addonOptionValue();
      const va = this.variantAt(base, mat, ad);
      const vb = this.variantAt(this.sampleValue(base), mat, ad);
      if (!va || !vb) return 0;
      return Math.round((va.price - vb.price) / 100);
    }

    findVariant(o1) {
      return this.variantAt(this.sizeOptionValue(o1), this.materialValue(), this.addonOptionValue());
    }

    /*
      素の値段のバリアント（標準・額装なし）。
      ⭐ **サイズの比較に使う。** ⚠️ **サイズのボタンに素材や額装の加算を乗せない** ――
      **あの欄はサイズを選ぶためのもので、比べたいのはサイズ間の差**。
      加算は色と額装それぞれの行が持つ。
    */
    baseVariantOf(o1) {
      return this.variantAt(o1, '標準', 'なし');
    }

    currentVariant() {
      if (!this.hasProduct || !this.state.sizeEl) return null;
      return this.findVariant(this.state.sizeEl.dataset.optionValue);
    }





    /** 仕上がり寸法の文言。高さが読めていれば「W × D × H mm」 */
    dimsText(md) {
      if (!md) return '—';
      const base = Math.round(md.widthMm) + ' × ' + Math.round(md.depthMm);
      const h = this.modelHeightMm(md.longEdgeMm);
      return h ? base + ' × ' + Math.round(h) + ' mm' : base + ' mm';
    }

    /*
      いまの範囲とサイズでの模型の高さ[mm]。標高差が読めていなければ null。

      **`renderSizeLimits` と同じ式を2か所に書かない**ためのヘルパ。
      呼び手は「読めなければ出さない」で扱うこと（**高さが出ないだけで注文は止めない**）。
    */
    modelHeightMm(longMm) {
      const crop = this.currentCrop();
      const stats = this.contourStats;
      if (!crop || !stats || !longMm) return null;
      const sig = [crop.south, crop.west, crop.north, crop.east].join(',');
      if (this.contourStatsSig !== sig) return null;
      const spanM = Math.max(crop.widthM, crop.heightM, 1);
      return Math.max(0, stats.maxM - stats.minM) * (longMm / spanM) + MODEL_BASE_MM;
    }

    /*
      標高差を取りに行く（2026-08-22）。

      **`renderContourRec` は `modelDims` を要求するので、サイズを選ぶまで走らない。**
      ところが**サイズを無効化するには、選ぶ前に標高差が要る。**順序が逆なので、
      ここで独立して取る。**キャッシュは共通**（`contourStats` / `contourStatsSig`）なので、
      等高線のおすすめと二重に取りには行かない。

      **await で待たない。** 待つと描画が引きずられる（`updateMesh` と同じ方針）。
      結果が返った時点で描き直す。**待っているあいだに範囲が変わったら捨てる。**
    */
    async ensureTerrainStats() {
      const crop = this.currentCrop();
      if (!crop || !window.R3DPreview3D || !window.R3DPreview3D.stats) return;
      const sig = [crop.south, crop.west, crop.north, crop.east].join(',');
      if (this.contourStatsSig === sig || this.statsPending === sig) return;

      this.statsPending = sig;
      try {
        const st = await window.R3DPreview3D.stats(crop);
        if (this.statsPending !== sig) return;   // 範囲が変わっていた
        this.contourStats = st;
        this.contourStatsSig = sig;
        this.statsPending = null;
        /* サイズが外れることがあるので、バッジもサマリーも含めて描き直す */
        this.render();
      } catch (e) {
        /* **読めなくても止めない。** サイズを絞らないだけ */
        if (this.statsPending === sig) this.statsPending = null;
      }
    }

    /*
      高さの上限を超えるサイズを選べなくする（2026-08-22）。

      **高さは真スケールなので、切り出し範囲が狭いほど高くなる。**
        高さ[mm] = 標高差[m] × ( 長辺[mm] ÷ 切り出し範囲の長辺[m] ) + 基部11mm

      **上限120mmの根拠は1つだけ。**
        1. 造形範囲256mmを超えない（**作れない注文を通さない**）

      ⚠️⚠️ **かつて根拠2として「Lサイズ用の箱（内寸135mm）に収まる」を挙げていたが、
      これは 2026-08-22 に取り消されている**（`data/shipping.md` 7章）。
      **3辺合計を内寸で計算していた誤りで、内寸135mmの中箱は80サイズになる。**
      ⚠️ **確定した中箱は 内寸225×225×115（MA60-067）で、Lの高さ上限は91mm。**
      **120mmを超えるLは大箱（80サイズ）へ回る。**
      ⭐ **次に上限を見直すときは、箱ではなく造形範囲だけが縛りだと思って始めること。**

      **止めるのではなく、選べる範囲を狭める**（案C）。購入者は行き止まりに当たらず、
      「XLが押せない → 余白を広げれば押せる」と辿れる。
      **理由は必ず出す。** 理由なしにボタンが死んでいると故障に見える。

      **標高が読めないときは何もしない。** 通信の失敗で注文を止めない
      （`probeMesh` や等高線のおすすめと同じ方針）。**そのぶん、造形範囲の
      最終的な歯止めは製造側にある。**

      **`refreshSizePrices()` のあとに呼ぶこと。** あちらは在庫で `disabled` を
      上書きするので、先に呼ぶとここでの無効化が消える。
    */
    renderSizeLimits() {
      const root = this.root;
      const el = root.querySelector('[data-r3d-sizelimit]');
      const crop = this.currentCrop();
      const stats = this.contourStats;
      const cropSig = crop ? [crop.south, crop.west, crop.north, crop.east].join(',') : null;

      const clear = () => {
        root.querySelectorAll('[data-r3d-size]').forEach(function (b) {
          b.classList.remove('is-too-tall');
        });
        if (el) el.hidden = true;
      };

      /* 範囲が決まっていない／標高が読めていない／範囲が変わって取り直し中 */
      if (!crop || !stats || this.contourStatsSig !== cropSig) return clear();

      const relief = Math.max(0, stats.maxM - stats.minM);
      const spanM = Math.max(crop.widthM, crop.heightM, 1);

      const tall = [];
      let firstOverMm = 0;
      let anyOk = false;
      root.querySelectorAll('[data-r3d-size]').forEach(function (btn) {
        const longMm = parseInt(btn.dataset.longEdge, 10);
        if (!longMm) return;
        const h = relief * (longMm / spanM) + MODEL_BASE_MM;
        const over = h > MAX_MODEL_H_MM;
        btn.classList.toggle('is-too-tall', over);
        if (over) {
          btn.disabled = true;
          if (!tall.length) firstOverMm = h;
          tall.push(btn.dataset.label);
        } else {
          anyOk = true;
        }
      });

      /* 選んでいたサイズが入らなくなったら外す。**残すと、押せないのに
         選択済みのまま進めてしまう** */
      if (this.state.sizeEl && this.state.sizeEl.classList.contains('is-too-tall')) {
        this.state.sizeEl = null;
      }

      if (!el) return;
      if (!tall.length) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      /* どのサイズも入らないときだけ赤。**そこは本当に進めない** */
      el.classList.toggle('r3d-hint--stop', !anyOk);
      el.classList.toggle('r3d-hint--warn', anyOk);
      el.textContent = anyOk
        ? '今回の範囲では ' + tall.join('・') + ' の高さが上限（' + MAX_MODEL_H_MM +
          /* **切り上げる。** 120.3mm を四捨五入すると「約120mm」になり、
             上限120mmを超えていないように読める */
          'mm）を超えます（' + tall[0] + 'で約' + Math.ceil(firstOverMm) +
          'mm）。ステップ2で切り出し余白を広げると選べます。'
        : '今回の範囲では、どのサイズも高さが上限（' + MAX_MODEL_H_MM +
          'mm）を超えます。ステップ2で切り出し余白を広げてください。';
    }

    /* ---------------------------------------------------------------- *
     * つくるものの種類で選べないサイズ（2026-09-08）
     *
     * ⭐⭐ **地形モードの XL を選べなくする。**
     *
     * ⚠️⚠️ **理由は「作れない」ではなく「機械時間の採算が合わない」。**
     * **地形モードは点＋余白なので切り出しが必ず正方形になり、**
     * **同じ長辺でもルートモードより面積が大きく、造形時間が伸びる。**
     * ⚠️ **XL は `¥/機械時` がもともと一番低い**（`data/pricing.md` 1-3）ので、
     * そこへ正方形の面積が乗ると採算が合わない。
     *
     * ⚠️⚠️⚠️ **`renderSizeLimits` と文言を分けること。**
     * **あちらは「余白を広げると選べます」だが、こちらは広げても選べない。**
     * **同じ言い方をすると、購入者が余白を動かし続けることになる。**
     *
     * ⚠️ **`refreshSizePrices()` と `renderSizeLimits()` のあとに呼ぶこと。**
     * **どちらも `disabled` を書くので、最後に上書きする必要がある。**
     * ---------------------------------------------------------------- */

    renderModeSizeLimits() {
      const root = this.root;
      const el = root.querySelector('[data-r3d-sizemode]');
      const blocked = [];

      root.querySelectorAll('[data-r3d-size]').forEach((btn) => {
        /* 地形モードのXLだけ。**ルートモードでは何もしない**（元の状態に戻す） */
        const off = this.state.buildType === 'terrain' && btn.dataset.longEdge === '250';
        btn.classList.toggle('is-mode-blocked', off);
        if (off) {
          btn.disabled = true;
          blocked.push(btn.dataset.label);
        }
      });

      /* 選んでいたサイズが対象外になったら外す。**残すと押せないのに進めてしまう**
         （`renderSizeLimits` と同じ後始末） */
      if (this.state.sizeEl && this.state.sizeEl.classList.contains('is-mode-blocked')) {
        this.state.sizeEl = null;
      }

      if (!el) return;
      if (!blocked.length) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.textContent = blocked.join('・') +
        ' は「地形」ではお選びいただけません。ルート（GPSログ）からお作りの場合はお選びいただけます。';
    }

    /*
      起伏が浅すぎるサイズに注意を出す（**F5**、2026-09-06）。

      **`renderSizeLimits` の裏返し**だが、**押せなくはしない。**
      高さの上限は「作れない注文を通さない」ための歯止めなので `disabled` が
      正しいが、**起伏が浅いのは作れないわけではない** ―― 平らでもいいから
      小さいのが欲しい、という選び方はあり得る。**注意だけ出して選ばせる。**

      ⚠️ **ボタン側に見た目の印は付けていない。** 使える手が opacity しかなく、
      それは既に「押せない」の意味で使っている（`is-too-tall` が 0.4）。
      **薄くすると押せないように見えるので、文章でサイズ名を挙げるだけにした。**
      印を足すなら、opacity ではない別の語彙を用意すること。

      **標高が読めないときは何もしない。** 通信の失敗で注文を止めない
      （`renderSizeLimits` と同じ方針）。
    */
    renderReliefLimits() {
      const root = this.root;
      const el = root.querySelector('[data-r3d-sizeflat]');
      const crop = this.currentCrop();
      const stats = this.contourStats;
      const cropSig = crop ? [crop.south, crop.west, crop.north, crop.east].join(',') : null;

      const clear = () => {
        root.querySelectorAll('[data-r3d-size]').forEach(function (b) {
          b.classList.remove('is-too-flat');
        });
        if (el) el.hidden = true;
      };

      if (!crop || !stats || this.contourStatsSig !== cropSig) return clear();

      const relief = Math.max(0, stats.maxM - stats.minM);
      const spanM = Math.max(crop.widthM, crop.heightM, 1);

      const flat = [];
      let firstFlatMm = 0;
      let firstFlatZ = 0;
      let lastFlatLabel = '';
      let lastFlatZ = 0;
      let firstOkLabel = '';
      let maxLongMm = 0;
      root.querySelectorAll('[data-r3d-size]').forEach(function (btn) {
        const longMm = parseInt(btn.dataset.longEdge, 10);
        if (!longMm) return;
        /* 高さの上限で既に押せないサイズは、こちらの対象外 */
        if (btn.classList.contains('is-too-tall')) return;
        maxLongMm = Math.max(maxLongMm, longMm);
        const z = relief * (longMm / spanM);
        const low = z < reliefFloorMm(longMm);
        btn.classList.toggle('is-too-flat', low);
        if (low) {
          if (!flat.length) { firstFlatMm = longMm; firstFlatZ = z; }
          lastFlatLabel = btn.dataset.label;
          lastFlatZ = z;
          flat.push(btn.dataset.label);
        } else if (!firstOkLabel) {
          firstOkLabel = btn.dataset.label;
        }
      });

      if (!el) return;
      if (!flat.length) { el.hidden = true; return; }

      /*
        余白を狭めれば届くか。

        ⚠️⚠️ **「狭めれば選べます」は、計算できたときだけ出す。** 余白の下限は
        200mなので、長くて平坦な記録では狭めても届かない。**届かないのに
        誘導すると、動かない操作をさせることになる。**
        高さ上限の側が無条件に「広げてください」と書けるのは、**広げれば必ず
        高さが下がる**から。こちらは逆向きで、その保証が無い。

        ⚠️ 起伏はいまの範囲で測ったもの。**狭めれば起伏は減りこそすれ増えない**
        ので、これは「狭めたときに届き得る上限」。**ここで届かないなら本当に
        届かない。** 届くと出ても外れることはあるが、その向きの誤りは
        「やってみたら変わらなかった」で済む。
      */
      const marginEl = root.querySelector('[data-r3d-margin]');
      const nowM = marginEl ? parseInt(marginEl.value, 10) : 0;
      const minM = marginEl ? parseInt(marginEl.min, 10) || 200 : 200;
      let couldHelp = false;
      if (marginEl && nowM > minM) {
        const bbox = this.currentBbox();
        const tight = bbox ? window.R3DGpx.cropArea(bbox, minM) : null;
        if (tight) {
          const tightSpan = Math.max(tight.widthM, tight.heightM, 1);
          /* 一部だけ外れているなら「一番小さい外れたサイズ」、
             全部外れているなら「一番大きいサイズ」が届くかを見る */
          const want = firstOkLabel ? firstFlatMm : maxLongMm;
          couldHelp = relief * (want / tightSpan) >= reliefFloorMm(want);
        }
      }

      /* **切り下げる。** 3.29mm を四捨五入すると「約3.3mm」になり、
         実際より立って見える。高さ上限の側が切り上げなのと同じ理屈で向きが逆 */
      const mm = (z) => (Math.floor(z * 10) / 10).toFixed(1);

      el.hidden = false;
      el.classList.toggle('r3d-hint--stop', !firstOkLabel);
      el.classList.toggle('r3d-hint--warn', !!firstOkLabel);

      if (firstOkLabel) {
        el.textContent = '今回の範囲では ' + flat.join('・') + ' の起伏が浅く、板に近い仕上がりになります（' +
          flat[0] + 'で約' + mm(firstFlatZ) + 'mm）。' + firstOkLabel + '以上なら地形として立ち上がります。' +
          (couldHelp ? 'ステップ2で切り出し余白を狭めても選べます。' : '');
      } else if (couldHelp) {
        el.textContent = '今回の範囲ではどのサイズも起伏が浅く、板に近い形になります（' +
          lastFlatLabel + 'で約' + mm(lastFlatZ) + 'mm）。ステップ2で切り出し余白を狭めると立ち上がります。';
      } else {
        el.textContent = '今回の記録は高低差が少なく、地形としては立ち上がりません。標高差のあるコースでしたらお作りできます。';
      }
    }

    /** いまの切り出し範囲。決まっていなければ null */
    currentCrop() {
      const bbox = this.currentBbox();
      if (!bbox) return null;
      return window.R3DGpx.cropArea(bbox, this.state.margin);
    }

    /** サイズカードの価格表示を、実バリアントの価格に差し替える */
    refreshSizePrices() {
      if (!this.hasProduct) return;
      const self = this;
      this.root.querySelectorAll('[data-r3d-size]').forEach(function (btn) {
        const o1 = btn.dataset.optionValue;
        /* ⭐⭐ **表示は必ず素の値段**（作例不可側）。**案「乙」でサイズ欄の数字は動かさない。**
           割引は合計と「ご注文の内容」にだけ出す */
        const shown = self.baseVariantOf(o1);
        /* ⚠️⚠️ **在庫は「いま選んでいる作例の状態」で見る。**
           **可5・不可5 で分かれているので、M（作例可）だけ売り切れがあり得る** */
        const stock = self.variantAt(self.sizeOptionValue(o1), '標準', 'なし') || shown;
        const out = btn.querySelector('[data-r3d-size-price]');
        if (shown) {
          out.textContent = shown.money;
          btn.disabled = !(stock && stock.available);
          btn.classList.toggle('is-unavailable', !(stock && stock.available));
        } else {
          out.textContent = btn.dataset.fallbackPrice || '—';
        }
      });
    }

    /* ---------------------------------------------------------------- *
     * 描画
     * ---------------------------------------------------------------- */

    setProp(key, value) {
      const el = this.root.querySelector('[data-r3d-prop="' + key + '"]');
      if (el) el.value = value;
    }

    /* ---------------------------------------------------------------- *
     * 日付（出発日〜帰着日）と文字数カウンタ
     * ---------------------------------------------------------------- */

    /**
     * 2つの date 入力から、刻印用の1行を組み立てて hidden へ入れる。
     * 書式は製造側（route.py の date_line）と同じ:
     *   同日または片方だけ  -> "YYYY/MM/DD"
     *   日をまたぐ          -> "YYYY/MM/DD - MM/DD"
     * 両方空なら空文字。空の property は pruneForSubmit が送信から外すので、
     * 「日付を刻まない」はチェックボックスではなく空欄で表す。
     *
     * ⚠️ **帰着日から年を落としている**（2026-08-29に変更）。理由は刻印の大きさ。
     * 背面の数値3行は同じ枠に揃えるので、いちばん長い日付行に3行とも引きずられる。
     * **範囲表記のままだと S（長辺100mm）で 2.40mm まで縮み**、px_mm 0.30 では
     * 横棒1本が1画素前後になって彫りが欠ける。**年を落とすと 2.80mm に戻る。**
     * ⚠️ **年をまたぐ山行では "2026/12/30 - 01/02" になる。**
     * 月まで落とす案（"2026/08/22-26"）は 3.40mm と更に大きいが、
     * **月をまたぐと "2026/08/30-02" となって読めないので採らなかった。**
     */
    syncDateProp() {
      const root = this.root;
      const a = root.querySelector('[data-r3d-date-start]');
      const b = root.querySelector('[data-r3d-date-end]');
      const hid = root.querySelector('[data-r3d-date]');
      if (!hid) return;

      /*
        ⚠️⚠️⚠️ **逆順で入っていたら入れ替える**（2026-09-09に追加）。
        **`min`/`max` はカレンダーを狭めるだけで、直接打ち込むのは止めない。**
        **さらに片方を空にすると、もう片方の上限が外れる**（`a.max = bv || ''`）ので、
        **「帰着日をクリア → 出発日を後ろへ動かす → 帰着日に過去日」で逆順が作れた。**

        ⚠️⚠️ **放置すると刻印が壊れる。** **年を落とすのは2番目の日付**なので、
        逆順のまま並べ替えると `2025/09/11 - 09/01` となり、
        **年が消えたうえに終わりが始まりより前に見える文字列**が銘板に出る。

        ⭐ **入れ替えは値が2つとも揃っているときだけ起きる。**
        **`input[type=date]` は日付が完成するまで空文字を返す**ので、
        打っている途中で入れ替わることはない。
      */
      if (a && b && a.value && b.value && a.value > b.value) {
        const swap = a.value;
        a.value = b.value;
        b.value = swap;
      }

      const av = a ? a.value : '';
      const bv = b ? b.value : '';
      /* 帰着日が出発日より前になる組み合わせはカレンダー側でも塞ぐ。
         上で入れ替えているので、ここに来る時点で av <= bv になっている */
      if (a && b) {
        b.min = av || '';
        a.max = bv || '';
      }

      const iso = av && bv ? (av <= bv ? [av, bv] : [bv, av]) : [av || bv, ''];
      const fmt = function (v) {
        return v ? v.replace(/-/g, '/') : '';
      };
      const s0 = fmt(iso[0]);
      const s1 = fmt(iso[1]);
      /* 帰着日は "YYYY/MM/DD" の先頭5文字（年とスラッシュ）を落として "MM/DD" にする */
      const tail = s1.slice(5);
      hid.value = !s0 ? '' : !s1 || s0 === s1 ? s0 : s0 + ' - ' + tail;
    }

    /** GPXの日付（JST変換済み）を出発日・帰着日へ入れる */
    fillDatesFromGpx(gpx) {
      const root = this.root;
      const a = root.querySelector('[data-r3d-date-start]');
      const b = root.querySelector('[data-r3d-date-end]');
      if (!a || !b) return;

      /* gpx.dateLabel は "YYYY/MM/DD" か "YYYY/MM/DD - YYYY/MM/DD"。
         date 入力は ISO しか受け付けないので変換する。 */
      const parts = String(gpx.dateLabel || '')
        .split(' - ')
        .map(function (x) {
          return x.trim().replace(/\//g, '-');
        });
      const start = parts[0] || '';
      const end = parts.length > 1 ? parts[1] : '';   // 日帰りは帰着日を空のままにする

      /* 空か、前回こちらが入れた値のままなら上書きする（手で直した値は守る）。
         「空のときだけ入れる」だと、別のGPXに差し替えても前の日付が残る。 */
      if (!a.value || a.value === this.autoFilled.dateStart) {
        a.value = start;
        this.autoFilled.dateStart = start;
      }
      if (!b.value || b.value === this.autoFilled.dateEnd) {
        b.value = end;
        this.autoFilled.dateEnd = end;
      }
      this.syncDateProp();
    }

    /* 文字数カウンタ。上限は input の maxlength をそのまま使う。
       ⭐ **`data-r3d-width-max` がある欄だけ、字数ではなく幅で数える**（`textWidth`）。 */
    renderCounts() {
      this.root.querySelectorAll('[data-r3d-counted]').forEach(function (el) {
        const field = el.closest('.r3d-field');
        const out = field && field.querySelector('[data-r3d-count]');
        if (!out) return;
        const wmax = widthLimit(el);
        const now = wmax ? textWidth(el.value) : el.value.length;
        const max = wmax || el.maxLength;
        /* 幅は 7.5 のような値になる。整数のときは小数点を出さない */
        out.textContent = Number.isInteger(now) ? String(now) : now.toFixed(1);
        const box = out.closest('.r3d-count');
        const over = now > max;
        if (box) {
          box.classList.toggle('is-full', now >= max);
          /* ⭐ **上限ちょうどと、超えているのを分ける**（9-54）。
             `15/12` は注文を止めるが、`12/12` は通る */
          box.classList.toggle('is-over', over);
        }
        /* ⚠️⚠️ **カウンタの赤だけでは、どの欄をどう直せばいいのか分からない**（9-54）。
           **欄に赤枠を付け、あと何字減らすかを直下に出す。**
           ⭐ 幅で数える欄（フリーテキスト）は入力時に削っているので、ここは出ない。 */
        el.classList.toggle('is-over', over);
        const warn = field.querySelector('[data-r3d-over]');
        if (warn) {
          warn.hidden = !over;
          if (over) {
            warn.textContent = 'あと' + (now - max) + '字減らしてください（' + max + '字まで）。';
          }
        }
      });
    }

    /* ---------------------------------------------------------------- *
     * 仕上がりの3Dプレビュー
     * ---------------------------------------------------------------- */

    /*
      等高線の推奨間隔。**一般論ではなく、いまの切り出し範囲とサイズでの結果を出す。**

      製造側（`product.py`）は等高線を「水平方向の線幅を一定（0.45mm）」で彫る。
      隣り合う線の中心間隔は 間隔[m] ÷ 斜度 × 縮尺[mm/m] で決まるので、
      **急な斜面ほど線が寄り、模型が小さいほど寄る。** 中心間隔が線幅の2倍を
      下回ると線と線のあいだに平らな面が残らず、彫った跡が面として見える。
      その面積の割合を標高タイルから見積もり、10%に収まる最も細かい間隔を勧める。

      **サイズに依存するのが要点。** 同じ山でもSとXLで答えが変わるので、
      サイズを変えたら出し直す。斜度の分布（`stats`）は範囲が同じなら使い回す。
    */
    async renderContourRec() {
      const el = this.root.querySelector('[data-r3d-contour-rec]');
      if (!el) return;

      const bbox = this.currentBbox();
      if (!bbox || !this.modelDims || !window.R3DPreview3D || !window.R3DPreview3D.stats) {
        el.hidden = true;
        this.contourRecSig = null;   // 戻ってきたときに出し直せるようにする
        return;
      }

      const crop = window.R3DGpx.cropArea(bbox, this.state.margin);
      const cropSig = [crop.south, crop.west, crop.north, crop.east].join(',');
      const longMm = Math.max(this.modelDims.widthMm, this.modelDims.depthMm);
      const sig = cropSig + '|' + Math.round(longMm);
      if (this.contourRecSig === sig) return;

      /* 標高タイルの取得が絡む。**待っているあいだに条件が変わることがある**ので、
         結果を書く前にもう一度突き合わせる（3Dの `view3dPending` と同じ考え方） */
      this.contourRecSig = sig;
      let stats = this.contourStats;
      try {
        if (!stats || this.contourStatsSig !== cropSig) {
          stats = await window.R3DPreview3D.stats(crop);
          this.contourStats = stats;
          this.contourStatsSig = cropSig;
        }
      } catch (e) {
        stats = null;
      }
      if (this.contourRecSig !== sig) return;   // 待っているあいだに変わった
      if (!stats) {
        /* 標高が読めなくても注文は止めない。おすすめを黙って出さないだけ */
        el.hidden = true;
        this.contourRecSig = null;
        return;
      }

      el.hidden = false;
      el.textContent = this.contourRecText(stats, crop, longMm);
    }

    /*
      推奨の文面。**3通りしかない。**
        1. 標高差が小さすぎて線がほとんど引けない → 「なし」
        2. 20/50/100 のどれかが10%以内に収まる → その間隔
        3. 100mでも収まらない（斜面が急すぎる） → 「なし」
      どれも「今回の場合」で始め、根拠（標高差・本数・潰れる割合）を添える。

      ⚠️⚠️ **モバイル（375px）で2行に収めること**（2026-09-10に詰めた）。
      **枠は 277px、12px なので1行20字。43字までが2行、44字で3行に落ちる。**
      ⚠️ **数えるときは「いちばん長くなる値」で数える** ――
      **サイズ名は `XL`（2字）、間隔は `100m`（4字）、本数は3桁**まで伸びる。
      **`S`＋`50m`＋2桁で40字なら、最悪でも43字で収まる。**
      ⚠️ **「〜がおすすめです」は入らない**（付けると45字で3行）。
      **`〜サイズなら50m間隔。` の形にして、推奨は「なら」で表す。**
      ⭐ **根拠の3つ（標高差・本数・潰れる割合）は落とさない。**
      **あれが無いと「なぜその間隔か」が消えて、ただの指示になる。**
      ⚠️ **`!pick` のときだけ3行になる**（「線を入れるなら100m〜」を足すため）。
      **あれは滅多に出ないうえ、代案の提示なので削らない。**
    */
    contourRecText(stats, crop, longMm) {
      const CONTOUR_W_MM = 0.45;      /* 製造側 contour_w_mm と同じ */
      const CRUSH_MM = CONTOUR_W_MM * 2; /* 中心間隔がこれ未満だと平らな面が残らない */
      const CRUSH_MAX = 0.1;          /* 潰れる面積がこれを超える間隔は勧めない */
      const MIN_LINES = 3;            /* これを下回るなら等高線として読めない */

      const relief = Math.max(0, stats.maxM - stats.minM);
      const mmPerM = longMm / Math.max(crop.widthM, crop.heightM);
      /* **文はルート色の注意（`renderContrastNotice`）と同じ調子で書く。**
         見出しと本文を「：」で切らず、そのまま続く文にする。
         `option_value` は「L（長辺200mm）」の形なので、括弧の入れ子を避けて
         短い `label` だけを使う。**長辺は書かない** ―― サイズ名で足りるうえ、
         括弧が続いて読みにくくなる（2026-09-06に外した） */
      const sizeEl = this.state.sizeEl;
      const head = '今回の範囲と' +
        (sizeEl ? sizeEl.dataset.label + 'サイズ' : '') + 'なら';
      const pct = function (r) {
        return r < 0.005 ? '1%未満' : '約' + Math.round(r * 100) + '%';
      };

      /* 起伏が小さいときは、間隔を細かくしても本数が足りない。
         **これはサイズに関係なく決まる**ので、サイズには触れずに書く */
      if (relief < 20 * MIN_LINES) {
        const few = Math.floor(relief / 20);
        return '今回の範囲は標高差' + Math.round(relief) + 'mで、20m間隔でも' +
          (few < 1 ? 'ほぼ入りません' : '約' + few + '本') +
          '。「なし」がおすすめ。';
      }

      const options = [20, 50, 100];
      let pick = null;
      let crushAtPick = 0;
      for (let i = 0; i < options.length; i++) {
        /* 中心間隔が CRUSH_MM を下回る斜度 = 間隔[m] × 縮尺[mm/m] ÷ CRUSH_MM */
        const limit = (options[i] * mmPerM) / CRUSH_MM;
        const crush = stats.steeperThan(limit);
        if (crush <= CRUSH_MAX) {
          pick = options[i];
          crushAtPick = crush;
          break;
        }
        if (i === options.length - 1) crushAtPick = crush;
      }

      if (!pick) {
        /* ⚠️ ここで「100m間隔がおすすめ」と書いてはいけない（2026-09-06に直した）。
           100mでも潰れるから !pick なのであって、その100mを勧めるのは矛盾する。
           上のコメントどおり、この場合のおすすめは「なし」。
           ⭐ ただし限界をわずかに超えただけなら、線を入れたい人のために
           「入れるなら100m」とだけ添える。大きく超えていたら添えない。 */
        const tail = crushAtPick >= 0.3
          ? '「なし」がおすすめ。'
          : '「なし」がおすすめ。入れるなら100mが最も潰れません。';
        return head + '100m間隔でも' + pct(crushAtPick) + 'が潰れます。' + tail;
      }

      const lines = Math.floor(relief / pick);
      const tail = crushAtPick < 0.005
        ? '潰れなし。'
        : '潰れ' + pct(crushAtPick) + '。';
      return head + pick + 'm間隔。標高差' +
        Math.round(relief).toLocaleString('ja-JP') + 'mに約' + lines + '本、' + tail;
    }

    /*
      ステップ3の3Dで使うものを、ステップ2のうちに取りに行く。
      **範囲が決まった時点は「先へ進む」意思が十分に強い。**
      GPXを読んだとき（ルート）と、地点が読めたとき（地形）の両方から呼ぶ。
      **地形にも要る。** 地点入力でも同じ3Dを組むので、ここを抜かすと
      地形のときだけステップ3で待たされる。
      取りこぼしても困らないので、待たないし失敗も見ない。
    */
    prefetch3d() {
      if (!window.R3DPreview3D || !window.R3DPreview3D.prefetch) return;
      try {
        const bbox = this.currentBbox();
        window.R3DPreview3D.prefetch(
          bbox ? window.R3DGpx.cropArea(bbox, this.state.margin) : null
        );
      } catch (e) {
        /* 先読みは無くても動く */
      }
    }

    /*
      ステップ5を開いたときだけ組む。Three.js は1.27MBあるので、
      ここに来ない人には読ませない（`r3d-preview3d.js` が動的 import する）。

      **形の作り直しは高い**（標高タイルの取得とメッシュ生成）。
      切り出し範囲・サイズが変わったときだけ作り直し、色だけの変更は
      材質の差し替えで済ませる。
    */
    async renderView3d() {
      const wrap = this.root.querySelector('[data-r3d-view3d]');
      if (!wrap || !window.R3DPreview3D) return;
      const stage = wrap.querySelector('[data-r3d-view3d-stage]');
      const status = wrap.querySelector('[data-r3d-view3d-status]');
      /* 操作の説明は枠の中の右下にある。**status と入れ替えで出し入れする。**
         読み込み中や「サイズを選ぶと立体が出ます」の裏で「ドラッグで回し」と
         書いてあっても、回すものがまだ無い */
      const lead = wrap.querySelector('[data-r3d-view3d-lead]');
      /* 枠の下の肯定1行も同じ扱い。**立体が出ているあいだだけ出す。**
         「サイズを選ぶと立体が出ます」の裏で「この形と色でお作りします」と
         書いても、指せる形がまだ無い。操作の説明（lead）と理由は同じ。
         **※の2行はここで出し入れしない。** あちらはプレビューという仕組みの
         性質の説明で、立体の有無に関係なく成り立つ。 */
      const affirm = this.root.querySelector('[data-r3d-view3d-affirm]');
      const say = function (t) {
        if (lead) lead.hidden = !!t;
        if (affirm) affirm.hidden = !!t;
        if (!status) return;
        status.textContent = t;
        status.hidden = !t;
      };

      /* 3Dが出せたかどうかで、控えの平面図を出し入れする。
         **失敗したら平面図に戻す。** ステップ3から色の確認手段が消えると困る */
      const fallback = this.root.querySelector('[data-r3d-trackmap-wrap]');
      const useFallback = (on) => {
        if (fallback) {
          const hasTrack = !!(this.state.gpx && this.state.gpx.points.length);
          fallback.hidden = !(on && hasTrack);
        }
        const stage3d = this.root.querySelector('[data-r3d-view3d]');
        if (stage3d) stage3d.hidden = !!on;
      };

      const bbox = this.currentBbox();
      if (!bbox || !this.modelDims) {
        say('サイズを選ぶと立体が出ます。');
        useFallback(false);
        return;
      }
      const crop = window.R3DGpx.cropArea(bbox, this.state.margin);
      const colors = this.view3dColors();
      const sig = [
        crop.south, crop.west, crop.north, crop.east,
        Math.round(this.modelDims.widthMm), Math.round(this.modelDims.depthMm),
        this.state.buildType, this.state.routeMode,
      ].join(',');

      // 形が同じなら色だけ差し替えて終わり
      if (this.view3dSig === sig && this.viewer) {
        this.viewer.setColors(colors);
        say('');
        useFallback(false);
        return;
      }

      /* 作り直しの最中に条件が変わることがある（サイズを続けて押す等）。
         走らせ直さないと、最後の選択が反映されないまま止まる */
      if (this.view3dBusy) {
        this.view3dPending = true;
        return;
      }
      this.view3dBusy = true;
      this.view3dPending = false;
      say('準備しています…');

      try {
        await window.R3DPreview3D.load();
        if (!this.viewer) {
          this.viewer = new window.R3DPreview3D.Viewer(stage);
          this.viewer.init();
        }
        const h = await window.R3DPreview3D.build(crop, this.modelDims, say);
        if (!h) {
          /* **黙って空の枠を出さない**（2026-08-20）。標高が1点も取れないと
             `build()` は null を返す。以前はここで `say('')` していたため、
             「描けた」ように見えて中身が空、という状態になっていた。
             理由を書いて、控えの平面図に戻す。 */
          say('この範囲の標高データが読み取れないため、立体を表示できません。');
          useFallback(true);
          this.view3dBusy = false;
          return;
        }
        this.viewer.show(h, crop, this.view3dTracks(), colors);
        this.view3dSig = sig;
        say('');
        useFallback(false);
      } catch (e) {
        /* 通信も描画も外部要因で落ちうる。**カート追加は止めない。**
           プレビューが出ないことと、注文できないことは別。
           平面図に戻して、色の確認だけはできる状態を保つ */
        say('');
        useFallback(true);
      }
      this.view3dBusy = false;
      if (this.view3dPending) {
        this.view3dPending = false;
        this.renderView3d();
      }
    }

    /** 地形の色と、ルートの色（トラック別のときはその並び） */
    view3dColors() {
      const s = this.state;
      const hexOf = (name, kind) => {
        if (!name) return null;
        const btn = Array.from(
          this.root.querySelectorAll('[data-r3d-swatch="' + kind + '"]')
        ).find(function (b) {
          return b.dataset.value === name;
        });
        return btn ? btn.dataset.hex : null;
      };
      const base = hexOf(s.baseColor, 'base') || '#1A1A1C';
      const route = [];
      if (s.buildType === 'route') {
        /* **地図と同じ `lineColorAt()` を使う。** 別に決めると、
           地図の線の色と立体のルートの色が食い違う */
        const n = Math.max(1, this.colorSegments().length);
        for (let i = 0; i < n; i++) route.push(this.lineColorAt(i));
      }
      return { base: base, route: route };
    }

    /*
      ルートの点列。色を割り当てる単位ごとに分けて渡す。
      **地図と同じ `colorSegments()` を使う。** 別々に分けると、
      地図で見た色分けと立体の色分けが食い違う。
    */
    view3dTracks() {
      const s = this.state;
      if (s.buildType !== 'route' || !s.gpx) return [];
      const pts = s.gpx.points;
      const segs = this.colorSegments();
      if (!segs || !segs.length) return [pts];
      return segs
        .map(function (seg) {
          return pts.slice(seg[0], seg[1] + 1);
        })
        .filter(function (g) {
          return g.length > 1;
        });
    }

    /* ---------------------------------------------------------------- *
     * 背面刻印のプレビュー
     * ---------------------------------------------------------------- */

    /** 刻印する行を run_product.py と同じ順・同じ書式で組み立てる */
    proofLines() {
      const root = this.root;
      const val = function (sel) {
        const el = root.querySelector(sel);
        return el ? el.value.trim() : '';
      };
      /* ⚠️ **`刻印：距離` / `刻印：標高` はセレクトになった**（2026-09-09）。
         **`入れない` も値として送られる**ので、値で判定すること。 */
      const engSel = function (name) {
        const el = root.querySelector('[name="properties[' + name + ']"]');
        return el ? String(el.value || '').trim() : '';
      };

      const name = val('input[name="properties[山名]"]');
      const route = val('input[name="properties[ルート名]"]');
      const free = val('input[name="properties[フリーテキスト]"]');
      const date = val('[data-r3d-date]');
      const gpx = this.state.gpx;

      /* Distance / Elevation Gain / Date は同じ性質の数値なので1グループに詰め、
         同一サイズに揃える。時刻を持たないGPXでは Date 行が落ちる。 */
      const data = [];
      const dataLine = function (text, g) {
        return { t: text, cap: PROOF.dataCapMm, gap: PROOF.dataGapMm, g: g || 'data' };
      };
      if (engSel('刻印：距離') === '含める' && gpx) {
        data.push(dataLine('Distance: ' + km(gpx.distanceM)));
      }
      /* ⚠️⚠️ **標高は「累積」と「最高」の排他**（2026-09-09）。**銘板の数値は2行まで。**
         ⭐⭐ 2026-09-01 に `Elevation Gain:` → `Ascent:` に詰めた（9-49）。
         **行群は最も長い行で大きさが決まる**ので、この23字がデータ3行すべてを
         縮めていた（S で 距離 4.07 → 5.67 / 累積 4.33 → 5.87）。
         ⭐ **`Peak:` はさらに短いので、この行が律速になることはない。**
         ⚠️ **製造側 model.py の `_back_lines` と対。片方だけ変えない。** */
      const elevKind = engSel('刻印：標高');
      if (elevKind === '累積標高' && gpx && gpx.elevationGainM !== null) {
        data.push(dataLine('Ascent: ' + meters(gpx.elevationGainM)));
      } else if (elevKind === '最高標高' && gpx &&
                 gpx.elevationMaxM !== null && gpx.elevationMaxM !== undefined) {
        data.push(dataLine('Peak: ' + meters(gpx.elevationMaxM)));
      }
      // 日付はチェックボックスを持たない。欄が空なら行が出ない
      if (date) {
        /* ⚠️ **日付だけ別グループ**（`model.py` の _back_lines と同値）。
           グループは「全員が収まる最大サイズ」に揃うので、**いちばん長い行が
           全部を決める。** 日付行は30字で Distance(17) / Elevation Gain(23) より長く、
           揃えていたせいで数値2行が引き下げられていた。 */
        /* ⭐ `(JST)` は外してある（2026-08-29。`model.py` の _back_lines と対）。
           30字 → 24字で `Elevation Gain:` とほぼ同じ長さになり、
           日付だけ小さい状態が解消した。国内専売なので但し書きは要らない。
           ⚠️ **JSTへの変換そのものは `r3d-gpx.js` のまま。外していない。** */
        /* ⭐⭐ 2026-09-01 に `Date:` のラベルを外した（9-49）。
           **数字の並びから日付と分かる。** 律速は版面の幅なので、字数がそのまま効く
           （S で 4.13 → 5.53mm）。⚠️ 製造側 model.py と対。 */
        data.push(dataLine(date, 'date'));
      }
      // 塊の下はスペーサーが続くので、最後の1行だけ既定の行間に戻す
      if (data.length) data[data.length - 1].gap = PROOF.gapMm;

      const routeCap = hasJp(route) ? PROOF.routeCapJpMm : PROOF.routeCapMm;
      const g = PROOF.gapMm;
      /* ⚠️⚠️ **1.0 固定**（2026-09-06。銘板を標準にした）。**`make_plate_art.py` が
         `src["width_mm"] = 100.0` として入口の倍率を 1.0 に揃えているのと対。**
         **どの製品サイズでも同じ版になる**ので、模型の長辺から比例させない。 */
      const capScale = 1.0;
      /* ⚠️⚠️ **フリーテキストが空なら、その行もスペーサーも出さない**
         （2026-09-06。9-79）。**製造側 model.py は `if free:` で丸ごと落としている。**
         常に出していたので、空のときだけプレビューに 7.5 + 2.0mm の余分な空きが入り、
         その分だけ他の行が縮んで**実物と版面がずれていた。** */
      const tail = [{ t: '', cap: PROOF.spacerMm, gap: g }];
      if (free) {
        tail.push({ t: free, cap: PROOF.freeCapMm, gap: g });
        tail.push({ t: '', cap: PROOF.spacerMm, gap: g });
      }
      /* ⚠️⚠️ **空の行は作らない**（2026-09-06。9-79）。`model.py` と対。
         山名が空だと 16mm の空行が頭に居座り、残り全部がそのぶん縮む。 */
      const head = [];
      if (name) head.push({ t: name, cap: PROOF.nameCapMm, gap: g });
      if (route) head.push({ t: route, cap: routeCap, gap: g });
      head.push({ t: '', cap: PROOF.spacerMm, gap: g });
      return head
        .concat(data)
        .concat(tail)
        .concat([
          // 出典と銘は組で見せたいので、この2行だけ間隔を詰める
          { t: PROOF.attr, cap: PROOF.attrCapMm, gap: PROOF.attrGapMm, foot: true },
          /* ⭐⭐ 銘と注文番号を2行に分けた（2026-09-01。9-49）。
             ⚠️⚠️ **前は「行は増やさない」と決めていたが、その前提が逆だった。**
             律速は幅で、縦には余りがある。**2行に割ってもデータ行は小さくならない**
             （銘 3.67 → 4.93 ／ 番号 3.67 → 6.27mm）。⚠️ 製造側 model.py と対。 */
          /* ⚠️ 2行は同じ group。**短い注文番号が銘の側へ下がって同じ大きさになる。**
             別々にすると番号だけ大きくなり、1つの署名として読めない。 */
          { t: PROOF.mark, cap: PROOF.markCapMm, gap: PROOF.markGapMm, g: 'mark', foot: true },
          { t: PROOF.markPlaceholder, cap: PROOF.markCapMm, gap: g, g: 'mark', foot: true },
        ])
        /* ⚠️⚠️ **大きさは長辺100mmを基準にした比率で決まる**（2026-09-06。9-79）。
           **製造側 model.py は `k = min(長辺mm / 100, 1.5)` を全部の cap と gap に掛けている。**
           プレビューは掛けていなかったので、**M以上（長辺150mm〜）では指定が製造側の
           2/3 になり、幅で頭打ちにならない短い行だけが小さく出て、大きさの順位が狂っていた。**
           ⭐ S（長辺100mm）では k=1 なので差が出ない。**M以降だけずれていた。** */
        .map(function (ln) {
          return { t: ln.t, cap: ln.cap * capScale, gap: ln.gap * capScale, g: ln.g, foot: ln.foot };
        });
    }

    /*
      台紙の寸法[mm]。

      ⚠️⚠️ **模型の底面から銘板の実寸へ**（2026-09-06。9-50 で銘板を標準にした）。
      **刻むのは 54×86 の既製ブランクなので、模型のサイズによらず版面は同じ。**
      ⭐ **`make_plate_art.py` の `PLATE_W_MM` / `PLATE_H_MM` と対。片方だけ変えない。**
      ⭐ **サイズを選ぶ前から実寸で出せる**ので、仮置きが要らなくなった。
    */
    proofPlateMm() {
      return { w: PROOF.plateWMm, h: PROOF.plateHMm, provisional: false };
    }

    renderProof() {
      const wrap = this.root.querySelector('[data-r3d-proof]');
      if (!wrap) return;
      const canvas = wrap.querySelector('[data-r3d-proof-canvas]');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const plate = this.proofPlateMm();
      let lines = this.proofLines();

      /* 台紙をCSSピクセルで何倍に描くか。以降 ctx は mm 単位で扱えるので、
         製造側と同じ数値をそのまま書ける。 */
      const cssW = (canvas.parentElement && canvas.parentElement.clientWidth) || 320;
      const scale = cssW / plate.w;
      const cssH = plate.h * scale;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.height = cssH + 'px';
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
      ctx.clearRect(0, 0, plate.w, plate.h);
      /* ⚠️⚠️ **字間と単語間を明示的に0にする**（2026-09-06。9-79）。
         **本文に letter-spacing 0.1em を入れてから、環境によっては canvas の
         文字がばらけて出る。** 既定は 0px と規定されているが、実装差で
         要素の計算済みスタイルを拾う場合がある。**版面は実物と1対1でなければ
         意味がないので、既定に頼らず毎回打ち消す。**
         ⚠️ 古いブラウザにはこの属性が無いので try で囲む（無ければ字間は0）。 */
      try { ctx.letterSpacing = '0px'; ctx.wordSpacing = '0px'; } catch (e) {}

      /* Google Fonts の和文は unicode-range で分割配信される。canvas は
         使う字のサブセットが届く前でも描けてしまい、そのときは別の書体
         （fallback）の字幅で測ることになる。**測る前に、実際に使う字を
         指定して読み込ませる。** 届いていなければ一度だけ描き直す。 */
      /* ⚠️ 400（Regular）。製造側 product.py の FONT_JP と対（2026-09-01。9-45）。 */
      const fontSpec = '400 20px "BIZ UDGothic"';
      const allText = lines
        .map(function (ln) {
          return ln.t;
        })
        .join('');
      if (allText && document.fonts && document.fonts.check) {
        if (!document.fonts.check(fontSpec, allText) && !this.proofFontPending) {
          this.proofFontPending = true;
          document.fonts
            .load(fontSpec, allText)
            .then(() => {
              this.proofFontPending = false;
              this.renderProof();
            })
            .catch(() => {
              this.proofFontPending = false;
            });
        }
      }

      /* ⚠️⚠️ **長辺は150mmで頭打ち**（2026-09-06。9-79）。製造側 `core/product.py` の
         `_shrink = back_long_cap_mm / 長辺` と対。**L・XL の背面は M と同じ字の大きさで、
         周りの余白だけが広がる。** これが無いと L・XL のプレビューだけ字が大きく出る。 */
      const longMm = Math.max(plate.w, plate.h);
      const shrink = longMm > PROOF.longCapMm ? PROOF.longCapMm / longMm : 1;
      const avail = plate.w * shrink - 2 * PROOF.marginMm;
      /* ⚠️⚠️ **足元3行は QR の左に置くので幅が狭い**（2026-09-06）。
         **`make_plate_art.py` の `foot_w = avail_w - (QR_MM + QR_GAP_MM)` と対。**
         ⚠️ **これが無いと、足元の字が QR に重なる。** */
      const footAvail = avail - (PROOF.qrMm + PROOF.qrGapMm);
      const xFoot = PROOF.marginMm + footAvail / 2;
      const setFont = function (mm) {
        /* ⚠️⚠️ **400 で描く。700 にしないこと**（2026-09-06。9-79）。
           BIZ UDGothic は 400 しか配信されておらず、700 は合成ボールドになる。
           幅は +0.5% だが **墨の高さが 4〜4.5% 膨らむ**。下の `fit` は
           `k.h <= capMm` で高さを見て 0.96 倍ずつ縮めるので、
           **行ごとに余分な縮小が1〜2段入り、大きさの順位まで入れ替わっていた。**
           ⭐ 400 なら製造側（PIL・BIZ-UDGothicR.ttc）と墨の寸法が一致する。
           実測 墨高/em: 地理院標高タイル加工 製造0.960 / 400=0.96 / 700=1.00
           ⚠️ 読み込みも `document.fonts.load('400 20px …')` で 400。揃えること。 */
        ctx.font = '400 ' + mm + 'px ' + PROOF.font;
      };
      /* 実際にインクが乗る範囲。PIL の textbbox と同じ「字形の外接矩形」で、
         フォントの行ボックスではない。ここを取り違えると配置がずれる。 */
      /* ⚠️⚠️⚠️ **測るときだけ拡大する**（2026-09-06。9-79）。
         ctx は mm 単位なので `ctx.font` に渡る数値は 5〜16 という極小の px になる。
         **その大きさだと `measureText` の墨の寸法が整数画素に丸められ、
         フォントサイズを変えても値が動かない。** 実測（開聞岳／日本百名山#76）:
           size 5.267 -> 高6.00 ／ size 5.000 -> 高6.00 ／ size 4.800 -> 高6.00
         `fit` は `墨高 <= cap` で判定するので **6.00 <= 5.33 が永久に偽**になり、
         必要のない縮小が4段も入っていた（cap 5.33 に対し描画 4.40 ＝ 17%小さい）。
         ⭐ cap が丸めた高さ以上の行（Distance の 7.00 など）は一発で通るので無傷。
         **合う行と合わない行が混在していたのはこれが理由。**
         ⭐ MEAS 倍で測って割り戻す。返す値の単位は mm のまま変わらない。 */
      const MEAS = 64;
      const inkAt = function (text, sizeMm) {
        ctx.font = '400 ' + sizeMm * MEAS + 'px ' + PROOF.font;
        const m = ctx.measureText(text);
        return {
          w: (m.actualBoundingBoxLeft + m.actualBoundingBoxRight) / MEAS,
          h: (m.actualBoundingBoxAscent + m.actualBoundingBoxDescent) / MEAS,
          left: m.actualBoundingBoxLeft / MEAS,
          right: m.actualBoundingBoxRight / MEAS,
          ascent: m.actualBoundingBoxAscent / MEAS,
        };
      };
      /* 高さと幅の両方に収まる最大サイズ。収まらないときだけ縮め、拡大はしない。
         **縮んだことは画面に出さない。** 台紙が小さければ縮むのが当たり前で、
         利用者に直しようがないことを警告しても意味がない。結果はプレビューが見せる。 */
      /* ⚠️⚠️ **製造側と同じ刻みに丸める**（2026-09-06。9-79）。
         `core/product.py` の `fit_size` は文字サイズを**整数画素**で持つ:
           size = max(8, int(cap_mm / px_eff)) ; size = int(size * 0.96)
         px_eff は 0.20mm を3倍レンダで割った 0.0667mm。**小数のまま扱うと、
         同じ幅で頭打ちになる行どうしが 0.1mm 単位でずれる。**
         実際、地理院・銘・注文番号の3行は製造側では 5.13mm に揃うのに、
         プレビューでは 5.30 / 5.20 / 5.10 とばらけていた。 */
      const PX_EFF = 0.20 / 3;
      const fit = function (text, capMm, wMm) {
        const lim = wMm === undefined ? avail : wMm;
        let n = Math.max(8, Math.floor(capMm / PX_EFF));
        for (let i = 0; i < 60; i++) {
          const size = n * PX_EFF;
          const k = inkAt(text, size);
          if (k.h <= capMm && k.w <= lim) return size;
          n = Math.floor(n * 0.96);
          if (n * PX_EFF < PROOF.minCapMm) break;
        }
        return Math.max(n * PX_EFF, PROOF.minCapMm);
      };

      /* 幅で縮んだぶんは行の高さも詰める（product.py と同じ）。
         行の高さは「その行に与える em」であって字の実寸ではないので、
         幅に合わせて字を小さくしても枠だけが元の高さで残り、上に空きができる。
         小さい台紙ほど幅で縮む量が大きく、版面が間延びして見えていた。 */
      const fitted = {};
      lines.forEach(function (ln, i) {
        if (!ln.t) return;
        const g = ln.g || '_' + i;
        const f = fit(ln.t, ln.cap, ln.foot ? footAvail : avail);
        fitted[g] = fitted[g] === undefined ? f : Math.min(fitted[g], f);
      });
      /* ⚠️⚠️ **日付は data 群より大きくしない**（2026-09-06。9-78）。
         日付を別群にしたのは 2026-08-29 で、当時は `Date (JST): …` の30字で最長だったため。
         ⚠️ **その後 `(JST)` と `Date:` を外して単日なら10字＝最短になり、群を分けたまま
         だったので日付だけ縮まず版面で最大になっていた。**
         ⭐ 小さくなるのは許す。**大きくなる側だけを止める。**
         ⚠️ **`manufacture/core/product.py` の同じ処理と対。片方だけ変えない。** */
      if (fitted.date !== undefined && fitted.data !== undefined) {
        fitted.date = Math.min(fitted.date, fitted.data);
      }
      lines = lines.map(function (ln, i) {
        if (!ln.t) return ln;
        const g = ln.g || '_' + i;
        /* ⚠️⚠️ **foot を落とさないこと**（2026-09-06）。落とすと足元3行が
           台紙の中央に寄り、**QR に重なる。** 行を組み直す箇所は3つある。 */
        return { t: ln.t, cap: Math.min(ln.cap, fitted[g]), gap: ln.gap, g: ln.g, foot: ln.foot };
      });

      /* 台紙に収まらないときの詰め方（product.py の engrave() と同じ）。
         **まず空き（行間とスペーサー行）を詰め、それでも足りないときだけ文字を縮める。**
         全部を等比で縮めると、空きに余地があるのに文字まで小さくしてしまう。
         正方形のSサイズは空きを詰めるだけで収まり、文字は原寸のまま置ける。
         空きは PROOF.spaceMinRatio までしか詰めない（詰めきると行が団子になる）。

         **縮めるのは字の大きさを決める前。** 順番を逆にすると、
         元の大きさで組んだ字を縮んだ行間に流し込むことになり、行が重なる。 */
      const sumHeight = function (ls) {
        let t = 0;
        for (let i = 0; i < ls.length; i++) {
          t += ls[i].cap + (i < ls.length - 1 ? ls[i].gap : 0);
        }
        return t;
      };
      let total = sumHeight(lines);
      const availH = plate.h * shrink - 2 * PROOF.marginMm;
      let textMm = 0;
      lines.forEach(function (ln) {
        if (ln.t) textMm += ln.cap;
      });
      const spaceMm = total - textMm;
      if (spaceMm > 0 && total > availH && availH > 0) {
        let sscale = Math.min(1, Math.max(PROOF.spaceMinRatio, (availH - textMm) / spaceMm));
        let tscale = 1;
        if (textMm + spaceMm * sscale > availH) {
          const k = availH / (textMm + spaceMm * sscale);
          tscale = k;
          sscale *= k;
        }
        lines = lines.map(function (ln) {
          return {
            t: ln.t,
            cap: ln.cap * (ln.t ? tscale : sscale),
            gap: ln.gap * sscale,
            g: ln.g,
            foot: ln.foot,        /* ⚠️ 落とすと QR に重なる */
          };
        });
        total = sumHeight(lines);
      }

      /* 逆に台紙が余っているときは全体を拡大する（product.py と同じ）。
         行の高さは絶対値[mm]なので、**大きいモデルほど版面が小さく見えていた**
         （長辺250mmで上下の余白が80mmずつ）。幅と高さの両方に収まる範囲で引き伸ばす。
         PROOF.maxGrow で頭打ちにしているのは、余白が消えるまで太らせないため。 */
      if (total > 0 && total <= availH) {
        let grow = Math.min(PROOF.maxGrow, availH / total);
        lines.forEach(function (ln) {
          if (!ln.t) return;
          const w = inkAt(ln.t, ln.cap).w;
          if (w > 0) grow = Math.min(grow, avail / w);
        });
        if (grow > 1) {
          lines = lines.map(function (ln) {
            return { t: ln.t, cap: ln.cap * grow, gap: ln.gap * grow, g: ln.g, foot: ln.foot };
          });
          total = sumHeight(lines);
        }
      }

      // グループが同じ行は最小サイズに揃える
      const gsize = {};
      lines.forEach(function (ln, i) {
        if (!ln.t) return;
        const g = ln.g || '_' + i;
        const size = fit(ln.t, ln.cap, ln.foot ? footAvail : avail);
        gsize[g] = gsize[g] === undefined ? size : Math.min(gsize[g], size);
      });

      /* ⚠️⚠️ **黒地に白へ**（2026-09-06）。**銘板を標準にしたので実物がそう見える。**
         ⚠️ **台紙の色は `r3d-designer.css` の `.r3d-proof__plate` と対。片方だけ変えない。**
         ⚠️ **純白にしない** ―― **レーザーで皮膜の色を抜いた面は、白ではなく明るい灰色。** */
      /* ⚠️ **外枠も刻まれる。** `make_plate_art.py` が版に入れているので、
         プレビューにも出す。**位置合わせの基準でもある。** */
      ctx.strokeStyle = PROOF_INK;
      ctx.lineWidth = PROOF.borderWMm;
      const bi = PROOF.borderInsetMm;
      ctx.strokeRect(bi, bi, plate.w - bi * 2, plate.h - bi * 2);

      ctx.fillStyle = PROOF_INK;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';

      let y = (plate.h - total) / 2;
      const footC = [];                      // 足元の各行の中心[mm]
      lines.forEach(function (ln, i) {
        if (ln.t) {
          const drawSize = gsize[ln.g || '_' + i];
          const k = inkAt(ln.t, drawSize);   // ⚠️ 測ってから描画用の書体に戻す
          setFont(drawSize);
          /* インクの中心を台紙の中心へ、インクの上端を行の上端へ合わせる。
             ⚠️ **足元3行だけは QR の左** ―― `make_plate_art.py` の `x_foot` と対 */
          const cx = ln.foot ? xFoot : plate.w / 2;
          ctx.fillText(ln.t, cx - (k.right - k.left) / 2, y + k.ascent);
          if (ln.foot) footC.push(y + k.h / 2);
        }
        y += ln.cap + ln.gap;
      });

      /* ⚠️⚠️ **QR は足元3行の右。中行の中心に上下を合わせる**（2026-09-06）。
         **帯全体の中心ではない** ―― `make_plate_art.py` のコメントのとおり、
         **行間が上下で違うので、見て揃うのは中行**。 */
      const qn = PROOF_QR.length;
      const qpx = PROOF.qrMm / qn;
      const qc = footC.length ? footC[Math.floor(footC.length / 2)]
                              : plate.h - PROOF.marginMm - PROOF.qrMm / 2;
      const qTop = Math.min(Math.max(qc - PROOF.qrMm / 2, PROOF.marginMm),
                            plate.h - PROOF.marginMm - PROOF.qrMm);
      const qLeft = plate.w - PROOF.marginMm - PROOF.qrMm;
      for (let r = 0; r < qn; r++) {
        for (let c = 0; c < qn; c++) {
          if (PROOF_QR[r][c] === '1') {
            /* ⚠️ わずかに重ねて描く。**隙間が出ると目が粗く見える** */
            ctx.fillRect(qLeft + c * qpx, qTop + r * qpx, qpx + 0.02, qpx + 0.02);
          }
        }
      }

      const note = wrap.querySelector('[data-r3d-proof-note]');
      if (note) {
        /* ⚠️⚠️ **「彫り込みの陰影は含みません」を外した**（2026-09-01）。
           **刻印は彫るのをやめて色で出している**（9-31）ので、彫りの話がそもそも無い。 */
        const parts = ['図は実寸比率。'];
        /* 最終行の #0000 は伏せ字。**番号はまだ存在しない**（発番はご注文時）。
           等幅なので伏せ字と実物で幅が変わらず、版面はこのままになる。
           **ここが刻印の唯一の確認機会**なので、伏せ字であることは必ず伝える。 */
        /* ⚠️⚠️ **1行に収める。効くのはモバイル**（2026-09-10に測り直した）。
           **375pxでは枠が277px・12px・字間1.5pxで、21字が上限**（22字で2行）。
           **デスクトップの320px・23字より、こちらが厳しい。モバイルで測ること。**
           ⚠️ **「配置と大きさは実寸比率。」（24字）は2行だった。**
           **「図は」に替えて19字にした** ―― 図そのものを指せば、配置も
           大きさもまとめて言える。
           ⚠️ `#0000` の後ろの空白は消さないこと。無いと折り返せない塊になり、
           かえって2行になる（実測）。 */
        parts.push('#0000 は注文番号。');
        /* ⚠️ **寸法はここに書かない**（2026-08-29に削除）。
           右の「ご指定内容」に**仕上がり寸法（67 × 100 × 25 mm）**が出ており、
           同じ数字を2か所に置いていた。しかもこちらは高さの無い2辺だけで、
           **同じものの違う書き方が並んでいる状態**だった。数字は1か所に置く。
           ⚠️ **「GPSログとサイズを選ぶと実際の比率になります」を外した**
           （2026-09-06）。**銘板は 54×86 の既製ブランクで、模型のサイズに
           よらず版面が同じ**なので、比率が仮になる場面が無くなった。 */
        note.textContent = parts.join('');
      }

    }

    render() {
      const root = this.root;
      const s = this.state;

      // ステップの表示
      root.querySelectorAll('[data-r3d-panel]').forEach(function (p) {
        p.hidden = parseInt(p.dataset.r3dPanel, 10) !== s.step;
      });
      root.querySelectorAll('[data-r3d-stepnav] .r3d-stepnav__item').forEach(function (li) {
        const n = parseInt(li.dataset.step, 10);
        li.classList.toggle('is-current', n === s.step);
        li.classList.toggle('is-done', n < s.step);
        li.classList.toggle('is-clickable', n <= s.step);
      });

      /*
        パネルの外にあって、特定のステップでだけ出す要素。
        **サマリー内の「戻る：立体のプレビュー」がこれ。**
        ⚠️ **サマリーはどのステップでも出ている**ので、パネルの出し入れには乗らない。
        ⚠️⚠️ **`hidden` を効かせるには CSS 側にも `[hidden]` の指定が要る。**
        **`.r3d-btn` は `display: inline-flex` を持っており、
        ブラウザ既定の `[hidden] { display: none }` に詳細度で勝ってしまう。**
      */
      root.querySelectorAll('[data-r3d-onstep]').forEach(function (el) {
        el.hidden = parseInt(el.dataset.r3dOnstep, 10) !== s.step;
      });

      // ビルドタイプ
      root.querySelectorAll('[data-r3d-buildtype]').forEach(function (btn) {
        const on = btn.dataset.r3dBuildtype === s.buildType;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      const buildLabels = { route: 'ルート', terrain: '地形' };
      this.setProp('buildType', buildLabels[s.buildType] || '');

      // ビルドタイプ別の出し入れ
      root.querySelectorAll('[data-r3d-when]').forEach(function (el) {
        const types = el.dataset.r3dWhen.split(/\s+/);
        el.hidden = !s.buildType || types.indexOf(s.buildType) === -1;
      });

      // サイズ
      root.querySelectorAll('[data-r3d-size]').forEach(function (btn) {
        const on = btn === s.sizeEl;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.setProp('size', s.sizeEl ? s.sizeEl.dataset.optionValue : '');

      // 色
      /* ⚠️ **ベース色を先に確定させてから**ルート色を見る（9-48）。
         ルート色の可否はベース色から決まるので、順番が逆だと1回ぶん古い判定になる。 */
      const self = this;
      root.querySelectorAll('[data-r3d-swatch="base"]').forEach(function (btn) {
        const on = btn.dataset.value === s.baseColor;
        const ok = btn.dataset.instock !== 'false';
        btn.classList.toggle('is-selected', on && ok);
        btn.setAttribute('aria-pressed', on && ok ? 'true' : 'false');
        self.paintSwatch(
          btn,
          { show: true, ok: ok, reason: ok ? '' : 'oos' },
          btn.dataset.value || ''
        );
      });
      // 在庫の無いベース色が選ばれたまま残らないようにする
      if (s.baseColor) {
        const bb = root.querySelector(
          '[data-r3d-swatch="base"][data-value="' + (window.CSS && CSS.escape ? CSS.escape(s.baseColor) : s.baseColor) + '"]'
        );
        if (bb && bb.dataset.instock === 'false') s.baseColor = '';
      }
      this.pruneColorChoices();
      root.querySelectorAll('[data-r3d-swatch="route"]').forEach(function (btn) {
        const st = self.routeStateOf(btn.dataset.value);
        const on = st.ok && btn.dataset.value === s.routeColor;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        self.paintSwatch(btn, st, btn.dataset.value || '');
      });
      const baseNameEl = root.querySelector('[data-r3d-swatch-name="base"]');
      const selBase = this.baseSwatch();
      const fee = this.surchargeOf(selBase);
      /* 選んだ色に加算があるときは、色名のとなりに金額を出す。
         **注記だけだと、選んだ自分に関係あるのかが読み取れない。** */
      /*
        ⚠️⚠️ **ここは「見せる名前」で、注文に載る名前とは別**（2026-09-09）。
        **`s.baseColor` はそのまま `properties[ベース色]` になり、納品書・受注メール・
        台帳にも出る。** **そちらは商品オプションの値と一字一句合っている必要がある。**
        ⭐ **だから札の下のキャプションだけ、読み手向けに言い換える。**
        ⚠️ **増やすときは「注文の値 → 見せ方」の対応をここに足すこと。**
        **`s.baseColor` そのものを書き換えないこと。**
      */
      const SWATCH_CAPTION = { 'マーブル白': 'マーブル白・大理石調' };
      baseNameEl.textContent = SWATCH_CAPTION[s.baseColor] || s.baseColor || '—';
      if (fee > 0) {
        const chip = document.createElement('span');
        chip.className = 'r3d-fee';
        chip.textContent = '（' + this.feeText(fee) + '）';
        baseNameEl.appendChild(chip);
      }
      root.querySelector('[data-r3d-swatch-name="route"]').textContent = s.routeColor || '—';

      this.renderRouteMode();
      this.renderContrastNotice();
      this.renderTrackMap();
      this.refreshTrackColors();
      this.setProp('baseColor', s.baseColor);
      if (s.buildType !== 'route') {
        this.setProp('routeColor', '');
        this.setProp('trackSplit', '');
      } else if (s.routeMode !== 'single') {
        /* 「トラック1: レッド / …」「2010/09/19: レッド / …」の形で1行にまとめる。
           property をまとまりの数だけぶら下げると、注文画面がその分だけ伸びる */
        const labels = this.outputLabels();
        const parts = [];
        for (let i = 0; i < this.trackCount(); i++) {
          parts.push(labels[i] + ': ' + (s.trackColors[i] || '—'));
        }
        this.setProp('routeColor', parts.join(' / '));
        this.setProp(
          'trackSplit',
          s.routeMode === 'date' ? '日付ごとに色を分ける' : 'トラックごとに色を分ける'
        );
      } else {
        this.setProp('routeColor', s.routeColor);
        this.setProp('trackSplit', '');
      }

      // 刻印
      // 刻印欄は常に表示する（標準仕様のため出し分けない）

      this.renderSample();
      this.refreshSizePrices();
      /* **必ず refreshSizePrices のあと。** あちらは在庫で disabled を上書きする */
      this.renderSizeLimits();
      /* **必ず renderSizeLimits のあと。** 高さで外れたサイズを対象から抜くため */
      this.renderReliefLimits();
      /* **必ず最後。** 上の3つがどれも disabled を書くので、ここで上書きする */
      this.renderModeSizeLimits();
      this.drawCrop();
      this.renderSummary();

      // 地図で地点を選べる状態か（カーソルで示す）
      const mapEl = root.querySelector('[data-r3d-map]');

      // 「次へ」の可否
      root.querySelectorAll('[data-r3d-next]').forEach((btn) => {
        btn.disabled = !this.stepComplete(parseInt(btn.dataset.r3dNext, 10) - 1);
      });

      // カート追加の可否
      /* ⭐ カート追加はサマリーの1つだけ（2026-09-07にステップ4のぶんを外した）。
         ⚠️ **`querySelectorAll` のままにしておく** ―― 増えたときに片方だけ
         更新される事故を防ぐ。1つでも正しく動く */
      /*
        ⚠️⚠️ **最後の確認まで進んでいないと押せない**（2026-09-09）。
        **`validate()` は入力が揃っているかだけを見ていて、進行段階を見ていない。**
        **刻印は任意なので、ステップ3の時点で条件が揃い、
        「次へ：銘板の刻印」の隣でカート追加が押せてしまっていた。**
        ⭐ **サマリーは全ステップで出ている**ので、ここで段階を足す必要がある。

        ⭐⭐ **ステップ1〜3では「隠す」**（2026-09-10。前は disabled で置いていた）。
        ⚠️⚠️ **灰色のボタンは「あなたの入力に不備がある」と読ませる。**
        **合計金額は出ていて、必須バッジも全部「選択済み」なのにボタンだけ灰色**
        ―― という画面になり、**入力漏れを探させてしまう。**
        ⚠️⚠️ **押せない理由は `title` にしか無く、そこには届かない** ――
        **モバイルにホバーは無く、`disabled` な要素はマウスイベントを飲む。**
        ⚠️ **以前は「合計を見せる欄なので、ボタンごと消えると『どこで買えるのか』が
        分からなくなる」として隠さなかったが、実測して覆した** ――
        **サマリーは `position: static` で貼り付いておらず、デスクトップでは
        右カラム上端 y=290 に対しカート追加は y=894 で同時に見えない。**
        **モバイルではサマリーがパネルの下（340 / 685）。**
        **各ステップに「次へ：〜」が常にあるので、途中で買い場所を探す人はいない。**
        ⭐ **合計・内訳・注記は隠さない。** 消すのはボタンだけ。

        ⚠️ **ステップ4での `disabled` は残す。** あちらは理由が画面に出ている ――
        **未入力の欄は赤い「必須」バッジと「次はここ」の輪が指しており、
        受付終了は上部の告知バナー（`r3d-designer__warn--closed`）が出す。**
        ⚠️ **`hidden` が効くのは先頭の `[data-r3d-designer] [hidden]` があるから**
        （このボタンは `display: inline-flex`。素の `[hidden]` では消えない）。
      */
      const problem = this.validate();
      const tooEarly = s.step < 4;
      root.querySelectorAll('[data-r3d-submit]').forEach(function (b) {
        b.hidden = tooEarly;
        b.disabled = tooEarly || !!problem;
        b.title = tooEarly ? '' : problem || '';
      });

      this.renderRequired();
      this.renderNext();
      this.renderCounts();
      this.renderProof();

      /* ステップ3にいるあいだは、サイズ・色の変更をその場で立体に映す。
         形が同じなら材質の差し替えだけで済む（`view3dSig` で判定） */
      if (s.step === 3) {
        /* **サイズを選ぶ前に標高差が要る**ので、ここで取りに行く（待たない） */
        this.ensureTerrainStats();
        this.renderView3d();
        /* 等高線の推奨もサイズで変わる。**同じ条件なら中で弾く**ので毎回呼んでよい */
        this.renderContourRec();
      }
    }

    /*
      必須バッジの出し分け（2026-08-22）。**満たされたら「選択済み」に変える。**
      未選択のあいだだけ赤が残るので、**赤が残っている欄＝まだ選んでいない欄**として読める。

      **判定は `stepComplete(3)` と同じ条件にすること。** ずれると、バッジは全部
      「選択済み」なのに「次へ」が押せない、という状態が作れてしまう。
      ルート色は地形のとき対象外なので、そのときは満たされている扱い。
    */
    /** 「1,000」の形。**桁区切りだけ。** `feeText` は「+」が付くので割引には使えない */
    yen(n) {
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /*
      作例利用の欄（2026-09-08）。**押下状態と、割引額の文言を出す。**
      ⚠️⚠️ **金額は `sampleDiscount()`（バリアントの価格差）から。直書きしない。**
    */
    renderSample() {
      /* ⚠️⚠️ **地形モードでは「不可」に固定する**（2026-09-09）。
         **欄は隠れているが、`state.sample` が null のままだとサイズ軸の値が
         決まらず、バリアントを引けない**（`sizeOptionValue()`）。
         ⭐ **`render()` から必ず通るので、深いリンクや種類の切り替え直後でも効く。**
         ⚠️ **false（＝作例不可）にすること。** true にすると、
         **聞いていない人の模型を公開してよいことになる。** */
      if (this.state.buildType === 'terrain' && this.state.sample !== false) {
        this.state.sample = false;
      }
      const cut = this.sampleDiscount();
      /* ⭐ 括弧ごと入れる。**割引が引けないときは括弧も出さない** */
      const txt = cut > 0 ? '（割引 −' + this.yen(cut) + '円）' : '';
      this.root.querySelectorAll('[data-r3d-sample]').forEach((btn) => {
        const on = this.state.sample === (btn.dataset.r3dSample === 'yes');
        /* ⚠️ クラス名は `is-selected`。**`is-on` ではない**（CSS は `.r3d-choice.is-selected`）。
           **つくるものの選択・ベース形状と同じ語彙を使う** */
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.root.querySelectorAll('[data-r3d-sample-fee]').forEach((el) => {
        el.textContent = txt;
      });
      /* サイズ欄の下の注記。**同じ数字を2か所で直書きしないため、ここでも計算値を入れる** */
      this.root.querySelectorAll('[data-r3d-sample-hint]').forEach((el) => {
        el.textContent = cut > 0 ? this.yen(cut) + '円' : '—';
      });
      this.setProp('sample', this.state.sample === null ? '' : (this.state.sample ? '可' : '不可'));
    }

    /*
      刻印欄（山名・ルート名）が埋まっているか。

      ⚠️⚠️ **必須バッジ（`renderRequired`）・次の一手の輪（`nextTarget`）・
      `validate()` の3つが、必ず同じ答えを使うこと。** 判定が割れると
      **「入力済み」と出ているのにカート追加で止められる**状態が作れる。
      ⭐ **`validate()` と同じ根拠** ―― `data-r3d-required-when-engrave` が
      付いていて、`data-r3d-when` の囲みが隠れていない欄で、値が空でないこと。
      ⚠️ **隠れている欄は「済み」を返す**（地形モードのルート名）。
      必須ではないので、赤くも光らせもしない。
    */
    engraveFilled(propName) {
      const el = this.root.querySelector('[name="properties[' + propName + ']"]');
      if (!el) return true;
      const scope = el.closest('[data-r3d-when]');
      if (scope && scope.hidden) return true;
      return !!el.value.trim();
    }

    renderRequired() {
      const s = this.state;

      /*
        ⭐⭐ **刻印欄（ステップ4）もここで面倒を見る**（2026-09-10）。
        ⚠️⚠️ **以前は `<span class="r3d-req">必須</span>` に `data-r3d-req` が
        付いておらず、入力し終えても赤いまま**だった。ステップ1〜3の欄は
        済んだ時点でバッジが灰色に変わるのに、**刻印だけ変わらないので
        「まだ足りないのか」と読めてしまう。**
        ⚠️⚠️ **済んだかの判定は `validate()` と同じ根拠にすること** ――
        **`data-r3d-required-when-engrave` が付いていて、`data-r3d-when` の
        囲みが隠れていない欄で、値が空でないこと。** 判定が2か所で割れると、
        **「入力済み」と出ているのにカート追加で止められる**状態が作れる。
        ⚠️ **隠れている欄は「済み」として扱う**（地形モードのルート名）。
        必須ではないので赤くしてはいけない。バッジ自体も画面に出ていない。
      */
      const done = {
        size: !!s.sizeEl,
        baseColor: !!s.baseColor,
        /* ⚠️ **地形モードでは聞かない**（2026-09-09）。**`routeColor` と同じ形。**
           理由は `r3d-designer.liquid` の作例欄のコメント */
        sample: s.buildType !== 'route' || s.sample !== null,
        routeColor: s.buildType !== 'route' || this.routeColorReady(),
        engraveName: this.engraveFilled('山名'),
        engraveRoute: this.engraveFilled('ルート名'),
      };
      this.root.querySelectorAll('[data-r3d-req]').forEach(function (el) {
        const ok = done[el.dataset.r3dReq];
        if (ok === undefined) return;
        el.classList.toggle('r3d-req--done', ok);
        /* ⭐ 済んだときの語は欄ごとに変えられる。**選ぶ欄は「選択済み」、
           打ち込む欄は「入力済み」**（`data-r3d-req-done`）。既定は選択済み */
        el.textContent = ok ? (el.dataset.r3dReqDone || '選択済み') : '必須';
      });
    }

    /*
      次にどこを触ればよいかを1か所だけ光らせる（2026-08-29に追加）。

      **必須バッジ（`renderRequired`）だけでは順路が分からない**という声から。
      あちらは「この欄は必須か / 済んだか」を欄ごとに示すもので、
      **「次はどれか」は示していない。** 未入力が3つあれば赤いバッジが3つ並ぶ。

      **返すのは1つだけ。** いま開いているステップで、まだ済んでいない最初の欄。
      **このステップに残りが無ければ何も光らせない** ―― そのときは「次へ」を
      押す場面で、欄ではなくボタンが次の一手になる。

      ⚠️ **順番は画面の並びと同じにすること。** 光った先が画面の上下で
      飛ぶと、案内として働かない。
    */
    nextTarget() {
      const s = this.state;
      const root = this.root;
      const fieldset = (name) => {
        const b = root.querySelector('[data-r3d-req="' + name + '"]');
        return b ? b.closest('.r3d-fieldset') : null;
      };
      /*
        ⭐ **ステップ4は `.r3d-fieldset` ではなく `.r3d-field`**（2026-09-10）。
        **刻印欄は選択肢の集まりではなく「見出し＋入力欄」1組**なので、箱の
        クラスが違う。**`fieldset()` で引くと `null` が返って、何も光らない。**
      */
      const field = (name) => {
        const b = root.querySelector('[data-r3d-req="' + name + '"]');
        return b ? b.closest('.r3d-field') : null;
      };
      const route = s.buildType === 'route';
      /* [ステップ, 対象, 済んだか] を画面の並び順に */
      const steps = [
        /* ⭐ ステップ1だけパネルごと囲む（2026-08-29）。
           ここは**画面に出ている唯一の欄**で、見出しの下に選択肢が2つあるだけ。
           選択肢だけを囲むと「見出しとは別の何か」に見え、
           **囲みが何を指しているのか分かりにくかった**（実画面で確認）。
           ⚠️ **ステップ2・3は欄ごとのまま。** あちらは1画面に複数の欄が並ぶので、
           パネルごと囲むと「次はどれか」という肝心の情報が消える。 */
        [1, root.querySelector('[data-r3d-panel="1"]'), !!s.buildType],
        [2, route ? root.querySelector('[data-r3d-drop]') : root.querySelector('.r3d-point'),
            route ? !!s.gpx : !!s.latlon],
        [3, fieldset('size'), !!s.sizeEl],
        [3, fieldset('sample'), s.sample !== null],
        [3, fieldset('baseColor'), !!s.baseColor],
        [3, fieldset('routeColor'), !route || this.routeColorReady()],
        /* ⭐ ステップ4（2026-09-10）。**画面の並びどおり 山名 → ルート名。**
           ⚠️ **日付・フリーテキスト・標高は任意なので入れない。**
           ここに任意の欄を足すと、埋めなくても先へ進めるものを光らせることになり、
           **「光っている＝まだ必要」という読み方が壊れる。** */
        [4, field('engraveName'), this.engraveFilled('山名')],
        [4, field('engraveRoute'), this.engraveFilled('ルート名')],
      ];
      for (const [step, el, done] of steps) {
        if (step !== s.step) continue;
        if (!el || done) continue;
        /* 種類で出し入れしている領域は、隠れているあいだ対象にしない */
        const scope = el.closest('[data-r3d-when]');
        if (scope && scope.hidden) continue;
        return el;
      }
      return null;
    }

    renderNext() {
      const el = this.nextTarget();
      this.root.querySelectorAll('.r3d-next').forEach(function (n) {
        if (n !== el) n.classList.remove('r3d-next');
      });
      if (el) el.classList.add('r3d-next');
    }

    /*
      サイズに合わないオプションを「なし」に戻す（2026-09-07）。
      **額装は S〜L**（XL はつば込みの外形が造形範囲 235.5×256 に入らない・9-26）、
      **磁石は M〜XL**（S は銘板の座繰りで縁が 6.70mm しか残らない）。

      ⚠️⚠️ **黙って戻す。** 「XLでは額装を選べません」と止めるのは筋が悪い ――
      **サイズは模型の主で、オプションは従。** 主を選び直したら従が落ちるのが自然で、
      **落ちたことは注文内容の行が「なし」になることで見える。**
      ⭐ 対応範囲はラベル（「額装（+2,000円）：S〜L対応」）に書いてあるので、
      **戻された理由はその場で読める。**

      ⭐ **対応サイズは Liquid の `data-r3d-opt-sizes` が持つ。**
      属性が無い欄（ベース形状・フラッグ）は全サイズ対応。
      ⚠️ **いまは全オプションが「なし」固定**なので、実際に効くのは欄を薄くする所だけ。
      **選べるようにしたとき（案B）に、戻す側もそのまま働く。**
    */
    enforceOptionSizes() {
      const root = this.root;
      const label = this.state.sizeEl ? this.state.sizeEl.dataset.label || '' : '';
      root.querySelectorAll('[data-r3d-opt][data-r3d-opt-sizes]').forEach(function (el) {
        const allow = (el.dataset.r3dOptSizes || '').split(',');
        /* サイズ未選択のうちは触らない。**選ぶ前から塞ぐと「壊れている」に見える** */
        const ok = !label || allow.indexOf(label) !== -1;
        if (!ok) {
          /* ⭐ 既定値へ戻す。`<select>` は「なし」、固定の `<p>` は元の文字 */
          if (el.tagName === 'SELECT') el.value = 'なし';
          else delete el.dataset.value;
        }
        /* ⚠️⚠️⚠️ **準備中の欄は、サイズが合っていても選ばせない**（2026-09-08）。
           **`<p>` から `<select>` に変えた瞬間、ここが有効化してしまい、
           「準備中・選択不可」のはずの額装が本番で売れる状態になった。**
           ⭐ **造形を入れて売り出すときに、Liquid から `data-r3d-opt-soon` を外す。** */
        const soon = el.dataset.r3dOptSoon !== undefined;
        if (el.tagName === 'SELECT') el.disabled = !ok || soon;
        const field = el.closest('.r3d-field');
        if (field) field.classList.toggle('r3d-field--optoff', !ok);
        /* ⚠️ **黙って戻すだけだと「壊れた」に見える。** 理由をその場に出す */
        const note = root.querySelector('[data-r3d-opt-note="' + el.dataset.r3dOpt + '"]');
        if (note) {
          note.hidden = ok;
          if (!ok) {
            note.textContent =
              (el.dataset.optLabel || 'このオプション') + 'は ' + allow.join('・') +
              ' サイズのみです。' + label + ' では選べません。';
          }
        }
      });
    }

    /** 注文内容に出す長辺サイズ。「M（9,900円）」 */
    sizeSummaryText() {
      const el = this.state.sizeEl;
      if (!el) return '—';
      const label = el.dataset.label || el.dataset.optionValue || '';
      const v = this.baseVariantOf(el.dataset.optionValue);
      const m = (v && v.money) || el.dataset.fallbackPrice || '';
      const num = String(m).match(/[\d,]+/);
      return num ? label + '（' + num[0] + '円）' : label;
    }

    renderSummary() {
      const root = this.root;
      const s = this.state;
      this.enforceOptionSizes();
      const set = (k, v) => {
        const el = root.querySelector('[data-r3d-sum="' + k + '"]');
        if (el) el.textContent = v;
      };
      /* 必須なのに未選択の行を赤くする（2026-08-22）。
         **ステップ3に入ってからだけ。** 最初から赤いと、何もしていない状態が
         「エラー」に見えて、次から赤で止まらなくなる。
         `—` ではなく「未選択」と書く。ダッシュは「対象外」と見分けがつかない。 */
      const reached3 = s.step >= 3;
      const mark = (k, v, ok) => {
        const el = root.querySelector('[data-r3d-sum="' + k + '"]');
        if (!el) return;
        const missing = reached3 && !ok;
        el.textContent = missing ? '未選択' : v;
        el.classList.toggle('r3d-sum--missing', missing);
      };

      const buildLabels = { route: 'ルート', terrain: '地形' };
      set('buildType', buildLabels[s.buildType] || '—');
      /* ⭐ 作例利用。**割引はバリアントの価格差から出す**（直書きしない） */
      const cut = this.sampleDiscount();
      const sampleEl = root.querySelector('[data-r3d-sum="sample"]');
      if (s.buildType === 'terrain' && sampleEl) {
        /* ⚠️ **地形モードでは聞かない**（2026-09-09）。**ルート色と同じ「対象外」。**
           ⚠️ **`mark` を通らない枝なので、赤は自分で外すこと** */
        sampleEl.classList.remove('r3d-sum--missing');
        sampleEl.textContent = '対象外';
      } else {
        mark('sample', s.sample === null ? '—'
          : (s.sample ? '使ってよい（−' + this.yen(cut) + '円）' : '使わない'),
          s.sample !== null);
      }
      /*
        長辺サイズは「M（9,900円）」の形（2026-09-07）。
        ⭐ **以前は「M（長辺150mm）」だった**が、寸法はすぐ下の「仕上がり寸法」が
        実寸で出しているので**重複**で、**ここで知りたいのは合計の内訳のほう。**
        ⚠️ **オプションの加算は足さない。** ここは本体価格で、加算は各行が持つ。
        ⚠️ 「円」を直書きしているのは JPY 専用だから。通貨を増やすならここも直す。
      */
      mark('size', this.sizeSummaryText(), !!s.sizeEl);
      set('dims', this.dimsText(this.modelDims));
      /* ⭐ **加算のある色は、注文内容でも金額を添える**（2026-09-07）。
         **合計が本体価格と違う理由が、この行だけで読めるようにする。** */
      const baseFee = this.surchargeOf(this.baseSwatch());
      mark(
        'baseColor',
        s.baseColor ? s.baseColor + (baseFee > 0 ? '（' + this.feeText(baseFee) + '）' : '') : '—',
        !!s.baseColor
      );
      /*
        ルート色は、分けているときは「まとまり名 → 色」を1行ずつ出す。
        「トラック別（4まとまり）」だけでは何色を選んだのか分からず、
        カートに入れる前に確認できない。色の点も添えて見分けやすくする。
      */
      const routeEl = root.querySelector('[data-r3d-sum="routeColor"]');
      if (routeEl) {
        /* **`mark` を通らない枝では赤を自分で外すこと。**
           種別や色分けモードを切り替えたときに、前の状態の赤が残る */
        if (s.buildType !== 'route') {
          routeEl.classList.remove('r3d-sum--missing');
          routeEl.textContent = '対象外';
        } else if (s.routeMode === 'single') {
          mark('routeColor', s.routeColor || '—', !!s.routeColor);
        } else {
          routeEl.classList.remove('r3d-sum--missing');
          const labels = this.outputLabels();
          const rows = [];
          for (let i = 0; i < this.trackCount(); i++) {
            rows.push(
              '<span class="r3d-sumcolor">' +
                '<span class="r3d-sumcolor__name">' +
                this.esc(labels[i]) +
                '</span>' +
                '<span class="r3d-sumcolor__dot" style="background:' +
                this.trackColorAt(i) +
                '"></span>' +
                '<span class="r3d-sumcolor__color">' +
                this.esc(s.trackColors[i] || '—') +
                '</span>' +
                '</span>'
            );
          }
          routeEl.innerHTML = rows.join('');
        }
      }
      /* 等高線（2026-08-29に追加）。**JSは状態を持っておらず、select をそのまま送る**
         作りなので、ここでも DOM から読む。「なし」もそのまま出す ――
         空欄にすると「選び忘れ」と「なしを選んだ」の区別がつかない。 */
      const contourEl = root.querySelector('select[name="properties[等高線]"]');
      set('contour', (contourEl && contourEl.value) || '—');

      /* ベース形状と有償オプション3つ。**表示も加算も欄の DOM をそのまま写す。**
         ⚠️ **`mark` ではなく `set`。** どれも必須ではないので未選択の赤にしない。 */
      root.querySelectorAll('[data-r3d-opt]').forEach((el) => {
        const fee = this.surchargeOf(el);
        const name = this.optValue(el);
        set(el.dataset.r3dOpt, name ? name + (fee > 0 ? '（' + this.feeText(fee) + '）' : '') : '—');
      });

      set('gpx', s.gpx ? s.gpx.fileName : s.buildType === 'route' ? '未選択' : '対象外');

      /*
        ⭐⭐ **合計＝バリアントの価格そのもの。**

        ⚠️⚠️ **加算をここで足さないこと。** **素材も額装もフラッグも磁石も、
        全部バリアントの価格に入っている**（軸3本に畳み込んだ。2026-09-07）。
        **足すと二重になる。**
      */
      const totalEl = root.querySelector('[data-r3d-total]');
      const v = this.currentVariant();
      if (v) {
        totalEl.textContent = v.money;
        root.querySelector('[data-r3d-variant-id]').value = String(v.id);
      } else if (s.sizeEl) {
        /* ⚠️ バリアントが引けないのは、選んだ組み合わせが商品に無いとき。
           **素の値段を出しておく**が、`validate()` がカート追加を止める */
        totalEl.textContent = s.sizeEl.dataset.fallbackPrice || '—';
      } else {
        totalEl.textContent = '—';
      }
    }

    /* ---------------------------------------------------------------- *
     * 検証と送信
     * ---------------------------------------------------------------- */

    /** 問題があれば理由の文字列を、無ければ null を返す */
    validate() {
      const s = this.state;
      if (this.closed) return this.closedNotice;
      if (!this.hasProduct) return 'カスタムオーダー商品が未設定です';
      if (!s.buildType) return 'つくるものを選んでください';
      if (s.buildType === 'route' && !s.gpx) return 'GPSログを選んでください';
      if (s.buildType !== 'route' && !s.latlon) {
        return s.latlonOutside ? '日本国内の地点を選んでください' : '山を選んでください';
      }
      if (s.noDem) return window.R3DGpx.NO_DEM_MESSAGE;
      if (!s.sizeEl) return '長辺サイズを選んでください';
      /* **二重の歯止め。** ボタンは無効にしてあるが、範囲を変えた直後などに
         選択が残ることがある。作れない高さの注文を通さない */
      if (s.sizeEl.classList.contains('is-too-tall')) {
        return '今回の範囲では、このサイズは高さの上限を超えます';
      }
      if (s.sample === null) return '作例に使ってよいかをお選びください';
      if (!s.baseColor) return 'ベース色を選んでください';
      if (s.buildType === 'route' && this.overColorLimit()) {
        return 'ルートは' + (this.maxColors - 1) + '色までです';
      }
      if (s.buildType === 'route' && !this.routeColorReady()) {
        return s.routeMode === 'split'
          ? 'トラックごとの色を選んでください'
          : 'ルート色を選んでください';
      }
      if (!this.currentVariant()) return '選択に対応するバリアントが商品に見つかりません';

      // 刻印は標準なので、無条件で必須
      const missing = Array.from(
        this.root.querySelectorAll('[data-r3d-required-when-engrave]')
      ).filter(function (el) {
        /* ⚠️ **出ていない欄は必須にしない**（2026-08-29）。
           ルート名は `data-r3d-when="route"` の中にあり、地形モードでは隠れる。
           ここで種類を見ずに全部を必須にしていたため、**地形を選んだ人は
           見えない欄のせいで「山名とルート名を入力してください」から進めなかった。**
           `pruneForSubmit` も同じ `data-r3d-when` の hidden を見ているので、
           判定の根拠を1つに揃えてある。 */
        const scope = el.closest('[data-r3d-when]');
        if (scope && scope.hidden) return false;
        return !el.value.trim();
      });
      if (missing.length) {
        /* ⭐ **足りない欄だけを名指しする**（2026-09-10）。
           **以前は「山名のみ」だけを特別扱いしていたので、ルート名だけが空でも
           「山名とルート名を入力してください」と出て、埋まっている欄まで疑わせた。**
           ⚠️ **欄名は `properties[〜]` から取る。** ここで文字列を書き足すと、
           欄を増やしたときに直し忘れる。 */
        const names = missing.map(function (el) {
          return String(el.name).replace(/^properties\[|\]$/g, '');
        });
        return names.join('と') + 'を入力してください';
      }

      /* ⚠️⚠️ **長すぎる刻印を注文に通さない**（9-54）。
         **GPX の名前は上限を超えていても欄に入れる**ので、ここで止める。
         ⚠️ **`maxlength` は手入力にしか効かない** ―― `value` への代入は素通りする。
         ⭐ **幅で止める欄（フリーテキスト）は見ない** ―― 入力時に `trimToWidth` で
         削っているので超えられない。 */
      const over = Array.from(this.root.querySelectorAll('[data-r3d-counted]'))
        .filter(function (el) {
          const scope = el.closest('[data-r3d-when]');
          if (scope && scope.hidden) return false;
          if (widthLimit(el)) return false;
          return el.maxLength > 0 && el.value.length > el.maxLength;
        });
      if (over.length) {
        const field = over[0].closest('.r3d-field');
        const label = field && field.querySelector('.r3d-label');
        const req = label && label.querySelector('.r3d-req');
        const what = label
          ? label.textContent.replace(req ? req.textContent : '', '').trim()
          : '刻印';
        return what + 'が長すぎます。' + over[0].value.length + '字あるので、'
          + over[0].maxLength + '字までに短くしてください';
      }

      return null;
    }

    /**
     * 空の property を送信対象から外す。
     * 空のまま送ると、選ばなかった項目がカートと注文に空行として並ぶ。
     */
    pruneForSubmit() {
      const s = this.state;
      this.form.querySelectorAll('[name^="properties["]').forEach(function (el) {
        el.disabled = false;

        // 現在のビルドタイプで非表示になっている領域の入力は送らない
        const scope = el.closest('[data-r3d-when]');
        if (scope && scope.hidden) {
          el.disabled = true;
          return;
        }

        if (el.type === 'file') {
          if (!el.files || !el.files.length) el.disabled = true;
          return;
        }
        if (el.type === 'checkbox') return; // 未チェックはそもそも送られない
        if (!String(el.value).trim()) el.disabled = true;
      });

      /*
        ⚠️⚠️ **標高は排他なので、選ばれなかったほうの「値」も送らない。**
        **ラジオ自体は未選択なら送られないが、`累積標高` と `最高標高` の
        hidden は両方とも値を持っている**ので、ここで落とさないと注文に2つ載る。
        ⭐ **`入れない` のときは両方落ちる。**
      */
      const kindEl = this.form.querySelector('[name="properties[刻印：標高]"]');
      const kind = kindEl ? String(kindEl.value || '').trim() : '';
      const gainEl = this.form.querySelector('[data-r3d-prop="gain"]');
      const peakEl = this.form.querySelector('[data-r3d-prop="peak"]');
      if (gainEl && kind !== '累積標高') gainEl.disabled = true;
      if (peakEl && kind !== '最高標高') peakEl.disabled = true;

      /*
        ⚠️⚠️⚠️ **距離も同じ扱いにすること**（2026-09-09に踏んだ）。
        **セレクトで「入れない」を選んでも `properties[距離]` は値を持ったままなので、
        そのままだとカートと注文確認に「距離 52.4 km」と出るのに銘板には彫られない。**
        ⚠️ **製造側は正しく落としていた**（`刻印：距離=入れない` を見る）ので、
        **嘘をつくのは画面だけ。** **それでも客が見る面なので直す。**
        ⭐ **これは以前チェックボックスで踏んだのと同じ問題**
        （`r3d-designer.liquid` の刻印欄のコメントに記録がある）。
      */
      const distSel = this.form.querySelector('[name="properties[刻印：距離]"]');
      const distEl = this.form.querySelector('[data-r3d-prop="distance"]');
      if (distEl && (!distSel || String(distSel.value || '').trim() !== '含める')) {
        distEl.disabled = true;
      }
    }

    onSubmit(e) {
      const problem = this.validate();
      const errEl = this.root.querySelector('[data-r3d-error]');

      if (problem) {
        e.preventDefault();
        errEl.hidden = false;
        errEl.textContent = problem;
        return;
      }

      errEl.hidden = true;
      this.pruneForSubmit();

      const submit = this.root.querySelector('[data-r3d-submit]');
      submit.disabled = true;
      submit.textContent = 'カートに追加しています…';
      // ここから先は通常のフォームPOST。fetch に置き換えるとGPXが送られない。
    }
  }

  function init() {
    document.querySelectorAll('[data-r3d-designer]').forEach(function (root) {
      if (root.dataset.r3dReady === 'true') return;
      root.dataset.r3dReady = 'true';
      new Designer(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // テーマエディタでセクションを差し替えたときに再初期化する
  document.addEventListener('shopify:section:load', init);
})();
